/**
 * TrackDetail Page (v2.3 - Track-Authoritative)
 *
 * TRACKS ARE THE COMPARABILITY BOUNDARY.
 *
 * Conditions-met model (not a wizard): each configuration area can be entered
 * in any order. Readiness is evaluated by conditions, not sequence.
 *
 * Track tabs:
 * - Overview: Conditions dashboard (status per area, direct CTAs)
 * - Participation: Who participates
 * - Item pool: Items in this track
 * - Criteria & scales: Attributes and value types
 * - Values: Value matrix
 * - Sessions (tab): Run prioritization sessions in-track (inheritance + New Session)
 * - Results: Session results and published list
 * - Defaults: Session template (workflow preset + session defaults)
 *
 * Note: Loops/Catchball is at CYCLE level, not here.
 * Track = Cycle × Scope (ItemCategory × Altitude)
 */

/* eslint-disable react-hooks/exhaustive-deps --
   Pre-existing debt: 42 exhaustive-deps warnings across this ~4,800-line file
   (store-array `|| []` fallbacks feeding hook dependencies, plus deliberate
   mount-only fetch effects). Restructuring those hooks is a behavior-risk
   refactor that is out of scope for the catchball ball-possession work that
   touched this file; the debt predates it and is suppressed here so the
   changed-files lint gate stays meaningful for new rules. */

import { useState, useEffect, useMemo, useCallback, useRef, type CSSProperties } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  BIND_TRACK_TO_CYCLE_LABEL,
  BIND_TRACK_DIALOG_TITLE,
  BIND_TRACK_CONFIRM_LABEL,
  BIND_TRACK_REVIEW_TITLE,
} from '@/lib/tracks/vocabulary';
import {
  ChevronLeft,
  // ChevronDown,
  ChevronRight,
  Package,
  ListChecks,
  BarChart3,
  Play,
  Flag,
  Target,
  CheckCircle2,
  Circle,
  AlertCircle,
  Plus,
  Lock,
  Users,
  LayoutDashboard,
  Settings,
  X,
  Loader2,
  Trash2,
  Search,
  ClipboardList,
  Link2,
  Sliders,
  Unlink,
  RefreshCcw,
  MessageSquare,
  Trophy,
} from 'lucide-react';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useCycleStore } from '@/stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '@/stores/prioritization/catchballLoopStore';
import { useCatchballLoopContext } from '@/components/prioritization/catchball/ExchangeContextBanner';
import { BallHolderBanner } from '@/components/prioritization/catchball/BallHolderBanner';
import { useExecutionLineageStore } from '@/stores/prioritization/egapIterationStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '@/stores/prioritization/itemRelationshipsStore';
import {
  useCycleSessions,
  useTrackSessions,
  useSessionStore,
  setTrackParticipationLockedViaRpc,
  setTrackCriteriaAndRangesLockedViaRpc,
  setTrackRangesLockedViaRpc,
  setTrackValuesConvergedViaRpc,
} from '@/stores/prioritization/sessionStore';
import { useCycleParticipantsStore } from '@/stores/prioritization/cycleParticipantsStore';
import { useCycleParticipantGroupsStore } from '@/stores/prioritization/cycleParticipantGroupsStore';
import { useTrackParticipantsStore } from '@/stores/prioritization/trackParticipantsStore';
import { useTrackParticipantGroupsStore } from '@/stores/prioritization/trackParticipantGroupsStore';
import { useGroupsStore } from '@/stores/prioritization/groupsStore';
import { useEffectiveTrackParticipants } from '@/hooks/useEffectiveTrackParticipants';
import { RunItLiveCard } from '@/components/prioritization/session/RunItLiveCard';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { ObjectiveCriteriaChooser } from '@/components/prioritization/cycle-config/ObjectiveCriteriaChooser';
import { useCycleObjectives, type CycleObjectiveOption } from '@/hooks/prioritization/useCycleObjectives';
import { isObjectiveCriterionKey, withoutObjectiveAttributes } from '@/lib/objectiveCriteria';
import { useAttributeGovernanceStore, type ValueRange, criteriaRowsForTrack } from '@/stores/prioritization/attributeGovernanceStore';
import { formatRangeDisplay } from '@/lib/valueRanges';
import { useValueProposalStore } from '@/stores/prioritization/valueProposalStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useIsAdmin } from '@/hooks/prioritization/useScopedPermissions';
import { canLockPool, canUnlockPool } from '@/lib/poolAuthority';
import {
  fetchTrackCriteriaVersions,
  openVersion,
  reopenConsequence,
  reopenNeedsReason,
  sessionsRunLine,
  type TrackCriteriaVersion,
} from '@/lib/trackCriteriaVersions';
import { useTrackAuthority } from '@/hooks/prioritization/useTrackAuthority';
import { useWorkspaceMembers } from '@/hooks/useWorkspaceMembers';
import { useToast } from '@/components/ui/Toast';
import { ValueProvenanceLine } from '@/components/prioritization/ValueProvenanceLine';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { StepLockControl } from '@/components/prioritization/StepLockControl';
import { deriveStepStatus } from '@/components/prioritization/deriveStepStatus';
import { TrackOverview } from '@/components/prioritization/TrackOverview';
import { AskButton } from '@/components/ask/AskSheet';
import { FollowButton } from '@/components/follow/FollowButton';
import { AssignmentsSection } from '@/components/prioritization/commitments/AssignmentsSection';
import { ReverseActionButton } from '@/components/reversals/ReverseActionButton';
import { Button } from '@/components/ui/Button';
import { RationaleModal } from '@/components/plan-of-record/porUi';
import { unbindTrackFromCycle } from '@/lib/reversals/reversalsService';
import { getTrackCellLabel, getTrackOwnerUserIdFromMetadata, getTrackCellsFromMetadata, isDefaultTrack, UNSCOPED_CATEGORY, UNSCOPED_ALTITUDE } from '@/types/prioritization/track';
import { TrackOwnerSubControl } from '@/components/prioritization/TrackOwnerSubControl';
import type { Track } from '@/types/prioritization/track';

import type { PrioritizationSession } from '@/types/prioritization';
import type { GridCellFilter } from '@/types/prioritization/session';
import type { DegapCycle } from '@/types/prioritization/degapCycle';
import type { CapabilityId } from '@/types/prioritization/capabilityIds';
import {
  resolveEffectiveConfig,
  DEFAULT_GLOBAL_SESSION_CONFIG,
  workspaceLayerFromWorkspace,
  sessionLayerFromPriorSession,
  cycleLayerFromCycle,
  trackLayerFromTrack,
} from '@/lib/effectiveConfigResolver';
import { normalizeSessionToolId, SESSION_TOOL_DESCRIPTOR_BY_ANY_ID } from '@/lib/sessionToolCatalog';
import { detectTrackCycleConflicts, type BindingConflict } from '@/lib/bindingConflictDetector';
import { BindingConflictReview } from '@/components/prioritization/BindingConflictReview';
import { TrackResultPanel } from '@/components/prioritization/tracks/TrackResultPanel';
import { TrackSessionDefaultsSection, TrackItemScopeSection, TrackReadinessSection } from '@/components/prioritization/cycle-config';
import { useModuleGate } from '@/lib/moduleGate';
import { useTournamentStore } from '@/stores/prioritization/tournamentStore';
import WorkflowPresetSummary from '@/components/prioritization/cycle-config/WorkflowPresetSummary';
import { computeTrackConditionsSummary, type TrackConditionsSummary } from '@/lib/trackConditionsSummary';
import { SetupTab, ParticipantsTab, SessionItemsTab, CriteriaTab } from '../sessions/SessionDetail';
import { TrackCriteriaWeights } from '../../../components/prioritization/tracks/TrackCriteriaWeights';
import { criteriaFromTrackAttributeKeys } from '../../../lib/sessionCriteriaFromTrack';
import { useEffectiveSessionParticipants } from '@/hooks/useEffectiveSessionParticipants';
import { ValueSourceSelector, ValueSourceLockedProvenance } from '@/components/prioritization/ValueSourceSelector';
import { ValuesFromChoice } from '@/components/prioritization/ValuesFromChoice';
import { keeperNote, resolveValuesFrom, valuesFromLabel, type ValuesFrom } from '@/lib/valuesFrom';
import { useAttributeKeepers } from '@/hooks/prioritization/useAttributeKeepers';
import { useValueCollectionStore } from '@/stores/prioritization/valueCollectionStore';
import { SlideOverPanelStandalone } from '@/components/panels/SlideOverPanel';
import { TrackRelationshipsTab } from './TrackRelationshipsTab';
import {
  CHIP_OVERFLOW_CLASS,
  CLASSIFICATION_CHIP_CLASS,
  buildClassificationSummaryTags,
  getVisibleClassificationEntries,
} from '@/lib/classificationDisplay';
import {
  EMPTY_TRACK_RELATIONSHIP_SUMMARY,
  buildItemRelationshipReview,
  getTrackRelationshipBlockerMessage,
  summarizeItemRelationshipState,
  summarizeTrackRelationshipReadiness,
} from '@/lib/trackRelationshipReview';
import { LineageTimeline } from '@/components/prioritization/activity/LineageTimeline';
import { EntityCommentsTab } from '@/components/prioritization/activity/EntityCommentsTab';
import { useTrackLineageEvents } from '@/components/prioritization/activity/useTrackLineageEvents';
import { useReadinessDefinitionStore } from '@/stores/prioritization/readinessDefinitionStore';
import { evaluateReadyForRanking } from '@/lib/itemReadiness';
import { useAgentSignoffs } from '@/hooks/useAgentSignoffs';
import { recordReadinessFailed } from '@/lib/agentRuntimeService';
import { useDefinitionPolicyStore } from '@/stores/prioritization/definitionPolicyStore';
import type { Item } from '@/types/prioritization/lifecycle';
import { eventDesignerHref } from '@/lib/catchballSessionLink';
import { resolveTrackPoolRankingStep } from '@/lib/sessions/trackPoolRankingStep';
import { startTrackRankingSession } from '@/lib/sessions/startTrackRankingSession';
import { describeCreateSessionFailure } from '@/lib/sessions/createSessionFlow';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { CompetingChoiceNotice } from '@/components/prioritization/RelationshipSplitsPanel';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { useItemLinkScope } from '@/lib/itemLinkScope';
import { itemExitStatus } from '@/lib/intakeQueueVisibility';
import { Select } from '@/components/ds';
import { ItemQuickPanel } from '@/components/prioritization/item-view/ItemQuickPanel';

type TrackTab =
  | 'overview'
  | 'participation'
  | 'pool'
  | 'criteria'
  | 'value-ranges'
  | 'values'
  | 'relationships'
  | 'sessions'
  | 'results'
  | 'results-panel'
  | 'defaults'
  | 'provenance'
  | 'discussion';

type TrackPoolRecord = {
  id: string;
  name?: string;
  trackId?: string;
  priorityIds?: string[];
  poolLockedAt?: Date | null;
  pool_locked_at?: string | null;
  poolLockedBy?: string | null;
};

type TrackPoolItemRecord = {
  id: string;
  title?: string;
  description?: string;
  itemCategory?: string;
  altitudeLevel?: number;
  status?: string;
};

type ExecutionLineageLite = {
  id: string;
  trackId: string;
  iterationIndex: number;
};

// ─── Provenance tab sub-component ─────────────────────────────────────────────

export function TrackProvenanceTab({ trackId }: { trackId: string }) {
  const events = useTrackLineageEvents(trackId)
  return <LineageTimeline events={events} title="Track Lineage" />
}

// ─── Discussion tab sub-component ─────────────────────────────────────────────

export function TrackDiscussionTab({
  trackId,
  workspaceId,
}: {
  trackId: string
  workspaceId: string
}) {
  return (
    <EntityCommentsTab
      entityType="track"
      entityId={trackId}
      workspaceId={workspaceId}
    />
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function TrackDetail() {
  const { trackId } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  // Exchange (catchball) context — present when the URL carries ?loopId=
  const catchballLoopCtx = useCatchballLoopContext();

  // Stores - all arrays get defensive fallbacks to empty array
  const tracks = useTrackStore(state => state.tracks) || [];
  const fetchAllTracks = useTrackStore(state => state.fetchAllTracks);
  const isLoading = useTrackStore(state => state.isLoading);
  const bindTrackToCycle = useTrackStore(state => state.bindTrackToCycle);
  const fetchCycles = useCycleStore(state => state.fetchFromSupabase);

  const cycles = useCycleStore(state => state.cycles) || [];
  
  const fetchLoops = useCatchballLoopStore(state => state.fetchFromSupabase);
  
  const fetchExecutionLineage = useExecutionLineageStore(state => state.fetchFromSupabase);
  
  const allPools = useItemPoolsStore(state => state.itemPools) || [];
  const fetchPools = useItemPoolsStore(state => state.fetchFromSupabase);
  const removePriorityFromListTopLevel = useItemPoolsStore(state => state.removePriorityFromList);
  const allItems = useItemsStore(state => state.items) || [];
  const currentUserId = useUserStore(state => state.userId) ?? 'system';
  const currentWorkspaceId = useTenantStore(state => state.currentWorkspaceId);
  const relationshipGroups = useItemRelationshipsStore(state => state.groups) || [];
  const relationshipGroupMemberships = useItemRelationshipsStore(state => state.groupMemberships) || [];
  const relationshipEdges = useItemRelationshipsStore(state => state.edges) || [];
  const fetchRelationships = useItemRelationshipsStore(state => state.fetchFromSupabase);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TRACK_TABS: TrackTab[] = [
    'overview',
    'participation',
    'pool',
    'criteria',
    'value-ranges',
    'values',
    'relationships',
    'sessions',
    'results',
    'results-panel',
    'defaults',
    'provenance',
    'discussion',
  ];

  // Tab state - default to overview; sync with ?tab= query param for deep links
  const [currentTab, setCurrentTab] = useState<TrackTab>('overview');
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);


  // Assign-task modal state

  // Bind-to-cycle modal state
  const [showBindModal, setShowBindModal] = useState(false);
  const [bindTargetCycleId, setBindTargetCycleId] = useState('');
  const [isBinding, setIsBinding] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  // Next step after a successful bind: say what happened and offer the cycle.
  const [boundCycle, setBoundCycle] = useState<{ id: string; name: string } | null>(null);
  const [bindConflicts, setBindConflicts] = useState<BindingConflict[]>([]);
  const [bindStep, setBindStep] = useState<'select' | 'review'>('select');

  const urlTab = searchParams.get('tab') as TrackTab | null;
  useEffect(() => {
    if (urlTab && VALID_TRACK_TABS.includes(urlTab)) {
      setCurrentTab(urlTab);
    }
  }, [urlTab]);

  // Bind a standalone track to a cycle
  const handleBindToCycle = async () => {
    if (!trackId || !bindTargetCycleId || isBinding) return;
    setIsBinding(true);
    setBindError(null);

    // Snapshot track state before call for diagnostics
    const localTrack = useTrackStore.getState().getTrack(trackId);
    console.log('[TrackDetail] bindToCycle — local track snapshot:', {
      id: localTrack?.id,
      isStandalone: localTrack?.isStandalone,
      cycleId: localTrack?.cycleId,
    });

    const ok = await bindTrackToCycle(trackId, bindTargetCycleId);
    setIsBinding(false);
    if (ok) {
      setBoundCycle({
        id: bindTargetCycleId,
        name: cycles.find(c => c.id === bindTargetCycleId)?.name ?? 'the cycle',
      });
      setShowBindModal(false);
      setBindTargetCycleId('');
      setBindStep('select');
      setBindConflicts([]);
      // Refetch loops/lineage now that the track is cycle-bound
      fetchLoops(bindTargetCycleId);
      fetchExecutionLineage(bindTargetCycleId);
    } else {
      // Surface the actual store error rather than a generic message
      const storeError = useTrackStore.getState().error;
      console.error('[TrackDetail] bindToCycle failed. Store error:', storeError);
      setBindError(storeError ?? 'Failed to bind track to cycle. Please try again.');
    }
  };

  // Increment refresh key when switching to overview tab to force data refresh
  const handleTabChange = (tab: TrackTab) => {
    setCurrentTab(tab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
    if (tab === 'overview') {
      setOverviewRefreshKey(prev => prev + 1);
    }
  };
  
  // Find the track
  const track = useMemo(() => 
    tracks.find(t => t.id === trackId),
    [tracks, trackId]
  );
  useItemLinkScope({ cycleId: track?.cycleId });
  
  // Find the cycle
  const cycle = useMemo(() =>
    track ? cycles.find(c => c.id === track.cycleId) : null,
    [track, cycles]
  );

  // Unbind visibility: the doer (track owner) or the cycle owner — same pair
  // TrackDefaultsTab uses for "can reassign track owner".
  const canUnbindTrack = Boolean(currentUserId) && (
    currentUserId === getTrackOwnerUserIdFromMetadata(track?.metadata) ||
    (cycle != null && cycle.ownerId != null && currentUserId === cycle.ownerId)
  );
  // The cycle's template seeded this track once, on the server, when the track
  // was made (migration 1040). Opening this page never applies it again.

  // Get item pools associated with this track
  const trackPools = useMemo(() =>
    allPools.filter(p => p.trackId === trackId),
    [allPools, trackId]
  );
  
  // Get items in the track's pools
  const trackItemIds = useMemo(() => {
    const ids = new Set<string>();
    trackPools.forEach(pool => {
      pool.priorityIds?.forEach(id => ids.add(id));
    });
    return ids;
  }, [trackPools]);
  
  const trackItems = useMemo(() =>
    allItems.filter(item => trackItemIds.has(item.id)),
    [allItems, trackItemIds]
  );

  // When the grid cell scope is saved, remove pool items that no longer match
  // any of the newly selected cells (unscoped = no filtering, keep all).
  const handleScopeChanged = useCallback((newCells: GridCellFilter[]) => {
    if (!track) return;
    const primaryPool = allPools.find(p => p.trackId === track.id);
    if (!primaryPool) return;
    if (newCells.length === 0) return; // unscoped — nothing to remove

    const outOfScope = trackItems.filter(item => {
      return !newCells.some(
        c => item.itemCategory === c.itemCategory && item.altitudeLevel === c.altitudeLevel,
      );
    });

    for (const item of outOfScope) {
      removePriorityFromListTopLevel(primaryPool.id, item.id, track.cycleId ?? undefined, currentUserId);
    }
  }, [track, allPools, trackItems, removePriorityFromListTopLevel, currentUserId]);

  const trackRelationshipSummary = useMemo(
    () => summarizeTrackRelationshipReadiness(trackItems, relationshipGroups, relationshipGroupMemberships, relationshipEdges),
    [trackItems, relationshipGroups, relationshipGroupMemberships, relationshipEdges]
  );
  
  // Get sessions for this track.
  // Standalone tracks (no cycleId) have no cycle to filter by, so use track-based query directly.
  const cycleSessionsResult = useCycleSessions(track?.cycleId ?? '');
  const trackSessionsResult = useTrackSessions(trackId ?? '');
  const rawSessions = track?.cycleId ? cycleSessionsResult : trackSessionsResult;
  const allSessions = Array.isArray(rawSessions) ? rawSessions : [];
  const trackSessions = useMemo(() =>
    allSessions.filter(s => s.trackId === trackId),
    [allSessions, trackId]
  );

  const trackSessionsNewestFirst = useMemo(
    () =>
      [...trackSessions].sort((a, b) => {
        const aDate = new Date(a.updatedAt || a.createdAt).getTime();
        const bDate = new Date(b.updatedAt || b.createdAt).getTime();
        return bDate - aDate;
      }),
    [trackSessions]
  );

  // Participant count for stat boxes
  const { effectiveCount: trackParticipantCount } = useEffectiveTrackParticipants(track?.cycleId ?? undefined, trackId ?? '');
  
  // Fetch data - use empty deps for functions (they're stable from zustand stores)
  useEffect(() => {
    fetchAllTracks();
    fetchPools();
    fetchRelationships();
    fetchCycles();
  }, []);

  useEffect(() => {
    if (track?.cycleId) {
      fetchLoops(track.cycleId);
      fetchExecutionLineage(track.cycleId);
    }
  }, [track?.cycleId]);
  
  // Compute readiness status (track-native lock fields from migration 166; pool from iteration when present)
  const trackPoolForReadiness = trackPools.find(p => p.trackId === track?.id);
  const poolLockedRaw = trackPoolForReadiness as TrackPoolRecord | undefined;
  const poolLockedValue = !!(poolLockedRaw?.poolLockedAt ?? poolLockedRaw?.pool_locked_at);
  const readiness = {
    participationLocked: !!track?.participationLockedAt,
    poolLocked: poolLockedValue,
    criteriaLocked: !!track?.criteriaLockedAt,
    rangeSetLocked: !!track?.rangeSetLockedAt,
    valuesConverged: !!track?.valuesConvergedAt,
    hasRankingResult: false,
    isPublished: track?.status === 'accepted',
  };

  /** Canonical conditions summary (persisted state + effective config); used for header and facilitator blocker */
  const conditionsSummary = useMemo(() => {
    if (!track) return null;
    const relationshipBlockerMessage = getTrackRelationshipBlockerMessage(trackRelationshipSummary);
    return computeTrackConditionsSummary({
      track: {
        participationLockedAt: track.participationLockedAt,
        criteriaLockedAt: track.criteriaLockedAt,
        valuesConvergedAt: track.valuesConvergedAt,
        metadata: track.metadata,
        // First-class columns must be passed — they take precedence over metadata fallback
        defaultTimingMode: track.defaultTimingMode,
        defaultFacilitatorId: track.defaultFacilitatorId,
      },
      cycle,
      lastSession: trackSessionsNewestFirst[0]
        ? {
            modeConfig: trackSessionsNewestFirst[0].modeConfig,
            facilitatorId: trackSessionsNewestFirst[0].facilitatorId,
          }
        : null,
      poolLocked: readiness.poolLocked,
      relationshipBlockerMessage,
    });
  }, [track, cycle, trackSessionsNewestFirst, readiness.poolLocked, trackRelationshipSummary]);

  if (isLoading && !track) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--slate)]"></div>
      </div>
    );
  }
  
  if (!track) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
        <h2 className="text-lg font-medium text-[var(--text-pri)] mb-2">Track Not Found</h2>
        <p className="text-[var(--text-sec)] mb-4">
          The track you're looking for doesn't exist or has been deleted.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }
  
  /** When false, new sessions cannot override track session defaults (inherited / locked). */
  const sessionsInheritedLocked = track.metadata?.allowSessionOverride === false;

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
    { id: 'defaults' as const, label: 'Defaults', icon: Settings },
    { id: 'participation' as const, label: 'Participation', icon: Users },
    { id: 'pool' as const, label: 'Item pool', icon: Package, count: trackItems.length },
    { id: 'criteria' as const, label: 'Criteria', icon: ListChecks },
    { id: 'value-ranges' as const, label: 'Value ranges', icon: Sliders },
    { id: 'values' as const, label: 'Values', icon: BarChart3 },
    {
      id: 'relationships' as const,
      label: 'Relationships',
      icon: Link2,
      count: trackRelationshipSummary.itemsNeedingReview > 0 ? trackRelationshipSummary.itemsNeedingReview : undefined,
    },
    {
      id: 'sessions' as const,
      label: 'Sessions',
      icon: Play,
      count: trackSessions.length > 0 ? trackSessions.length : undefined,
    },
    { id: 'provenance' as const, label: 'Lineage', icon: Target },
    { id: 'discussion' as const, label: 'Discussion', icon: MessageSquare },
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Link
              to={track.isStandalone || !track.cycleId ? '/prioritization/tracks' : `/prioritization/cycles/${track.cycleId}`}
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Target className="h-6 w-6 text-[var(--text-ter)]" />
              <h1 className="text-2xl font-bold text-[var(--text-pri)]">{track.name}</h1>
            </div>
            <StructureVersionFinePrint table="tracks" id={track.id} mode="started" className="ml-8" />
          </div>
          
          <div className="flex items-center gap-4 text-sm text-[var(--text-sec)] ml-8 flex-wrap">
            <span>
              {isDefaultTrack(track) ? 'Primary Track' : getTrackCellLabel(track)}
            </span>
            <span>•</span>
            <span>{track.scopeLabel}</span>
            {cycle ? (
              <>
                <span>•</span>
                <Link
                  to={`/prioritization/cycles/${cycle.id}`}
                  className="text-[var(--text-ter)] hover:underline flex items-center gap-1"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />{cycle.name}
                </Link>
                {canUnbindTrack && (
                  <ReverseActionButton
                    label="Unbind from cycle"
                    confirmTitle="Unbind from cycle"
                    explain={`This will unbind "${track.name}" from "${cycle.name}", returning it to preparatory mode. Governance capabilities (proposals, catchball) will be unavailable until it is bound to a cycle again.`}
                    run={(reason) => unbindTrackFromCycle(track.id, reason)}
                    onDone={() => fetchAllTracks()}
                    variant="ghost"
                    className="!px-2 !py-0.5 text-xs"
                  />
                )}
              </>
            ) : (
              <>
                <span>•</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-2 py-0.5 rounded-full">
                  <Unlink className="h-3 w-3" />Unbound — preparatory mode
                </span>
                <button
                  type="button"
                  onClick={() => setShowBindModal(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1 rounded-full hover:bg-primary-100 transition-colors"
                >
                  <RefreshCcw className="h-3 w-3" />{BIND_TRACK_TO_CYCLE_LABEL}
                </button>
              </>
            )}
          </div>
          
          {track.description && (
            <p className="text-sm text-[var(--text-ter)] mt-2 ml-8">
              {track.description}
            </p>
          )}
          {boundCycle && (
            <div role="status" className="mt-3 ml-8 flex items-center gap-3 p-3 border border-[var(--rule)] bg-[var(--surface)] text-sm text-[var(--text-sec)]">
              <span className="flex-1">Bound to {boundCycle.name}. This track&apos;s sessions are now cycle-linked.</span>
              <Link to={`/prioritization/cycles/${boundCycle.id}`} className="font-medium text-[var(--mustard)] hover:underline">
                Open cycle
              </Link>
              <Button variant="ghost" size="sm" aria-label="Dismiss" onClick={() => setBoundCycle(null)}>
                ×
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex flex-col items-end gap-3">
          <AskButton
            home={{
              type: 'track',
              id: track.id,
              title: track.name,
              cycleId: track.cycleId ?? null,
              trackId: track.id,
              itemIds: trackItems.map((i) => i.id),
            }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors shrink-0"
          />
          <FollowButton
            type="track"
            id={track.id}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors shrink-0"
          />
          <TrackReadinessCard
            track={track}
            readiness={readiness}
            trackPoolForReadiness={trackPoolForReadiness}
            trackRelationshipSummary={trackRelationshipSummary}
            conditionsSummary={conditionsSummary}
            onReviewItems={() => handleTabChange('relationships')}
            onGoToParticipation={() => handleTabChange('participation')}
          />
        </div>
      </div>

      {/* Ball-holder accountability banner — names who holds the ball when this
          track is being worked inside a catchball exchange (non-blocking). */}
      {catchballLoopCtx?.loopId && <BallHolderBanner loopId={catchballLoopCtx.loopId} />}

      {/* Tab Navigation */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="flex items-center gap-1 overflow-x-auto px-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            const showSessionsLock = tab.id === 'sessions' && sessionsInheritedLocked;
            return (
              <button
                key={tab.id}
                data-track-tab={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                title={
                  showSessionsLock
                    ? 'Session timing and entry are inherited from this track — sessions cannot override them.'
                    : undefined
                }
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-[var(--slate)] text-[var(--text-ter)] bg-[var(--slate-dim)]/50'
                    : 'border-transparent text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--surface)]'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {showSessionsLock && (
                  <Lock
                    className="h-3.5 w-3.5 shrink-0 text-[var(--mustard)]"
                    aria-label="Inherited from Track: sessions cannot override these defaults"
                  />
                )}
                {tab.count !== undefined && (
                  <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                    isActive ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="bg-[var(--bg-raised)] border border-t-0 border-[var(--rule)] p-6">
        {currentTab === 'overview' && (
          <div className="space-y-6">
            {/* What people were asked to do on this track (1054). Ask is in the header. */}
            <AssignmentsSection
              home={{ type: 'track', id: track.id }}
              linkedItems={trackItems.map((i) => ({ id: i.id, title: i.title }))}
            />
            {/* Unbound callout — shown when track has no cycle */}
            {track.isStandalone && (
              <div className="flex items-start gap-4 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                <div className="flex-shrink-0 mt-0.5">
                  <Unlink className="h-5 w-5 text-[var(--mustard)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--mustard)]">Unbound — preparatory mode</p>
                  <p className="text-xs text-[var(--mustard)] mt-0.5 leading-relaxed">
                    This Track is not yet linked to a cycle. Configure participants, item pool, criteria, and sessions freely. Bind it to a cycle when you're ready to prioritize — governance capabilities (proposals, catchball) unlock at that point.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBindModal(true)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--mustard)] hover:bg-[var(--mustard)] transition-colors"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />{BIND_TRACK_TO_CYCLE_LABEL}
                </button>
              </div>
            )}

            {/* Key stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {([
                {
                  icon: Play,
                  label: 'Sessions',
                  value: trackSessions.length,
                  color: 'text-[var(--text-ter)]',
                  onClick: () => handleTabChange('sessions'),
                },
                {
                  icon: Users,
                  label: 'Participants',
                  value: trackParticipantCount,
                  color: 'text-[var(--text-ter)]',
                  onClick: () => handleTabChange('participation'),
                },
                {
                  icon: Package,
                  label: 'Items',
                  value: trackItems.length,
                  color: 'text-[var(--green)]',
                  onClick: () => handleTabChange('pool'),
                },
                {
                  icon: Flag,
                  label: 'Results',
                  value: trackSessions.filter(s => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED').length,
                  color: 'text-[var(--mustard)]',
                  onClick: () => handleTabChange('results-panel'),
                },
              ] as const).map(({ icon: Icon, label, value, color, onClick }) => (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className="bg-[var(--surface)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 flex items-center gap-3 text-left w-full transition-colors hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] cursor-pointer group"
                >
                  <div className="w-8 h-8 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] flex items-center justify-center flex-shrink-0 group-hover:border-[var(--rule)] dark:group-hover:border-[var(--rule-bright)] transition-colors">
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${color}`}>{value}</div>
                    <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{label}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Readiness dashboard — front and center */}
            <TrackOverview
              trackId={track.id}
              onNavigate={(tab) => handleTabChange(tab as TrackTab)}
              refreshKey={overviewRefreshKey}
              trackValuesConverged={readiness.valuesConverged}
              sessionCount={trackSessions.length}
              completedSessionCount={trackSessions.filter(s => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED').length}
            />

          </div>
        )}
        {currentTab === 'defaults' && (
          <TrackDefaultsTab
            track={track}
            cycle={cycle}
            currentUserId={currentUserId}
            workspaceId={currentWorkspaceId}
            onScopeChanged={handleScopeChanged}
          />
        )}
        {currentTab === 'participation' && (
          <TrackParticipationTab track={track} cycle={cycle ?? null} />
        )}
        {currentTab === 'pool' && (
          <TrackPoolTab track={track} items={trackItems} pools={trackPools} sessions={trackSessionsNewestFirst} />
        )}
        {currentTab === 'criteria' && (
          <TrackCriteriaTab track={track} />
        )}
        {currentTab === 'value-ranges' && (
          <TrackValueRangesTab track={track} />
        )}
        {currentTab === 'values' && (
          <TrackValuesTab
            track={track}
            readinessFlags={{
              participationLocked: readiness.participationLocked,
              poolLocked: readiness.poolLocked,
              criteriaLocked: readiness.criteriaLocked,
              rangeSetLocked: readiness.rangeSetLocked,
            }}
          />
        )}
        {currentTab === 'relationships' && (
          <TrackRelationshipsTab
            track={track}
            poolItems={trackItems}
            trackPool={trackPools.find((p) => p.trackId === track.id)}
          />
        )}
        {currentTab === 'sessions' && (
          <TrackSessionsTab
            track={track}
            cycle={cycle}
            sessions={trackSessionsNewestFirst}
            conditionsSummary={conditionsSummary}
          />
        )}
        {(currentTab === 'results' || currentTab === 'results-panel') && (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => handleTabChange('overview')}
              className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to overview
            </button>
            <TrackResultsTab track={track} />
          </div>
        )}

        {/* ── Provenance Tab ── */}
        {currentTab === 'provenance' && trackId && (
          <TrackProvenanceTab trackId={trackId} />
        )}

        {/* ── Discussion Tab ── */}
        {currentTab === 'discussion' && trackId && (
          <TrackDiscussionTab
            trackId={trackId}
            workspaceId={getCurrentWorkspaceId() ?? ''}
          />
        )}
      </div>

      {/* Bind to Cycle Modal */}
      {showBindModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!isBinding) {
                setShowBindModal(false); setBindError(null);
                setBindTargetCycleId(''); setBindStep('select'); setBindConflicts([]);
              }
            }}
          />
          <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 bg-[var(--mustard-dim)] flex items-center justify-center">
                <RefreshCcw className="h-5 w-5 text-[var(--mustard)]" />
              </div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">
                {bindStep === 'review' ? BIND_TRACK_REVIEW_TITLE : BIND_TRACK_DIALOG_TITLE}
              </h2>
            </div>

            {bindStep === 'select' ? (
              <>
                <p className="text-sm text-[var(--text-ter)] mb-5 pl-12">
                  Choose a cycle to bind this track to. Sessions under this track will become cycle-linked and governance capabilities will unlock.
                </p>
                {bindError && (
                  <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">{bindError}</div>
                )}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Cycle *</label>
                  <Select
                    value={bindTargetCycleId}
                    onChange={e => setBindTargetCycleId(e.target.value)}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select a cycle</option>
                    {cycles.filter(c => c.status === 'active' || c.status === 'draft').map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowBindModal(false); setBindError(null); setBindTargetCycleId(''); setBindStep('select'); setBindConflicts([]); }}
                    className="flex-1 px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!bindTargetCycleId) return;
                      const selectedCycle = cycles.find(c => c.id === bindTargetCycleId);
                      if (!selectedCycle) return;
                      const detected = detectTrackCycleConflicts(track, selectedCycle);
                      if (detected.length > 0) {
                        setBindConflicts(detected);
                        setBindStep('review');
                      } else {
                        handleBindToCycle();
                      }
                    }}
                    disabled={!bindTargetCycleId}
                    className="flex-1 px-4 py-2 text-white bg-[var(--mustard)] hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-3">
                <BindingConflictReview
                  bindingLabel={`Binding "${track.name}" to "${cycles.find(c => c.id === bindTargetCycleId)?.name ?? 'cycle'}"`}
                  conflicts={bindConflicts}
                  onAccept={handleBindToCycle}
                  onCancel={() => { setBindStep('select'); setBindConflicts([]); setBindError(null); }}
                  isAccepting={isBinding}
                  error={bindError}
                  acceptLabel={BIND_TRACK_CONFIRM_LABEL}
                />
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

interface TrackReadinessCardProps {
  track: Track;
  readiness: {
    participationLocked: boolean;
    poolLocked: boolean;
    criteriaLocked: boolean;
    rangeSetLocked: boolean;
    valuesConverged: boolean;
    hasRankingResult: boolean;
    isPublished: boolean;
  };
  trackPoolForReadiness: { id: string } | undefined;
  trackRelationshipSummary: { itemsNeedingReview: number };
  conditionsSummary: { liveFacilitatorAvailableWhenNeeded: boolean; liveFacilitatorBlockerMessage?: string | null } | null;
  onReviewItems: () => void;
  onGoToParticipation: () => void;
}

function TrackReadinessCard({
  track,
  readiness,
  trackPoolForReadiness,
  trackRelationshipSummary,
  conditionsSummary,
  onReviewItems,
  onGoToParticipation,
}: TrackReadinessCardProps) {
  const steps = [
    { key: 'participation', label: 'Participation', ready: readiness.participationLocked },
    { key: 'pool', label: 'Item pool', ready: readiness.poolLocked && !!trackPoolForReadiness },
    { key: 'criteria', label: 'Criteria', ready: readiness.criteriaLocked },
    { key: 'value-ranges', label: 'Value ranges', ready: readiness.rangeSetLocked },
    { key: 'values', label: 'Values', ready: readiness.valuesConverged },
    { key: 'relationships', label: 'Relationships', ready: trackRelationshipSummary.itemsNeedingReview === 0 },
    ...(conditionsSummary ? [{ key: 'facilitator', label: 'Facilitator', ready: conditionsSummary.liveFacilitatorAvailableWhenNeeded }] : []),
  ];

  const readyCount = steps.filter(s => s.ready).length;
  const totalCount = steps.length;

  const trackStatusLabel = track.status === 'accepted' ? 'Published' : track.status === 'final_rejected' ? 'Rejected' : 'Active';
  const trackStatusClass = track.status === 'accepted'
    ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
    : track.status === 'final_rejected'
      ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
      : 'bg-[var(--slate-dim)] text-[var(--text-sec)]';

  return (
    <div className="w-full border border-[var(--rule)] bg-[var(--surface)] p-3 lg:max-w-md">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Readiness</span>
          <span className="text-xs text-[var(--text-ter)]">{readyCount}/{totalCount}</span>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${trackStatusClass}`}>
          {trackStatusLabel}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full rounded-full bg-[var(--hover-tint)] mb-3">
        <div
          className="h-1.5 rounded-full bg-[var(--slate)] transition-all"
          style={{ width: `${(readyCount / totalCount) * 100}%` }}
        />
      </div>

      {/* 4-column grid of status items */}
      <div className="grid grid-cols-4 gap-x-3 gap-y-1">
        {steps.map(step => (
          <div key={step.key} className="flex items-center gap-1.5">
            {step.ready ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
            ) : (
              <Circle className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)]" />
            )}
            <span className={`text-xs truncate ${step.ready ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]'}`}>
              {step.label}
            </span>
            {/* Inline action links for items needing attention */}
            {step.key === 'relationships' && trackRelationshipSummary.itemsNeedingReview > 0 && (
              <button
                type="button"
                onClick={onReviewItems}
                className="ml-auto shrink-0 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] underline"
              >
                {trackRelationshipSummary.itemsNeedingReview}
              </button>
            )}
            {step.key === 'facilitator' && conditionsSummary && !conditionsSummary.liveFacilitatorAvailableWhenNeeded && (
              <button
                type="button"
                onClick={onGoToParticipation}
                className="ml-auto shrink-0 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] underline"
                title={conditionsSummary.liveFacilitatorBlockerMessage ?? undefined}
              >
                Fix
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Tab Content Components

// ─── Shared TrackWorkspace-style inline style helpers ────────────────────────
// TrackWorkspace's own inline CSS-variable design language (var(--mono) labels,
// var(--coral)/var(--mustard)/var(--green) semantic accents, var(--rule)/
// var(--bg-raised) surfaces), extracted once so every tab body applies the same
// look instead of each redefining its own copies (owner direction: the Tracks
// area should look like TrackWorkspace throughout).
const wsLabel: CSSProperties = { fontFamily: 'var(--mono)', fontSize: '0.55rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase' };
const wsCard: CSSProperties = { border: '1px solid var(--rule)', background: 'var(--bg-raised)', borderRadius: '2px', padding: '14px' };
const wsGreenBtn = (disabled: boolean): CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
  fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: 'var(--green)', border: 'none', borderRadius: '2px',
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});
const wsTh: CSSProperties = { padding: '10px 14px', textAlign: 'left', ...wsLabel };
const wsTd: CSSProperties = { padding: '10px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-pri)', borderTop: '1px solid var(--rule)' };

export function TrackParticipationTab({
  track,
  cycle,
}: {
  track: Track;
  cycle: { id: string; ownerId?: string; name?: string } | null;
}) {
  const { lockGate } = useTrackAuthority(track);
  const participants = useCycleParticipantsStore(state => state.participants) || [];
  const fetchParticipants = useCycleParticipantsStore(state => state.fetchParticipantsForCycle);
  const participantGroups = useCycleParticipantGroupsStore(state => state.participantGroups) ?? [];
  const fetchParticipantGroups = useCycleParticipantGroupsStore(state => state.fetchParticipantGroupsForCycle);
  const trackParticipants = useTrackParticipantsStore(state => state.participants) || [];
  const fetchTrackParticipants = useTrackParticipantsStore(state => state.fetchParticipantsForTrack);
  const addTrackParticipant = useTrackParticipantsStore(state => state.addParticipant);
  const removeTrackParticipant = useTrackParticipantsStore(state => state.removeParticipant);
  const trackParticipantGroups = useTrackParticipantGroupsStore(state => state.participantGroups) ?? [];
  const fetchTrackParticipantGroups = useTrackParticipantGroupsStore(state => state.fetchParticipantGroupsForTrack);
  const addTrackParticipantGroup = useTrackParticipantGroupsStore(state => state.addParticipantGroup);
  const removeTrackParticipantGroup = useTrackParticipantGroupsStore(state => state.removeParticipantGroup);
  const groups = useGroupsStore(state => state.groups) ?? [];
  const fetchGroups = useGroupsStore(state => state.fetchFromSupabase);
  const userId = useUserStore(state => state.userId) ?? 'system';
  const fetchExecutionLineage = useExecutionLineageStore(state => state.fetchFromSupabase);
  const fetchTracksForCycle = useTrackStore(state => state.fetchTracksForCycle);

  const participationLocked = !!track.participationLockedAt;
  const [lockBusy, setLockBusy] = useState(false);

  const { showToast } = useToast();
  const handleSetParticipationLocked = useCallback(async (locked: boolean) => {
    setLockBusy(true);
    try {
      const result = await setTrackParticipationLockedViaRpc(track.id, locked);
      if (!result.success) {
        showToast(
          'error',
          locked ? 'Participation not locked' : 'Participation not unlocked',
          `${result.error ?? 'The server refused the change'}. Nothing was changed.`,
        );
        return;
      }
      showToast('success', locked ? 'Participation locked' : 'Participation unlocked');
      if (track.cycleId) {
        await fetchTracksForCycle(track.cycleId);
        await fetchExecutionLineage(track.cycleId);
      }
    } finally {
      setLockBusy(false);
    }
  }, [track.id, track.cycleId, fetchTracksForCycle, fetchExecutionLineage, showToast]);

  const handleRemoveParticipant = useCallback(async (id: string) => {
    if (await removeTrackParticipant(id, userId)) return;
    const reason = useTrackParticipantsStore.getState().error ?? 'The server refused the change';
    showToast('error', 'Not removed from the track', `${reason.replace(/\.$/, '')}. Nothing was changed.`);
  }, [removeTrackParticipant, userId, showToast]);

  const handleRemoveParticipantGroup = useCallback(async (id: string) => {
    if (await removeTrackParticipantGroup(id, userId)) return;
    const reason = useTrackParticipantGroupsStore.getState().error ?? 'The server refused the change';
    showToast('error', 'Group not removed from the track', `${reason.replace(/\.$/, '')}. Nothing was changed.`);
  }, [removeTrackParticipantGroup, userId, showToast]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [facilitatorUserIds, setFacilitatorUserIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const currentWorkspaceId = useTenantStore(state => state.currentWorkspaceId);
  /** Store may hydrate after first paint; session already has workspace — keeps participant list scope stable. */
  const effectiveWorkspaceId = currentWorkspaceId ?? getCurrentWorkspaceId();
  const { users: scopedSystemUsers, loading: loadingUsers, refetch: refetchWorkspaceUsers } = useSystemUsers(
    effectiveWorkspaceId ? { workspaceId: effectiveWorkspaceId } : undefined,
  );
  const refetchUsersRef = useRef(refetchWorkspaceUsers);
  refetchUsersRef.current = refetchWorkspaceUsers;

  const trackOwnerId = getTrackOwnerUserIdFromMetadata(track.metadata);
  const canReassignTrackOwner = Boolean(userId) && (
    userId === trackOwnerId ||
    (cycle != null && cycle.ownerId != null && userId === cycle.ownerId)
  );
  const workspaceUsers = useMemo(
    () => scopedSystemUsers.map(u => ({ id: u.id, email: u.email, name: u.name })),
    [scopedSystemUsers]
  );
  const workspaceMemberIds = useMemo(
    () => new Set(workspaceUsers.map(u => u.id)),
    [workspaceUsers]
  );
  const [userDisplayMap, setUserDisplayMap] = useState<Record<string, { name?: string; email: string }>>({});

  // Cycle-level (inherited)
  const cycleParticipants = useMemo(() =>
    participants.filter(p => p.cycleId === track.cycleId && p.status === 'active'),
    [participants, track.cycleId]
  );
  const cycleParticipantGroups = useMemo(() =>
    participantGroups.filter(g => g.cycleId === track.cycleId && g.status === 'active'),
    [participantGroups, track.cycleId]
  );
  const stakeholders = useMemo(() =>
    cycleParticipants.filter(p => (p.stageRoles || []).includes('stakeholder')),
    [cycleParticipants]
  );
  // Track-level (this track only)
  const trackParticipantsActive = useMemo(() =>
    trackParticipants.filter(p => p.trackId === track.id && p.status === 'active'),
    [trackParticipants, track.id]
  );
  const trackParticipantGroupsActive = useMemo(() =>
    trackParticipantGroups.filter(g => g.trackId === track.id && g.status === 'active'),
    [trackParticipantGroups, track.id]
  );

  const { effectiveCount: effectiveTrackParticipantCount, loading: effectiveLoading } = useEffectiveTrackParticipants(track.cycleId ?? undefined, track.id);

  const participantDisplayName = useCallback((userId: string) => {
    const u = userDisplayMap[userId] || workspaceUsers.find(w => w.id === userId);
    if (u?.name) return u.name;
    if (u?.email) return u.email;
    return 'Participant';
  }, [userDisplayMap, workspaceUsers]);

  const allParticipantUserIds = useMemo(
    () => [...new Set([...cycleParticipants.map(p => p.userId), ...trackParticipantsActive.map(p => p.userId)].filter(Boolean))].sort().join(','),
    [cycleParticipants, trackParticipantsActive]
  );
  useEffect(() => {
    if (!allParticipantUserIds) return;
    const ids = allParticipantUserIds.split(',');
    import('../../../lib/supabaseClient').then(async ({ getSupabaseClient }) => {
      const supabase = getSupabaseClient();
      const { data: users } = await supabase
        .from('users')
        .select('id, email, name')
        .in('id', ids);
      if (users?.length) {
        const map: Record<string, { name?: string; email: string }> = {};
        users.forEach((u: { id: string; email: string; name?: string }) => {
          map[u.id] = { name: u.name, email: u.email || '' };
        });
        setUserDisplayMap(prev => ({ ...prev, ...map }));
      }
    });
  }, [allParticipantUserIds]);

  // For "Add to this track" modal: exclude users/groups already in track
  const availableUsers = useMemo(() => {
    const trackUserIds = new Set(trackParticipantsActive.map(p => p.userId));
    return workspaceUsers.filter(u => !trackUserIds.has(u.id));
  }, [workspaceUsers, trackParticipantsActive]);
  const availableGroups = useMemo(() => {
    const addedGroupIds = new Set(trackParticipantGroupsActive.map(g => g.groupId));
    return groups.filter(g => !addedGroupIds.has(g.id));
  }, [groups, trackParticipantGroupsActive]);

  /** Direct users row — canonical display names (avoids first-paint RPC / scope mismatches in useSystemUsers). */
  const [addModalUserProfiles, setAddModalUserProfiles] = useState<
    Record<string, { name: string; email: string }>
  >({});
  const availableUserIdsKey = useMemo(
    () => [...new Set(availableUsers.map(u => u.id))].sort().join(','),
    [availableUsers],
  );

  useEffect(() => {
    if (!showAddModal) {
      setAddModalUserProfiles({});
      return;
    }
    void refetchUsersRef.current();
  }, [showAddModal]);

  useEffect(() => {
    if (!showAddModal || !availableUserIdsKey) {
      if (!showAddModal) setAddModalUserProfiles({});
      return;
    }
    const ids = availableUserIdsKey.split(',').filter(Boolean);
    if (ids.length === 0) {
      setAddModalUserProfiles({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      const { getSafeSupabaseClient } = await import('@/lib/supabaseSync');
      const client = getSafeSupabaseClient();
      if (!client || cancelled) return;
      const { data, error } = await client.from('users').select('id, name, email').in('id', ids);
      if (cancelled || error || !data) return;
      const next: Record<string, { name: string; email: string }> = {};
      for (const row of data as { id: string; name: string | null; email: string | null }[]) {
        const email = row.email?.trim() ?? '';
        const name = row.name?.trim() || email || 'User';
        next[row.id] = { name, email };
      }
      setAddModalUserProfiles(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [showAddModal, availableUserIdsKey]);

  // Filter users/groups by search for the modal list
  const filteredUsers = useMemo(() => {
    if (!participantSearchQuery.trim()) return availableUsers;
    const q = participantSearchQuery.toLowerCase();
    return availableUsers.filter(
      u => (u.name?.toLowerCase().includes(q)) || (u.email?.toLowerCase().includes(q)) || u.id.toLowerCase().includes(q)
    );
  }, [availableUsers, participantSearchQuery]);
  const filteredGroupsForAdd = useMemo(() => {
    if (!participantSearchQuery.trim()) return availableGroups;
    const q = participantSearchQuery.toLowerCase();
    return availableGroups.filter(
      g => (g.displayName?.toLowerCase().includes(q)) || (g.name?.toLowerCase().includes(q)) || (g.description?.toLowerCase().includes(q))
    );
  }, [availableGroups, participantSearchQuery]);

  const toggleUserSelection = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setFacilitatorUserIds(f => { const nf = new Set(f); nf.delete(id); return nf; });
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const toggleFacilitator = (id: string) => {
    setFacilitatorUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleGroupSelection = (id: string) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  
  useEffect(() => {
    if (track.cycleId) {
      fetchParticipants(track.cycleId);
      fetchParticipantGroups(track.cycleId);
    }
  }, [track.cycleId, fetchParticipants, fetchParticipantGroups]);
  useEffect(() => {
    if (track.id) {
      fetchTrackParticipants(track.id);
      fetchTrackParticipantGroups(track.id);
    }
  }, [track.id, fetchTrackParticipants, fetchTrackParticipantGroups]);

  // Load groups when Add modal is opened so we can offer users and groups
  useEffect(() => {
    if (showAddModal) void fetchGroups();
  }, [showAddModal, fetchGroups]);
  
  const handleAddParticipants = async () => {
    const userCount = selectedUserIds.size;
    const groupCount = selectedGroupIds.size;
    if (userCount === 0 && groupCount === 0) return;
    setIsAdding(true);
    setAddError(null);
    const addedBy = userId;
    try {
      for (const uid of selectedUserIds) {
        const role = facilitatorUserIds.has(uid) ? 'facilitator' : 'contributor';
        await addTrackParticipant({ trackId: track.id, userId: uid, role, addedBy });
      }
      for (const groupId of selectedGroupIds) {
        await addTrackParticipantGroup({ trackId: track.id, groupId, role: 'contributor', addedBy });
      }
      // If the user designated a facilitator and the track has no default set yet, auto-apply it.
      // This connects the "Facilitator" checkbox to the Session Defaults facilitator field.
      if (facilitatorUserIds.size > 0 && !track.defaultFacilitatorId) {
        const [firstFacilitatorId] = facilitatorUserIds;
        // Phase C-2 (migration 598): the default facilitator is Policy content —
        // written to session_policies ('track' scope), then re-applied locally.
        const { useSessionPolicyStore } = await import('@/stores/prioritization/sessionPolicyStore');
        // Someone without the right to configure the track still adds the
        // people; the default facilitator just stays unset (migration 1012).
        const saved = await useSessionPolicyStore.getState().upsertPolicy({
          scopeKind: 'track',
          scopeId: track.id,
          defaultFacilitatorId: firstFacilitatorId,
        });
        if (saved) useTrackStore.getState().applyPolicyToTrackLocal(track.id);
      }
      await fetchTrackParticipants(track.id);
      await fetchTrackParticipantGroups(track.id);
      setShowAddModal(false);
      setSelectedUserIds(new Set());
      setSelectedGroupIds(new Set());
      setFacilitatorUserIds(new Set());
      setParticipantSearchQuery('');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add participant(s)');
    } finally {
      setIsAdding(false);
    }
  };

  const totalSelectedCount = selectedUserIds.size + selectedGroupIds.size;
  const groupDisplayName = (groupId: string) => groups.find(g => g.id === groupId)?.displayName ?? groups.find(g => g.id === groupId)?.name ?? 'Group';

  // Load groups when we have any participant groups (cycle or track) so we show names, not IDs
  useEffect(() => {
    if (cycleParticipantGroups.length > 0 || trackParticipantGroupsActive.length > 0) void fetchGroups();
  }, [cycleParticipantGroups.length, trackParticipantGroupsActive.length, fetchGroups]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ ...wsCard, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div>
          <div style={wsLabel}>Track owner</div>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '4px' }}>
            Accountable for configuring participants and running sessions in this Track. Reassignable by the track owner or the cycle owner.
          </p>
        </div>
        <TrackOwnerSubControl
          trackId={track.id}
          readOnly={!canReassignTrackOwner}
          workspaceId={currentWorkspaceId}
        />
      </div>

      {/* Participation lock control */}
      <StepLockControl
        title="Participation"
        description={`Cycle: ${cycleParticipants.length} people, ${cycleParticipantGroups.length} group${cycleParticipantGroups.length !== 1 ? 's' : ''}. Track: ${trackParticipantsActive.length} people, ${trackParticipantGroupsActive.length} group${trackParticipantGroupsActive.length !== 1 ? 's' : ''}.${!effectiveLoading ? ` Effective: ${effectiveTrackParticipantCount} people.` : ''}`}
        status={deriveStepStatus(participationLocked, trackParticipantsActive.length > 0 || cycleParticipants.length > 0)}
        conditionsMet={trackParticipantsActive.length > 0 || cycleParticipants.length > 0}
        onLock={() => handleSetParticipationLocked(true)}
        onUnlock={() => handleSetParticipationLocked(false)}
        busy={lockBusy}
        readOnlyNote={lockGate.allowed ? undefined : lockGate.reason}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={wsLabel}>Participants</div>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={participationLocked}
          title={participationLocked ? 'Unlock participation to add or remove participants' : undefined}
          style={wsGreenBtn(participationLocked)}
        >
          <Plus size={13} />
          Add to this track
        </button>
      </div>

      {/* Stakeholders: who gets notified to submit items */}
      {stakeholders.length > 0 && (
        <div style={{ borderRadius: '2px', border: '1px solid var(--rule)', borderLeft: '3px solid var(--slate)', background: 'var(--bg-raised)', padding: '14px' }}>
          <div style={{ ...wsLabel, color: 'var(--text-ter)', marginBottom: '6px' }}>Stakeholders (notify to submit items)</div>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginBottom: '8px' }}>
            These people will be notified to go and submit their items for this track.
          </p>
          <ul style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {stakeholders.map(p => (
              <li key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: 'var(--hover-tint)', borderRadius: '2px', fontSize: '0.75rem', fontFamily: 'var(--sans)', color: 'var(--text-pri)' }}>
                {participantDisplayName(p.userId)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <div style={{ ...wsLabel, marginBottom: '6px' }}>From cycle (inherited)</div>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginBottom: '8px' }}>Cycle-level participants apply to all Tracks. Edit in Cycle settings.</p>
          {(cycleParticipants.length === 0 && cycleParticipantGroups.length === 0) ? (
            <p style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-ter)', fontStyle: 'italic' }}>No cycle participants.</p>
          ) : (
            <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {cycleParticipantGroups.map(g => (
                    <tr key={g.id}>
                      <td style={{ ...wsTd, borderTop: 'none', fontWeight: 500 }}>{groupDisplayName(g.groupId)}</td>
                      <td style={{ ...wsTd, borderTop: 'none', color: 'var(--text-sec)' }}>Group</td>
                    </tr>
                  ))}
                  {cycleParticipants.map((p, i) => (
                    <tr key={p.id}>
                      <td style={{ ...wsTd, borderTop: i === 0 && cycleParticipantGroups.length === 0 ? 'none' : undefined, fontWeight: 500 }}>{participantDisplayName(p.userId)}</td>
                      <td style={{ ...wsTd, borderTop: i === 0 && cycleParticipantGroups.length === 0 ? 'none' : undefined, color: 'var(--text-sec)' }}>{p.role === 'facilitator' ? 'Facilitator' : 'Participant'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div>
          <div style={{ ...wsLabel, marginBottom: '6px' }}>This track</div>
          {trackParticipantsActive.length === 0 && trackParticipantGroupsActive.length === 0 ? (
            <div style={{ ...wsCard, textAlign: 'center', padding: '24px' }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', marginBottom: '12px' }}>No track-level participants yet. Add people or groups for this track only.</p>
              <button onClick={() => setShowAddModal(true)} disabled={participationLocked} style={{ ...wsGreenBtn(participationLocked), margin: '0 auto' }}>
                <Plus size={13} />
                Add to this track
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {trackParticipantGroupsActive.length > 0 && (
                <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {trackParticipantGroupsActive.map((g, i) => (
                        <tr key={g.id}>
                          <td style={{ ...wsTd, borderTop: i === 0 ? 'none' : undefined, fontWeight: 500 }}>{groupDisplayName(g.groupId)}</td>
                          <td style={{ ...wsTd, borderTop: i === 0 ? 'none' : undefined, color: 'var(--text-sec)' }}>Group</td>
                          <td style={{ ...wsTd, borderTop: i === 0 ? 'none' : undefined, textAlign: 'right' }}>
                            <button
                              onClick={() => void handleRemoveParticipantGroup(g.id)}
                              disabled={participationLocked}
                              title={participationLocked ? 'Unlock to remove' : 'Remove from track'}
                              style={{ background: 'none', border: 'none', cursor: participationLocked ? 'not-allowed' : 'pointer', color: 'var(--coral)', opacity: participationLocked ? 0.5 : 1 }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--hover-tint)' }}>
                      <th style={wsTh}>User</th>
                      <th style={wsTh}>Role</th>
                      <th style={wsTh}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackParticipantsActive.map(p => {
                      const isNonMember = !loadingUsers && workspaceMemberIds.size > 0 && !workspaceMemberIds.has(p.userId);
                      return (
                        <tr key={p.id} style={{ background: isNonMember ? 'rgba(212,160,23,0.06)' : undefined }}>
                          <td style={wsTd}>
                            <span style={{ fontWeight: 500 }}>{participantDisplayName(p.userId)}</span>
                            {isNonMember && (
                              <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', fontSize: '0.65rem', fontWeight: 500, borderRadius: '2px', background: 'rgba(212,160,23,0.12)', color: 'var(--mustard)' }}>
                                Not a workspace member
                              </span>
                            )}
                          </td>
                          <td style={{ ...wsTd, color: 'var(--text-sec)' }}>{p.role === 'facilitator' ? 'Facilitator' : 'Participant'}</td>
                          <td style={{ ...wsTd, textAlign: 'right' }}>
                            <button
                              onClick={() => void handleRemoveParticipant(p.id)}
                              disabled={participationLocked}
                              title={participationLocked ? 'Unlock to remove' : 'Remove from track'}
                              style={{ background: 'none', border: 'none', cursor: participationLocked ? 'not-allowed' : 'pointer', color: 'var(--coral)', opacity: participationLocked ? 0.5 : 1 }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add people and groups modal: single surface, both kinds visible */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }}
            onClick={() => {
              setShowAddModal(false);
              setSelectedUserIds(new Set());
              setSelectedGroupIds(new Set());
              setFacilitatorUserIds(new Set());
              setParticipantSearchQuery('');
            }}
          />
          <div style={{ position: 'relative', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', maxWidth: '640px', width: '100%', margin: '0 16px', padding: '24px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <div style={{ fontFamily: 'var(--sans)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-pri)' }}>Add to this track</div>
                <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '4px' }}>
                  Select people or groups. Check <strong>Facilitator</strong> to designate the session facilitator — this will also set the default facilitator in Session Defaults.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedUserIds(new Set());
                  setSelectedGroupIds(new Set());
                  setFacilitatorUserIds(new Set());
                  setParticipantSearchQuery('');
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ marginBottom: '12px', position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
              <input
                type="text"
                value={participantSearchQuery}
                onChange={(e) => setParticipantSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid var(--rule)', borderRadius: '2px', background: 'var(--bg)', color: 'var(--text-pri)', fontFamily: 'var(--sans)', fontSize: '0.85rem' }}
                placeholder="Search people or groups..."
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', minHeight: 0 }}>
              <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', display: 'flex', flexDirection: 'column', minHeight: '200px' }}>
                <div style={{ padding: '8px 12px', background: 'var(--hover-tint)', borderBottom: '1px solid var(--rule)', display: 'flex', alignItems: 'center' }}>
                  <span style={{ flex: 1, ...wsLabel }}>People</span>
                  <span style={{ width: '80px', textAlign: 'center', ...wsLabel }}>Facilitator</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                  {loadingUsers ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-sec)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Loader2 className="animate-spin" size={14} /> Loading...</div>
                  ) : filteredUsers.length === 0 ? (
                    <p style={{ padding: '16px', textAlign: 'center', color: 'var(--text-sec)', fontSize: '0.8rem' }}>No matching people</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {filteredUsers.slice(0, 50).map(user => {
                        const isSelected = selectedUserIds.has(user.id);
                        const profile = addModalUserProfiles[user.id];
                        const displayName = profile?.name || user.name?.trim() || user.email;
                        return (
                          <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '2px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: 'pointer', minWidth: 0 }}>
                              <input type="checkbox" checked={isSelected} onChange={() => toggleUserSelection(user.id)} />
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-pri)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={profile?.email || user.email || undefined}>
                                {displayName}
                              </span>
                            </label>
                            <div style={{ width: '80px', display: 'flex', justifyContent: 'center' }}>
                              {isSelected && (
                                <input
                                  type="checkbox"
                                  checked={facilitatorUserIds.has(user.id)}
                                  onChange={() => toggleFacilitator(user.id)}
                                  title="Designate as facilitator"
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', display: 'flex', flexDirection: 'column', minHeight: '200px' }}>
                <div style={{ padding: '8px 12px', background: 'var(--hover-tint)', borderBottom: '1px solid var(--rule)', ...wsLabel }}>Groups</div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                  {filteredGroupsForAdd.length === 0 ? (
                    <p style={{ padding: '16px', textAlign: 'center', color: 'var(--text-sec)', fontSize: '0.8rem' }}>No matching groups</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {filteredGroupsForAdd.slice(0, 50).map(g => (
                        <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '2px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={selectedGroupIds.has(g.id)} onChange={() => toggleGroupSelection(g.id)} />
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-pri)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.displayName || g.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {addError && (
              <div style={{ marginTop: '8px', padding: '10px 12px', background: 'rgba(192,83,58,0.06)', border: '1px solid rgba(192,83,58,0.25)', borderRadius: '2px', fontSize: '0.8rem', color: 'var(--coral)' }}>{addError}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--rule)' }}>
              <span style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
                {totalSelectedCount === 0 ? '0 selected' : (
                  <>
                    {selectedUserIds.size} {selectedUserIds.size === 1 ? 'person' : 'people'}
                    {selectedGroupIds.size > 0 && `, ${selectedGroupIds.size} ${selectedGroupIds.size === 1 ? 'group' : 'groups'}`}
                    {facilitatorUserIds.size > 0 && (
                      <span style={{ color: 'var(--text-ter)' }}> ({facilitatorUserIds.size} facilitator{facilitatorUserIds.size !== 1 ? 's' : ''})</span>
                    )}
                  </>
                )}
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => { setShowAddModal(false); setSelectedUserIds(new Set()); setSelectedGroupIds(new Set()); setFacilitatorUserIds(new Set()); setParticipantSearchQuery(''); }}
                  style={{ padding: '7px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button onClick={handleAddParticipants} disabled={isAdding || totalSelectedCount === 0} style={wsGreenBtn(isAdding || totalSelectedCount === 0)}>
                  {isAdding ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TrackPoolTab({ track, items, pools, sessions }: {
  track: Track;
  items: TrackPoolItemRecord[];
  pools: TrackPoolRecord[];
  /** Track's sessions, newest first — used to detect whether a ranking session already exists (BREAK 1). */
  sessions: PrioritizationSession[];
}) {
  const navigate = useNavigate();
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const allItems = useItemsStore(state => state.items) || [];
  const relationshipGroups = useItemRelationshipsStore(state => state.groups) || [];
  const relationshipGroupMemberships = useItemRelationshipsStore(state => state.groupMemberships) || [];
  const relationshipEdges = useItemRelationshipsStore(state => state.edges) || [];
  const updateGroup = useItemRelationshipsStore(state => state.updateGroup);
  const dissolveGroup = useItemRelationshipsStore(state => state.dissolveGroup);
  const updateEdge = useItemRelationshipsStore(state => state.updateEdge);
  const dissolveEdge = useItemRelationshipsStore(state => state.dissolveEdge);
  const fetchFromSupabase = useItemPoolsStore(state => state.fetchFromSupabase);
  const createItemPoolForTrack = useItemPoolsStore(state => state.createItemPoolForTrack);
  const addPriorityToList = useItemPoolsStore(state => state.addPriorityToList);
  const addPrioritiesToList = useItemPoolsStore(state => state.addPrioritiesToList);
  const getItemPoolResult = useItemPoolsStore(state => state.getItemPoolResult);
  const removePriorityFromList = useItemPoolsStore(state => state.removePriorityFromList);
  const lockItemPool = useItemPoolsStore(state => state.lockItemPoolWithResult);
  const unlockItemPool = useItemPoolsStore(state => state.unlockItemPoolWithResult);
  const isItemPoolLocked = useItemPoolsStore(state => state.isItemPoolLocked);
  const userId = useUserStore(state => state.userId) ?? 'system';
  const executionLineageRecords = (useExecutionLineageStore(state => state.iterations) || []) as ExecutionLineageLite[];
  const lockCandidatePool = useExecutionLineageStore(state => state.lockCandidatePool);
  const unlockCandidatePool = useExecutionLineageStore(state => state.unlockCandidatePool);
  const fetchExecutionLineage = useExecutionLineageStore(state => state.fetchFromSupabase);
  const fetchSessions = useSessionStore(state => state.fetchFromSupabase);

  const [poolLockBusy, setPoolLockBusy] = useState(false);
  const [poolUnlockOpen, setPoolUnlockOpen] = useState(false);

  const trackPool = pools.find((p) => p.trackId === track.id);
  const poolLocked = trackPool ? isItemPoolLocked(trackPool.id) : false;

  // Pre-click gate: set_item_pool_lock's rules, read client-side.
  const isWorkspaceAdmin = useIsAdmin();
  const poolCycle = useDegapProcessStore((s) => (track.cycleId ? s.getCycle(track.cycleId) : undefined));
  const poolGate = poolLocked
    ? canUnlockPool(
        { userId, isWorkspaceAdmin },
        { poolLockedBy: trackPool?.poolLockedBy },
        poolCycle ? { ownerId: poolCycle.ownerId, createdBy: poolCycle.createdBy } : null,
      )
    : canLockPool({ userId, isWorkspaceAdmin });
  const poolResult = trackPool ? getItemPoolResult(trackPool.id) : null;

  // ── Readiness gate for pool lock ────────────────────────────────────────────
  const workspaceId = useTenantStore(s => s.currentWorkspaceId);
  const allCycles = useCycleStore(s => s.cycles);
  const loadReadinessDefs = useReadinessDefinitionStore(s => s.loadForWorkspace);
  const getEffectiveReadiness = useReadinessDefinitionStore(s => s.getEffectiveDefinition);

  useEffect(() => {
    if (workspaceId) loadReadinessDefs(workspaceId);
  }, [workspaceId, loadReadinessDefs]);

  const workspaceReadinessId = useReadinessDefinitionStore(s => s.workspaceDefinitionId);
  const readinessDefinitions = useReadinessDefinitionStore(s => s.definitions);
  const effectiveReadiness = useMemo(() => {
    const cycle = allCycles.find(c => c.id === track.cycleId) ?? null;
    void readinessDefinitions;
    return getEffectiveReadiness({
      workspaceDefinitionId: workspaceReadinessId,
      cycleDefinitionId: cycle?.readinessDefinitionId ?? null,
      trackDefinitionId: track.readinessDefinitionId ?? null,
    });
  }, [allCycles, track.cycleId, track, getEffectiveReadiness, workspaceReadinessId, readinessDefinitions]);

  // Items not ready to rank here: the readiness definition in force (track ??
  // cycle ?? workspace) plus the every-item Hard Stops, which apply even when
  // no definition is set. Re-evaluated when the rules or accepted values load.
  const everyItemRules = useDefinitionPolicyStore((s) => s.overrides);
  const acceptedValuesForReadiness = useValueProposalStore((s) => s.acceptedValues);
  const trackAgentSignoffs = useAgentSignoffs(useMemo(() => items.map((i) => i.id), [items]));
  const readinessFailures = useMemo(() => {
    void everyItemRules;
    void acceptedValuesForReadiness;
    return items
      .map((item) => ({
        item,
        result: evaluateReadyForRanking(item as unknown as Item, effectiveReadiness.definition, undefined, trackAgentSignoffs[item.id] ?? {}),
      }))
      .filter((r) => !r.result.pass);
  }, [items, effectiveReadiness.definition, everyItemRules, acceptedValuesForReadiness, trackAgentSignoffs]);

  // Items held back here are the readiness_failed event agents can watch (spec §2 E); once a day per set.
  const failingKey = readinessFailures.map((r) => r.item.id).sort().join(',');
  useEffect(() => {
    const ws = getCurrentWorkspaceId();
    if (!ws || !failingKey) return;
    const codes = [...new Set(readinessFailures.flatMap((r) => r.result.criteria.filter((c) => !c.pass).map((c) => c.criterion.type)))];
    void recordReadinessFailed(ws, failingKey.split(','), codes);
  }, [failingKey]); // once per failing set, not per render

  // Pool can only be locked when there are items AND all pass readiness
  const poolConditionsMet = items.length > 0 && readinessFailures.length === 0;

  const latestExecutionLineageForPool = useMemo(() => {
    const trackIters = executionLineageRecords
      .filter((iteration) => iteration.trackId === track.id)
      .sort((left, right) => right.iterationIndex - left.iterationIndex);
    return trackIters[0] ?? null;
  }, [executionLineageRecords, track.id]);

  const itemRelationshipSummaries = useMemo(() => {
    const itemIdSet = new Set(items.map((item: { id: string }) => item.id));
    return new Map(
      items.map((item: { id: string }) => [
        item.id,
        summarizeItemRelationshipState(item.id, itemIdSet, relationshipGroups, relationshipGroupMemberships, relationshipEdges),
      ]),
    );
  }, [items, relationshipGroups, relationshipGroupMemberships, relationshipEdges]);

  const { showToast } = useToast();
  // Lock / unlock are one governed server transaction (set_item_pool_lock,
  // migration 749). Unlocking reopens membership without an amendment, so it
  // takes a written reason; the server's refusal is shown as-is.
  const handlePoolLock = useCallback(async () => {
    if (!trackPool) return;
    setPoolLockBusy(true);
    try {
      const result = await lockItemPool(trackPool.id, userId, track.cycleId ?? undefined);
      if (!result.success) {
        showToast('error', 'Item pool not locked', result.error ?? 'The lock could not be saved. The pool is still open.');
        return;
      }
      if (latestExecutionLineageForPool) await lockCandidatePool(latestExecutionLineageForPool.id, trackPool.id);
      showToast('success', 'Item pool locked');
      await fetchFromSupabase();
      if (track.cycleId) await fetchExecutionLineage(track.cycleId);
      // Sessions drive the "Start ranking session" ↔ "Open ranking session"
      // CTA below — refresh them so a lock that just happened is reflected
      // immediately (the CTA appears) without waiting on another tab's fetch.
      await fetchSessions();
    } finally {
      setPoolLockBusy(false);
    }
  }, [trackPool, lockItemPool, userId, track.cycleId, fetchFromSupabase, latestExecutionLineageForPool, lockCandidatePool, fetchExecutionLineage, showToast, fetchSessions]);

  // Throwing keeps the reason modal open with the server's message.
  const handlePoolUnlock = useCallback(async (reason: string) => {
    if (!trackPool) return;
    setPoolLockBusy(true);
    try {
      const result = await unlockItemPool(trackPool.id, userId, track.cycleId ?? undefined, reason);
      if (!result.success) {
        throw new Error(result.error ?? 'The unlock could not be saved. The pool is still locked.');
      }
      setPoolUnlockOpen(false);
      if (latestExecutionLineageForPool) await unlockCandidatePool(latestExecutionLineageForPool.id);
      showToast('success', 'Item pool unlocked', 'Your reason was recorded.');
      await fetchFromSupabase();
      if (track.cycleId) await fetchExecutionLineage(track.cycleId);
      await fetchSessions();
    } finally {
      setPoolLockBusy(false);
    }
  }, [trackPool, unlockItemPool, userId, track.cycleId, fetchFromSupabase, latestExecutionLineageForPool, unlockCandidatePool, fetchExecutionLineage, showToast, fetchSessions]);

  // ── BREAK 1: "Lock pool ✕ Rank in session" — the next step after a lock ────
  // Canon: "Membership must be locked before a force-ranking Session begins"
  // (01-terminology-and-glossary.md, "Item Pool"). Locking used to be a dead
  // end; this surfaces the next step and takes the user there in one click.
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const rankingStep = useMemo(
    () => resolveTrackPoolRankingStep({ poolLocked, trackId: track.id, sessions }),
    [poolLocked, track.id, sessions],
  );
  const canFacilitateSessions = can('session.facilitate');
  const [startingRanking, setStartingRanking] = useState(false);

  const handleStartRankingSession = useCallback(async () => {
    setStartingRanking(true);
    try {
      const result = await startTrackRankingSession({
        trackId: track.id,
        trackName: track.name,
        cycleId: track.cycleId ?? null,
        userId,
        navigate,
        toast: showToast,
      });
      if (!result.ok) {
        showToast('error', 'Ranking session not started', result.error);
      }
    } finally {
      setStartingRanking(false);
    }
  }, [track.id, track.name, track.cycleId, userId, navigate, showToast]);
  
  // The item being fixed from the readiness list, in the quick-edit panel.
  const [fixingItemId, setFixingItemId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSourceMode, setAddSourceMode] = useState<'items' | 'pool'>('items');
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [relationshipReviewItemId, setRelationshipReviewItemId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const trackItemIds = useMemo(() => new Set(items.map((item) => item.id)), [items]);
  const allItemsById = useMemo(
    () => new Map(allItems.map((item) => [item.id, { id: item.id, title: item.title }])),
    [allItems],
  );
  const flaggedItems = useMemo(
    () => items.filter((item) => (itemRelationshipSummaries.get(item.id) ?? EMPTY_TRACK_RELATIONSHIP_SUMMARY).totalFlags > 0),
    [items, itemRelationshipSummaries],
  );
  const selectedRelationshipReview = useMemo(() => {
    if (!relationshipReviewItemId) return null;
    return buildItemRelationshipReview(
      relationshipReviewItemId,
      trackItemIds,
      allItemsById,
      relationshipGroups,
      relationshipGroupMemberships,
      relationshipEdges,
    );
  }, [
    relationshipReviewItemId,
    trackItemIds,
    allItemsById,
    relationshipGroups,
    relationshipGroupMemberships,
    relationshipEdges,
  ]);
  const selectedRelationshipItem = useMemo(
    () => (relationshipReviewItemId ? allItems.find((item) => item.id === relationshipReviewItemId) ?? null : null),
    [relationshipReviewItemId, allItems],
  );

  // Other pools (excluding this track's pool) for "Add from pool"
  const otherPools = useMemo(() => {
    return pools.filter(
      (p: { trackId?: string; priorityIds?: string[] }) =>
        p.trackId !== track.id && (p.priorityIds?.length ?? 0) > 0
    );
  }, [pools, track.id]);

  // Many pools share a name ("Product pool"); name each by the track and cycle
  // it belongs to, so the list can be told apart.
  const tracksForPoolLabels = useTrackStore((s) => s.tracks);
  const cyclesForPoolLabels = useDegapProcessStore((s) => s.cycles);
  const poolLabel = useCallback(
    (p: TrackPoolRecord) => {
      const owner = p.trackId ? tracksForPoolLabels.find((t) => t.id === p.trackId) : undefined;
      const cycle = owner?.cycleId ? cyclesForPoolLabels.find((c) => c.id === owner.cycleId) : undefined;
      const n = p.priorityIds?.length ?? 0;
      const where = [owner?.name, cycle?.name].filter(Boolean).join(' · ');
      return `${p.name ?? 'Untitled pool'}${where ? ` — ${where}` : ''} (${n} ${n === 1 ? 'item' : 'items'})`;
    },
    [tracksForPoolLabels, cyclesForPoolLabels],
  );
  
  // Filter available items (not already in pool, matching category/altitude if track has filters)
  const availableItems = useMemo(() => {
    const poolItemIds = new Set(items.map(i => i.id));
    const trackCells = getTrackCellsFromMetadata(track.metadata);
    return allItems.filter(item => {
      if (poolItemIds.has(item.id)) return false;
      // A retired item (exit_status 'archived') is out of play and is never offered.
      if (itemExitStatus(item) === 'archived') return false;
      // Multi-cell track: item must match at least one of the configured cells
      if (trackCells && trackCells.length > 0) {
        const matches = trackCells.some(
          c => item.itemCategory === c.itemCategory && item.altitudeLevel === c.altitudeLevel,
        );
        if (!matches) return false;
      } else {
        // Single-cell or unscoped track: use the primary columns
        if (track.requiredItemCategory !== UNSCOPED_CATEGORY && item.itemCategory !== track.requiredItemCategory) return false;
        if (track.requiredAltitudeLevel !== UNSCOPED_ALTITUDE && item.altitudeLevel !== track.requiredAltitudeLevel) return false;
      }
      if (searchQuery && !item.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [allItems, items, track, searchQuery]);
  
  const handleAddItems = async () => {
    if (selectedItemIds.size === 0) return;
    setIsAdding(true);
    setAddError(null);
    
    const itemIdsToAdd = Array.from(selectedItemIds);
    
    try {
      let poolId = trackPool?.id;
      if (!poolId) {
        poolId = await createItemPoolForTrack(
          { cycleId: track.cycleId ?? '', trackId: track.id },
          `${track.name} Pool`,
          { ownerId: userId }
        );
      }
      // One write for the whole selection — see addPrioritiesToList. Looping
      // addPriorityToList here raced the same column once per item.
      const result = addPrioritiesToList(
        poolId!,
        itemIdsToAdd,
        track.cycleId ?? undefined,
        userId,
        undefined,
        'manual',
      );
      if (result.error) {
        setAddError(result.error);
        return;
      }
      // Do not refetch here: the store is already updated; a refetch can
      // overwrite with stale DB data before syncToSupabase has completed.
      setShowAddModal(false);
      setSelectedItemIds(new Set());
      setSearchQuery('');
    } catch (err) {
      console.error('[TrackPoolTab] Error adding items:', err);
      setAddError(err instanceof Error ? err.message : 'Failed to add items');
    } finally {
      setIsAdding(false);
    }
  };
  
  const toggleItemSelection = (itemId: string) => {
    const newSet = new Set(selectedItemIds);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    setSelectedItemIds(newSet);
  };

  // Add all items from another pool (filter by track category/altitude)
  const handleAddFromPool = async () => {
    if (!selectedPoolId) return;
    const sourcePool = pools.find((p: { id: string }) => p.id === selectedPoolId);
    if (!sourcePool?.priorityIds?.length) return;
    setIsAdding(true);
    setAddError(null);
    try {
      let poolId = trackPool?.id;
      if (!poolId) {
        poolId = await createItemPoolForTrack(
          { cycleId: track.cycleId ?? '', trackId: track.id },
          `${track.name} Pool`,
          { ownerId: userId }
        );
      }
      const poolItemIds = new Set(items.map((i: { id: string }) => i.id));
      const trackCellsForPool = getTrackCellsFromMetadata(track.metadata);

      // Decide membership first, write once. This used to call
      // addPriorityToList per item, which fires a whole-array write per call —
      // 21 concurrent PATCHes racing for the same column, last one wins. The
      // same 21-item add persisted 4 items on one run and 20 on the next, with
      // every call reporting success. See addPrioritiesToList.
      const eligible = sourcePool.priorityIds.filter((itemId: string) => {
        if (poolItemIds.has(itemId)) return false;
        const item = allItems.find((i: { id: string }) => i.id === itemId);
        // A retired item stays in its old pool's record but is never carried into a new one.
        if (item && itemExitStatus(item) === 'archived') return false;
        if (trackCellsForPool && trackCellsForPool.length > 0) {
          return trackCellsForPool.some(
            c => item?.itemCategory === c.itemCategory && item?.altitudeLevel === c.altitudeLevel,
          );
        }
        if (track.requiredItemCategory !== UNSCOPED_CATEGORY && item?.itemCategory !== track.requiredItemCategory) return false;
        if (track.requiredAltitudeLevel !== UNSCOPED_ALTITUDE && item?.altitudeLevel !== track.requiredAltitudeLevel) return false;
        return true;
      });

      const result = addPrioritiesToList(
        poolId!,
        eligible,
        track.cycleId ?? undefined,
        userId,
        undefined,
        'filter',
      );
      if (result.error) {
        setAddError(result.error);
        return;
      }
      setShowAddModal(false);
      setSelectedPoolId(null);
      setAddSourceMode('items');
    } catch (err) {
      console.error('[TrackPoolTab] Error adding from pool:', err);
      setAddError(err instanceof Error ? err.message : 'Failed to add from pool');
    } finally {
      setIsAdding(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Item pool lock control */}
      <StepLockControl
        title="Item Pool"
        description={`${items.length} item${items.length !== 1 ? 's' : ''} in this track${poolResult ? ` · Pool: ${poolResult.metadata.name} (${poolResult.itemIds.length} items)` : ''}${readinessFailures.length > 0 ? ` · ${readinessFailures.length} item${readinessFailures.length !== 1 ? 's' : ''} not ready` : ''}`}
        status={deriveStepStatus(poolLocked, poolConditionsMet)}
        conditionsMet={poolConditionsMet}
        onLock={handlePoolLock}
        onUnlock={() => setPoolUnlockOpen(true)}
        busy={poolLockBusy}
        disabled={!trackPool || (!poolLocked && !poolGate.allowed)}
        disabledReason={!trackPool ? 'No item pool exists for this track yet' : poolGate.reason}
        unlockDisabledReason={poolLocked && !poolGate.allowed ? poolGate.reason : undefined}
      />
      {poolUnlockOpen && trackPool && (
        <RationaleModal
          title="Unlock item pool?"
          subtitle={trackPool.name}
          confirmLabel="Unlock pool"
          fieldLabel="Reason for unlocking"
          placeholder="Why does this locked pool need to change? Recorded with the unlock."
          onConfirm={handlePoolUnlock}
          onClose={() => setPoolUnlockOpen(false)}
        >
          <p className="text-sm text-[var(--text-sec)] mb-3">
            Unlocking lets items be added or removed without an amendment. Once a ranking has run against
            this pool it can no longer be unlocked — amend the pool instead. Only the person who locked it, the cycle owner or a workspace
            admin can unlock it.
          </p>
        </RationaleModal>
      )}

      {/* BREAK 1: next step after a lock — rank this pool in a session */}
      {rankingStep.kind !== 'pending' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', borderRadius: '2px', border: '1px solid var(--slate)', background: 'var(--slate-dim)', padding: '12px 16px' }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-ter)' }}>
              {rankingStep.kind === 'open' ? 'A ranking session already exists for this pool' : 'Ready to rank'}
            </p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '2px' }}>
              {rankingStep.kind === 'open'
                ? 'Continue the session that ranks this locked pool.'
                : 'The pool is locked — start the session that ranks it.'}
            </p>
          </div>
          {rankingStep.kind === 'open' ? (
            <button
              onClick={() => navigate(`/prioritization/sessions/${rankingStep.sessionId}`)}
              style={wsGreenBtn(false)}
            >
              <ChevronRight size={13} />
              Open ranking session
            </button>
          ) : (
            <button
              onClick={() => void handleStartRankingSession()}
              disabled={startingRanking || !canFacilitateSessions}
              title={!canFacilitateSessions ? describeCreateSessionFailure(undefined, 'authorization_denied') : undefined}
              style={wsGreenBtn(startingRanking || !canFacilitateSessions)}
            >
              {startingRanking ? <Loader2 className="animate-spin" size={13} /> : <Play size={13} />}
              {startingRanking ? 'Starting…' : 'Start ranking session'}
            </button>
          )}
        </div>
      )}

      {/* Readiness failures — block lock and surface exactly what's wrong */}
      {!poolLocked && readinessFailures.length > 0 && (
        <div style={{ borderRadius: '2px', border: '1px solid rgba(212,160,23,0.25)', background: 'rgba(212,160,23,0.06)', padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <AlertCircle size={14} style={{ color: 'var(--mustard)', marginTop: '2px', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--mustard)' }}>
                {readinessFailures.length} item{readinessFailures.length !== 1 ? 's' : ''}{' '}
                {effectiveReadiness.definition
                  ? `${readinessFailures.length === 1 ? "doesn't" : "don't"} meet the "${effectiveReadiness.definition.name}" readiness rules`
                  : readinessFailures.length === 1 ? "is missing something every item must have" : "are missing something every item must have"}
              </p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '2px' }}>
                Every item must be ready before the pool can be locked. Fix one here; rules marked "every item" come from Settings → Readiness → Every item.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                {readinessFailures.map(({ item, result }) => (
                  <div key={item.id} style={{ borderRadius: '2px', border: '1px solid rgba(212,160,23,0.25)', background: 'var(--bg-raised)', padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <p style={{ flex: 1, minWidth: 0, fontFamily: 'var(--sans)', fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-pri)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(item as Record<string, unknown>).title as string ?? item.id}</p>
                      <button
                        type="button"
                        onClick={() => setFixingItemId(item.id)}
                        style={{ flexShrink: 0, border: '1px solid var(--rule-bright)', background: 'var(--bg)', padding: '3px 10px', fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-pri)', cursor: 'pointer' }}
                      >
                        Fix
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {result.criteria
                        .filter((c) => !c.pass)
                        .map((c, i) => (
                          <span
                            key={i}
                            style={{ display: 'inline-flex', alignItems: 'center', borderRadius: '2px', background: 'rgba(212,160,23,0.12)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--mustard)' }}
                          >
                            {c.reason ?? 'Missing'}{c.everyItem ? ' · every item' : ''}
                          </span>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {fixingItemId && (
        // The item's quick-edit panel, as a drawer on the right so the list
        // stays in view: an item drops off the list once it is ready.
        <div
          role="dialog"
          aria-label="Fix an item"
          style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 60, borderLeft: '1px solid var(--rule)', boxShadow: '-8px 0 24px rgba(0,0,0,0.12)', background: 'var(--bg-raised)' }}
        >
          <ItemQuickPanel
            itemId={fixingItemId}
            itemIds={readinessFailures.map(({ item }) => item.id)}
            onClose={() => setFixingItemId(null)}
            onNavigate={setFixingItemId}
            defaultTab="details"
          />
        </div>
      )}

      {/* Provenance line — shown when pool was promoted from a Collection */}
      {trackPool && (trackPool as { description?: string }).description?.startsWith('Created from collection:') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '-12px', padding: '0 4px' }}>
          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--slate)', flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--mono)' }}>{(trackPool as { description?: string }).description}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={wsLabel}>Items</div>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={poolLocked}
          title={poolLocked ? 'Unlock pool to add items' : undefined}
          style={wsGreenBtn(poolLocked)}
        >
          <Plus size={13} />
          Add Items
        </button>
      </div>

      {flaggedItems.length > 0 && (
        <div style={{ borderRadius: '2px', border: '1px solid rgba(212,160,23,0.25)', background: 'rgba(212,160,23,0.06)', padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--mustard)' }}>Relationship review queue</div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', marginTop: '4px' }}>
                {flaggedItems.length} item{flaggedItems.length !== 1 ? 's' : ''} in this track have relationship signals worth reviewing before a final session.
              </p>
            </div>
            <span style={{ borderRadius: '2px', background: 'rgba(212,160,23,0.12)', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--mustard)', flexShrink: 0 }}>
              {flaggedItems.length} flagged
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
            {flaggedItems.slice(0, 6).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setRelationshipReviewItemId(item.id)}
                style={{ borderRadius: '2px', border: '1px solid rgba(212,160,23,0.3)', background: 'var(--bg-raised)', padding: '6px 12px', fontSize: '0.8rem', color: 'var(--text-pri)', cursor: 'pointer' }}
              >
                {item.title || item.id}
              </button>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', padding: '32px', textAlign: 'center' }}>
          <Package size={40} style={{ color: 'var(--text-ter)', margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)', marginBottom: '4px' }}>No items yet</div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', marginBottom: '16px' }}>
            Add items that match this track's category and altitude requirements.
          </div>
          <button onClick={() => setShowAddModal(true)} style={{ ...wsGreenBtn(false), margin: '0 auto' }}>
            <Plus size={13} />
            Add First Item
          </button>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--hover-tint)' }}>
                <th style={wsTh}>Item</th>
                <th style={wsTh}>Category</th>
                <th style={wsTh}>Altitude</th>
                <th style={wsTh}>Relationship flags</th>
                <th style={wsTh}>Status</th>
                {!poolLocked && <th style={{ ...wsTh, width: '48px' }} />}
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 20).map(item => {
                const relationshipSummary = itemRelationshipSummaries.get(item.id) ?? EMPTY_TRACK_RELATIONSHIP_SUMMARY;
                return (
                  <tr key={item.id} className="group">
                    <td style={wsTd}>
                      <Link to={`/prioritization/items?id=${item.id}`} style={{ fontWeight: 500, color: 'var(--text-ter)', textDecoration: 'none' }}>
                        {item.title}
                      </Link>
                      {item.description && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '380px' }}>{item.description}</div>
                      )}
                    </td>
                    <td style={{ ...wsTd, color: 'var(--text-sec)' }}>{item.itemCategory || '-'}</td>
                    <td style={{ ...wsTd, color: 'var(--text-sec)' }}>{item.altitudeLevel || '-'}</td>
                    <td style={wsTd}>
                      {relationshipSummary.totalFlags === 0 ? (
                        <span style={{ color: 'var(--text-ter)' }}>None</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                          {relationshipSummary.candidateCount > 0 && (
                            <span style={{ padding: '3px 8px', borderRadius: '2px', fontSize: '0.7rem', background: 'rgba(212,160,23,0.12)', color: 'var(--mustard)' }}>
                              {relationshipSummary.candidateCount} unreviewed
                            </span>
                          )}
                          {relationshipSummary.hardDependencyOutsideTrackCount > 0 && (
                            <span style={{ padding: '3px 8px', borderRadius: '2px', fontSize: '0.7rem', background: 'rgba(192,83,58,0.12)', color: 'var(--coral)' }}>
                              {relationshipSummary.hardDependencyOutsideTrackCount} hard dep
                            </span>
                          )}
                          {relationshipSummary.activeGroupCount > 0 && (
                            <span style={{ padding: '3px 8px', borderRadius: '2px', fontSize: '0.7rem', background: 'var(--hover-tint)', color: 'var(--text-sec)' }}>
                              {relationshipSummary.activeGroupCount} group
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setRelationshipReviewItemId(item.id)}
                            style={{ marginLeft: '4px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-ter)', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            Review
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={wsTd}>
                      <span style={{
                        padding: '3px 8px', borderRadius: '2px', fontSize: '0.7rem',
                        background: item.status === 'active' ? 'rgba(58,125,86,0.12)' : 'var(--hover-tint)',
                        color: item.status === 'active' ? 'var(--green)' : 'var(--text-sec)',
                      }}>
                        {item.status || 'draft'}
                      </span>
                    </td>
                    {!poolLocked && (
                      <td style={{ ...wsTd, textAlign: 'right' }}>
                        <button
                          onClick={() => {
                            if (trackPool) removePriorityFromList(trackPool.id, item.id, track.cycleId ?? undefined, userId);
                          }}
                          className="opacity-0 group-hover:opacity-100"
                          style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--coral)', transition: 'opacity 0.15s' }}
                          title="Remove from pool"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
              )})}
            </tbody>
          </table>
          {items.length > 20 && (
            <div style={{ padding: '10px 14px', background: 'var(--hover-tint)', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-sec)' }}>
              Showing 20 of {items.length} items
            </div>
          )}
        </div>
      )}

      <SlideOverPanelStandalone
        open={!!relationshipReviewItemId && !!selectedRelationshipReview}
        onClose={() => setRelationshipReviewItemId(null)}
        title={selectedRelationshipItem?.title ? `Relationship Review: ${selectedRelationshipItem.title}` : 'Relationship Review'}
        size="xl"
        footer={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link
                to={relationshipReviewItemId ? `/prioritization/items?id=${relationshipReviewItemId}` : '/prioritization/items'}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-sec)', background: 'none', border: '1px solid var(--rule)', borderRadius: '2px', textDecoration: 'none' }}
              >
                Open Item
              </Link>
              {trackPool && !poolLocked && relationshipReviewItemId && (
                <button
                  type="button"
                  onClick={() => {
                    removePriorityFromList(trackPool.id, relationshipReviewItemId, track.cycleId ?? undefined, userId);
                    setRelationshipReviewItemId(null);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--coral)', background: 'none', border: '1px solid var(--rule)', borderRadius: '2px', cursor: 'pointer' }}
                  title="Remove this item from the pool"
                >
                  <Trash2 size={13} />
                  Remove from pool
                </button>
              )}
            </div>
            <button onClick={() => setRelationshipReviewItemId(null)} style={wsGreenBtn(false)}>
              Done
            </button>
          </div>
        }
      >
        {selectedRelationshipReview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ ...wsCard }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                <span style={{ borderRadius: '2px', background: 'rgba(212,160,23,0.12)', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--mustard)' }}>
                  {selectedRelationshipReview.summary.candidateCount} unreviewed
                </span>
                <span style={{ borderRadius: '2px', background: 'rgba(192,83,58,0.12)', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--coral)' }}>
                  {selectedRelationshipReview.summary.hardDependencyOutsideTrackCount} external hard dep
                </span>
                <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-sec)' }}>
                  {selectedRelationshipReview.summary.activeGroupCount} confirmed group
                </span>
              </div>
              <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <h4 style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-pri)' }}>Needs attention now</h4>
                  {selectedRelationshipReview.blockingReasons.length === 0 ? (
                    <p style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>No blocking relationship issues are currently flagged.</p>
                  ) : (
                    <ul style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
                      {selectedRelationshipReview.blockingReasons.map((reason) => (
                        <li key={reason} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <AlertCircle size={14} style={{ color: 'var(--mustard)', marginTop: '2px', flexShrink: 0 }} />
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <h4 style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-pri)' }}>Informational context</h4>
                  <ul style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
                    {selectedRelationshipReview.informationalReasons.map((reason) => (
                      <li key={reason} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <ClipboardList size={14} style={{ color: 'var(--text-dim)', marginTop: '2px', flexShrink: 0 }} />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>Groups</h4>
              {selectedRelationshipReview.groups.length === 0 ? (
                <p style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>No canonical group relationships are attached to this item.</p>
              ) : (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedRelationshipReview.groups.map((group) => (
                    <div key={group.id} style={{ ...wsCard }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)' }}>{group.title || `${group.type} relationship`}</span>
                        <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--text-sec)' }}>{group.type}</span>
                        {group.subtype && <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--text-sec)' }}>{group.subtype}</span>}
                        <span style={{ borderRadius: '2px', padding: '2px 8px', fontSize: '0.7rem', background: group.status === 'candidate' ? 'rgba(212,160,23,0.12)' : 'rgba(58,125,86,0.12)', color: group.status === 'candidate' ? 'var(--mustard)' : 'var(--green)' }}>
                          {group.status === 'candidate' ? 'unreviewed' : group.status}
                        </span>
                        <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--text-sec)' }}>{group.declaredMode}</span>
                      </div>
                      {group.rationale && <p style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>{group.rationale}</p>}
                      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {group.members.map((member) => (
                          <Link
                            key={`${group.id}-${member.itemId}`}
                            to={`/prioritization/items?id=${member.itemId}`}
                            style={{
                              borderRadius: '2px', border: '1px solid', padding: '4px 10px', fontSize: '0.7rem', textDecoration: 'none',
                              borderColor: member.inTrack ? 'rgba(58,125,86,0.35)' : 'rgba(192,83,58,0.35)',
                              background: member.inTrack ? 'rgba(58,125,86,0.08)' : 'rgba(192,83,58,0.08)',
                              color: member.inTrack ? 'var(--green)' : 'var(--coral)',
                            }}
                          >
                            {member.title}
                            {member.membershipRole ? ` · ${member.membershipRole}` : ''}
                            {member.inTrack ? ' · in track' : ' · not in pool'}
                          </Link>
                        ))}
                      </div>
                      {/* Add missing package members to pool */}
                      {group.members.some(m => !m.inTrack) && trackPool && !poolLocked && (
                        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {group.members.filter(m => !m.inTrack).map(member => (
                            <button
                              key={member.itemId}
                              type="button"
                              onClick={() => addPriorityToList(trackPool.id, member.itemId, track.cycleId ?? undefined, userId)}
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '2px', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 500, background: 'var(--hover-tint)', color: 'var(--text-ter)', border: '1px solid var(--rule)', cursor: 'pointer' }}
                            >
                              <Plus size={11} />
                              Add "{member.title}" to pool
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Confirm or dissolve unreviewed relationships */}
                      {group.status === 'candidate' && (
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--rule)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginRight: '2px' }}>Decide:</span>
                          <button
                            type="button"
                            onClick={() => updateGroup(group.id, { status: 'active' })}
                            className="dark:!text-[var(--text-ter)]" style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 600, borderRadius: '2px', background: 'rgba(58,125,86,0.1)', color: 'var(--green)', border: '1px solid rgba(58,125,86,0.3)', cursor: 'pointer' }}
                          >
                            Confirm relationship
                          </button>
                          <button
                            type="button"
                            onClick={() => dissolveGroup(group.id)}
                            style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 600, borderRadius: '2px', background: 'none', color: 'var(--text-sec)', border: '1px solid var(--rule)', cursor: 'pointer' }}
                          >
                            Dissolve
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>Edges</h4>
              {selectedRelationshipReview.edges.length === 0 ? (
                <p style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>No canonical edge relationships are attached to this item.</p>
              ) : (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedRelationshipReview.edges.map((edge) => (
                    <div key={edge.id} style={{ ...wsCard }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)' }}>{edge.type}</span>
                        {edge.subtype && <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--text-sec)' }}>{edge.subtype}</span>}
                        <span style={{ borderRadius: '2px', padding: '2px 8px', fontSize: '0.7rem', background: edge.status === 'candidate' ? 'rgba(212,160,23,0.12)' : 'rgba(58,125,86,0.12)', color: edge.status === 'candidate' ? 'var(--mustard)' : 'var(--green)' }}>
                          {edge.status === 'candidate' ? 'unreviewed' : edge.status}
                        </span>
                        <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--text-sec)' }}>{edge.directionLabel}</span>
                        <span style={{ borderRadius: '2px', background: 'var(--hover-tint)', padding: '2px 8px', fontSize: '0.7rem', color: 'var(--text-sec)' }}>{edge.declaredMode}</span>
                      </div>
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem' }}>
                        <span style={{ color: 'var(--text-sec)' }}>Related item:</span>
                        <Link to={`/prioritization/items?id=${edge.otherItemId}`} style={{ fontWeight: 500, color: 'var(--text-ter)' }}>
                          {edge.otherItemTitle}
                        </Link>
                        <span style={{ borderRadius: '2px', padding: '2px 8px', fontSize: '0.7rem', background: edge.otherItemInTrack ? 'rgba(58,125,86,0.12)' : 'rgba(192,83,58,0.12)', color: edge.otherItemInTrack ? 'var(--green)' : 'var(--coral)' }}>
                          {edge.otherItemInTrack ? 'in track' : 'not in pool'}
                        </span>
                      </div>
                      {/* If the related item is not in the pool, offer adding it */}
                      {!edge.otherItemInTrack && trackPool && !poolLocked && (
                        <div style={{ marginTop: '8px' }}>
                          <button
                            type="button"
                            onClick={() => addPriorityToList(trackPool.id, edge.otherItemId, track.cycleId ?? undefined, userId)}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '2px', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 500, background: 'var(--hover-tint)', color: 'var(--text-ter)', border: '1px solid var(--rule)', cursor: 'pointer' }}
                          >
                            <Plus size={11} />
                            Add "{edge.otherItemTitle}" to pool
                          </button>
                        </div>
                      )}
                      {edge.label && <p style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>Label: {edge.label}</p>}
                      {edge.rationale && <p style={{ marginTop: '4px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>{edge.rationale}</p>}
                      {/* Confirm or dissolve unreviewed edge */}
                      {edge.status === 'candidate' && (
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--rule)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginRight: '2px' }}>Decide:</span>
                          <button
                            type="button"
                            onClick={() => updateEdge(edge.id, { status: 'active' })}
                            className="dark:!text-[var(--text-ter)]" style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 600, borderRadius: '2px', background: 'rgba(58,125,86,0.1)', color: 'var(--green)', border: '1px solid rgba(58,125,86,0.3)', cursor: 'pointer' }}
                          >
                            Confirm relationship
                          </button>
                          <button
                            type="button"
                            onClick={() => dissolveEdge(edge.id)}
                            style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 600, borderRadius: '2px', background: 'none', color: 'var(--text-sec)', border: '1px solid var(--rule)', cursor: 'pointer' }}
                          >
                            Dissolve
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SlideOverPanelStandalone>
      
      {/* Add Items Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }}
            onClick={() => { setShowAddModal(false); setAddSourceMode('items'); setSelectedPoolId(null); }}
          />
          <div style={{ position: 'relative', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', maxWidth: '640px', width: '100%', margin: '0 16px', padding: '24px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ fontFamily: 'var(--sans)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-pri)' }}>Add Items to Pool</div>
              <button
                onClick={() => { setShowAddModal(false); setAddSourceMode('items'); setSelectedPoolId(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid var(--rule)' }}>
              <button
                type="button"
                onClick={() => setAddSourceMode('items')}
                style={{
                  padding: '8px 12px', ...wsLabel, fontSize: '0.6rem',
                  color: addSourceMode === 'items' ? 'var(--text-pri)' : 'var(--text-sec)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: addSourceMode === 'items' ? '2px solid var(--mustard)' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                Select items
              </button>
              <button
                type="button"
                onClick={() => setAddSourceMode('pool')}
                style={{
                  padding: '8px 12px', ...wsLabel, fontSize: '0.6rem',
                  color: addSourceMode === 'pool' ? 'var(--text-pri)' : 'var(--text-sec)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: addSourceMode === 'pool' ? '2px solid var(--mustard)' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                Add from pool
              </button>
            </div>

            {addSourceMode === 'items' ? (
              <>
                <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid var(--rule)', borderRadius: '2px', background: 'var(--bg)', color: 'var(--text-pri)', fontFamily: 'var(--sans)', fontSize: '0.85rem' }}
                      placeholder="Search items..."
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                    <span style={{ color: 'var(--text-sec)' }}>
                      {availableItems.length} item{availableItems.length !== 1 ? 's' : ''} available
                    </span>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedItemIds(new Set(availableItems.map(i => i.id)))}
                        disabled={availableItems.length === 0}
                        style={{ background: 'none', border: 'none', cursor: availableItems.length === 0 ? 'not-allowed' : 'pointer', color: availableItems.length === 0 ? 'var(--text-dim)' : 'var(--slate)' }}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedItemIds(new Set())}
                        disabled={selectedItemIds.size === 0}
                        style={{ background: 'none', border: 'none', cursor: selectedItemIds.size === 0 ? 'not-allowed' : 'pointer', color: selectedItemIds.size === 0 ? 'var(--text-dim)' : 'var(--text-sec)' }}
                      >
                        Clear selection
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--rule)', borderRadius: '2px' }}>
                  {availableItems.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-sec)', fontSize: '0.85rem' }}>
                      No matching items available
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {availableItems.slice(0, 50).map((item, i) => (
                        <label
                          key={item.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', cursor: 'pointer', borderTop: i === 0 ? 'none' : '1px solid var(--rule)' }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedItemIds.has(item.id)}
                            onChange={() => toggleItemSelection(item.id)}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)' }}>{item.title}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-sec)' }}>
                              {item.itemCategory || 'No category'} • {item.altitudeLevel ? `Level ${item.altitudeLevel}` : 'No altitude'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {addError && (
                  <div style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(192,83,58,0.06)', border: '1px solid rgba(192,83,58,0.25)', borderRadius: '2px', fontSize: '0.8rem', color: 'var(--coral)' }}>
                    {addError}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--rule)' }}>
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
                    {selectedItemIds.size} item{selectedItemIds.size !== 1 ? 's' : ''} selected
                  </span>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => { setShowAddModal(false); setAddSourceMode('items'); }}
                      style={{ padding: '7px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddItems}
                      disabled={isAdding || selectedItemIds.size === 0}
                      style={wsGreenBtn(isAdding || selectedItemIds.size === 0)}
                    >
                      {isAdding ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
                      Add Selected
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Choose a pool</label>
                  <Select
                    value={selectedPoolId ?? ''}
                    onChange={(e) => setSelectedPoolId(e.target.value || null)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--rule)', borderRadius: '2px', background: 'var(--bg)', color: 'var(--text-pri)', fontFamily: 'var(--sans)', fontSize: '0.85rem' }}
                  >
                    <option value="">Select pool...</option>
                    {otherPools.map((p: TrackPoolRecord) => (
                      <option key={p.id} value={p.id}>
                        {poolLabel(p)}
                      </option>
                    ))}
                  </Select>
                  {otherPools.length === 0 && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)', marginTop: '6px' }}>No other pools with items. Create pools in Item Management or add items to another track first.</p>
                  )}
                </div>
                {addError && (
                  <div style={{ marginBottom: '16px', padding: '10px 12px', background: 'rgba(192,83,58,0.06)', border: '1px solid rgba(192,83,58,0.25)', borderRadius: '2px', fontSize: '0.8rem', color: 'var(--coral)' }}>
                    {addError}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--rule)' }}>
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
                    {selectedPoolId ? (() => {
                      const sp = pools.find((p: { id: string }) => p.id === selectedPoolId);
                      return sp ? `${sp.priorityIds?.length ?? 0} items in pool (matching track filters will be added)` : '';
                    })() : 'Select a pool to add its items'}
                  </span>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => { setShowAddModal(false); setAddSourceMode('items'); setSelectedPoolId(null); }}
                      style={{ padding: '7px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddFromPool}
                      disabled={isAdding || !selectedPoolId}
                      style={wsGreenBtn(isAdding || !selectedPoolId)}
                    >
                      {isAdding ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
                      Add from pool
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export function TrackCriteriaTab({ track }: { track: Track }) {
  const allAttributes = useAttributesStore(state => state.attributes) || [];
  const fetchAttributesFromSupabase = useAttributesStore(state => state.fetchFromSupabase);
  const allCycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs) || [];
  const fetchCycleConfigs = useAttributeGovernanceStore(state => state.fetchCycleConfigs);
  const fetchTrackConfigs = useAttributeGovernanceStore(state => state.fetchTrackConfigs);
  const createCycleConfig = useAttributeGovernanceStore(state => state.createCycleConfig);
  const deleteCycleConfig = useAttributeGovernanceStore(state => state.deleteCycleConfig);

  const fetchTracksForCycle = useTrackStore(state => state.fetchTracksForCycle);
  const fetchAllTracks = useTrackStore(state => state.fetchAllTracks);
  const criteriaLocked = !!track?.criteriaLockedAt;
  const [criteriaLockBusy, setCriteriaLockBusy] = useState(false);
  const [removeBusyId, setRemoveBusyId] = useState<string | null>(null);

  // Refresh configs regardless of whether this track is cycle-bound or standalone
  const refreshConfigs = useCallback(async () => {
    if (track.cycleId) {
      await fetchCycleConfigs(track.cycleId);
    } else {
      await fetchTrackConfigs(track.id);
    }
  }, [track.cycleId, track.id, fetchCycleConfigs, fetchTrackConfigs]);

  const handleRemoveCriteria = useCallback(async (configId: string) => {
    setRemoveBusyId(configId);
    try {
      await deleteCycleConfig(configId);
      await refreshConfigs();
    } finally {
      setRemoveBusyId(null);
    }
  }, [deleteCycleConfig, refreshConfigs]);

  const { showToast } = useToast();

  // Where each criterion's values come from (1062). Saved on the track's row the way a
  // weight is; the server refuses it while the criteria are locked or without the right.
  const updateCycleConfigRow = useAttributeGovernanceStore(state => state.updateCycleConfig);
  const getCycleOverride = useAttributeGovernanceStore(state => state.getCycleOverride);
  const getTemplateByKey = useAttributeGovernanceStore(state => state.getTemplateByKey);
  const [valuesFromBusyId, setValuesFromBusyId] = useState<string | null>(null);
  const handleValuesFrom = useCallback(async (configId: string, next: { from: ValuesFrom | null; personId: string | null }) => {
    setValuesFromBusyId(configId);
    try {
      const result = await updateCycleConfigRow(configId, {
        collectionMethod: next.from ?? null,
        specificPersonId: next.from === 'specific_person' ? next.personId : null,
      });
      if (result.error) {
        showToast('error', 'Not saved', `${result.error}. Nothing was changed.`);
      }
      await refreshConfigs();
    } finally {
      setValuesFromBusyId(null);
    }
  }, [updateCycleConfigRow, refreshConfigs, showToast]);

  // Versions of the locked criteria (migration 1005, roadmap 1C V-11): every
  // lock is the next version; reopening closes it; once a session ran on the
  // open version, reopening asks why.
  const [versions, setVersions] = useState<TrackCriteriaVersion[]>([]);
  const [reopenOpen, setReopenOpen] = useState(false);
  const refreshVersions = useCallback(async () => {
    const result = await fetchTrackCriteriaVersions(track.id);
    if (result.error) console.error('[TrackCriteriaTab] Versions not read:', result.error);
    setVersions(result.versions);
  }, [track.id]);
  const criteriaLockedAtMs = track?.criteriaLockedAt ? new Date(track.criteriaLockedAt).getTime() : null;
  useEffect(() => {
    void refreshVersions();
  }, [refreshVersions, criteriaLockedAtMs]);
  const currentVersion = openVersion(versions);
  const earlierVersions = versions.filter((v) => v.id !== currentVersion?.id);
  const reopenAsksWhy = criteriaLocked && reopenNeedsReason(currentVersion);

  // Who may lock or reopen (711), and who may add or remove a criterion (1006).
  const { lockGate, criteriaGate } = useTrackAuthority(track);
  const criteriaWorkspaceId = useTenantStore(s => s.currentWorkspaceId);
  const { nameOf, activeMembers } = useWorkspaceMembers(criteriaWorkspaceId);
  // Which connected system keeps each criterion's value (canon 01 › Field Source): the
  // column says "kept by Jira", and warns when the track's answer is not the record.
  const keepers = useAttributeKeepers(criteriaWorkspaceId);

  const changeCriteriaLock = useCallback(async (target: boolean, reason?: string) => {
    setCriteriaLockBusy(true);
    try {
      // The tab is "Criteria & Scales" — one lock covers both. Locking criteria
      // while leaving scales editable was a half-lock (owner, 2026-07-09).
      // One server transaction: either half refused → neither changed.
      const result = await setTrackCriteriaAndRangesLockedViaRpc(track.id, target, reason);
      if (!result.success) {
        if (result.code === 'reason_required') {
          // A session ran since the versions were read: ask why, as the server does.
          await refreshVersions();
          setReopenOpen(true);
        }
        return result;
      }
      showToast(
        'success',
        target
          ? `Criteria & scales locked${result.version ? ` — version ${result.version}` : ''}`
          : result.version ? `Version ${result.version} reopened` : 'Criteria & scales unlocked',
        target ? undefined : result.version ? `Version ${result.version} is kept as it was locked.` : undefined,
      );
      if (track.cycleId) {
        await fetchTracksForCycle(track.cycleId);
      } else {
        await fetchAllTracks();
      }
      await refreshVersions();
      return result;
    } finally {
      setCriteriaLockBusy(false);
    }
  }, [track.id, track.cycleId, fetchTracksForCycle, fetchAllTracks, showToast, refreshVersions]);

  const handleCriteriaLockToggle = useCallback(async () => {
    if (criteriaLocked && reopenAsksWhy) {
      setReopenOpen(true);
      return;
    }
    const target = !criteriaLocked;
    const result = await changeCriteriaLock(target);
    if (!result.success && result.code !== 'reason_required') {
      console.error('[TrackCriteriaTab] Lock failed:', result.error);
      showToast('error', `Criteria & scales not ${target ? 'locked' : 'unlocked'}`, `${result.error ?? 'The server refused the change'}. Nothing was changed.`);
    }
  }, [criteriaLocked, reopenAsksWhy, changeCriteriaLock, showToast]);

  const handleReopenWithReason = useCallback(async (reason: string) => {
    const result = await setTrackCriteriaAndRangesLockedViaRpc(track.id, false, reason);
    if (!result.success) throw new Error(`${result.error ?? 'The server refused the change'}. Nothing was changed.`);
    setReopenOpen(false);
    showToast('success', `Version ${result.version ?? currentVersion?.version ?? ''} reopened`.trim(), 'Your reason was recorded with the version.');
    if (track.cycleId) {
      await fetchTracksForCycle(track.cycleId);
    } else {
      await fetchAllTracks();
    }
    await refreshVersions();
  }, [track.id, track.cycleId, currentVersion?.version, fetchTracksForCycle, fetchAllTracks, showToast, refreshVersions]);

  const dayOf = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const byWhom = (userId: string | null) => (userId ? ` by ${nameOf(userId)}` : '');
  
  // Filter configs for this track (criteria are track-scoped per spec)
  const trackConfigs = useMemo(() => 
    criteriaRowsForTrack(allCycleConfigs, track),
    [allCycleConfigs, track.cycleId, track.id]
  );

  // Strategic objectives as criteria (1066): ticking one adds its row the way Add Criteria
  // does (the database checks it and makes its attribute), unticking removes it like Remove.
  const objectiveKeysOnTrack = useMemo(
    () => trackConfigs.map(c => c.attributeKey).filter(isObjectiveCriterionKey),
    [trackConfigs],
  );
  const cycleObjectives = useCycleObjectives(track.cycleId, objectiveKeysOnTrack);
  const [objectiveBusyKey, setObjectiveBusyKey] = useState<string | null>(null);
  const handleObjectiveToggle = useCallback(async (option: CycleObjectiveOption) => {
    setObjectiveBusyKey(option.key);
    try {
      const row = trackConfigs.find(c => c.attributeKey === option.key);
      if (row) {
        const { error } = await deleteCycleConfig(row.id);
        if (error) throw new Error(error);
      } else {
        await createCycleConfig({
          cycleId: track.cycleId ?? undefined,
          trackId: track.id,
          attributeKey: option.key,
          rangeStatus: 'accepted',
        });
        await fetchAttributesFromSupabase();
      }
      await refreshConfigs();
    } catch (err) {
      showToast('error', 'Objective not changed', err instanceof Error ? err.message : String(err));
    } finally {
      setObjectiveBusyKey(null);
    }
  }, [trackConfigs, deleteCycleConfig, createCycleConfig, track.id, track.cycleId, fetchAttributesFromSupabase, refreshConfigs, showToast]);
  const genericAlignmentRow = objectiveKeysOnTrack.length > 0
    ? trackConfigs.find(c => c.attributeKey === 'strategic_alignment')
    : undefined;
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAttributeKey, setSelectedAttributeKey] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    fetchAttributesFromSupabase();
    if (track.cycleId) {
      void fetchCycleConfigs(track.cycleId);
    } else {
      void fetchTrackConfigs(track.id);
    }
  }, [track.cycleId, track.id, fetchAttributesFromSupabase, fetchCycleConfigs, fetchTrackConfigs]);
  
  // Get attributes already configured for this track (DB unique is cycle_id + track_id + attribute_key per migration 195)
  const configuredAttributeKeysInTrack = useMemo(
    () => new Set(allCycleConfigs.filter(c => c.trackId === track.id).map(c => c.attributeKey)),
    [allCycleConfigs, track.id]
  );
  // Objectives are chosen in their own list below (1066), and once one is a criterion here
  // the generic Strategic Alignment would score the same thing without saying which.
  const trackHasObjective = [...configuredAttributeKeysInTrack].some(isObjectiveCriterionKey);
  const availableAttributes = withoutObjectiveAttributes(allAttributes).filter(a =>
    !configuredAttributeKeysInTrack.has(a.key) && !(trackHasObjective && a.key === 'strategic_alignment'));
  
  // Get attribute details for configs
  const configsWithDetails = trackConfigs.map(config => ({
    ...config,
    attribute: allAttributes.find(a => a.key === config.attributeKey),
  }));
  
  const handleAddCriteria = async () => {
    if (!selectedAttributeKey || !track.id) return;
    setIsAdding(true);
    setAddError(null);
    
    try {
      // Use the store's createCycleConfig which handles Supabase sync
      // cycleId is omitted for standalone (unbound) tracks — the DB accepts null
      await createCycleConfig({
        cycleId: track.cycleId ?? undefined,
        trackId: track.id,
        attributeKey: selectedAttributeKey,
        rangeStatus: 'pending',
      });
      
      // Refresh the configs to ensure we have the latest
      await refreshConfigs();
      
      setShowAddModal(false);
      setSelectedAttributeKey('');
    } catch (err) {
      console.error('[TrackCriteriaTab] Error adding criteria:', err);
      const message = err instanceof Error ? err.message : 'Failed to add criteria';
      const isDuplicate = typeof message === 'string' && (
        message.includes('duplicate key') ||
        message.includes('unique constraint') ||
        message.includes('cycle_attribute_configs_cycle_id_attribute_key') ||
        message.includes('cycle_attribute_configs_cycle_track_attribute_key')
      );
      setAddError(isDuplicate
        ? `${selectedAttributeKey} is already configured for this track. Choose a different attribute.`
        : message
      );
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Criteria lock control */}
      <StepLockControl
        title="Criteria & scales"
        description={`${configsWithDetails.length} attribute${configsWithDetails.length !== 1 ? 's' : ''} configured for this track. Locking freezes criteria AND their scales/value ranges, and keeps them as a numbered version.`}
        status={deriveStepStatus(criteriaLocked, configsWithDetails.length >= 1)}
        conditionsMet={configsWithDetails.length >= 1}
        onLock={handleCriteriaLockToggle}
        onUnlock={handleCriteriaLockToggle}
        unlockLabel={reopenAsksWhy ? 'New version' : 'Unlock'}
        readOnlyNote={lockGate.allowed ? undefined : lockGate.reason}
        busy={criteriaLockBusy}
      />

      {versions.length > 0 && (
        <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-pri)' }}>
            {currentVersion ? (
              <>
                <strong>Version {currentVersion.version}</strong>
                {` · locked ${dayOf(currentVersion.lockedAt)}${byWhom(currentVersion.lockedBy)} · ${sessionsRunLine(currentVersion.sessionsRun)}`}
              </>
            ) : (
              <>
                <strong>Open for changes.</strong>
                {` Locking makes version ${(versions[0]?.version ?? 0) + 1}.`}
              </>
            )}
          </div>
          {earlierVersions.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)' }}>
                Earlier version{earlierVersions.length !== 1 ? 's' : ''} ({earlierVersions.length})
              </summary>
              <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {earlierVersions.map((v) => (
                  <li key={v.id} style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)' }}>
                    <div style={{ color: 'var(--text-pri)' }}>
                      <strong>Version {v.version}</strong>
                      {` — ${v.criteria.length ? v.criteria.map((c) => c.label).join(', ') : 'no criteria'}`}
                    </div>
                    <div>
                      {`Locked ${dayOf(v.lockedAt)}${byWhom(v.lockedBy)}`}
                      {v.reopenedAt && ` · reopened ${dayOf(v.reopenedAt)}${byWhom(v.reopenedBy)}`}
                      {` · ${sessionsRunLine(v.sessionsRun)}`}
                    </div>
                    {v.reopenReason && <div style={{ fontStyle: 'italic' }}>“{v.reopenReason}”</div>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {reopenOpen && currentVersion && (
        <RationaleModal
          title={`Start version ${currentVersion.version + 1}?`}
          subtitle="Criteria & scales"
          confirmLabel="Reopen for a new version"
          fieldLabel="Why do these criteria need to change?"
          placeholder={`Recorded with version ${currentVersion.version}, for everyone who reads its results.`}
          onConfirm={handleReopenWithReason}
          onClose={() => setReopenOpen(false)}
        >
          <p className="text-sm text-[var(--text-sec)] mb-3">{reopenConsequence(currentVersion)}</p>
        </RationaleModal>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={wsLabel}>Attributes</div>
        {criteriaGate.allowed && (
          <button
            onClick={() => setShowAddModal(true)}
            disabled={criteriaLocked}
            title={criteriaLocked ? 'Unlock criteria to add or remove' : undefined}
            style={wsGreenBtn(criteriaLocked)}
          >
            <Plus size={13} />
            Add Criteria
          </button>
        )}
      </div>

      {configsWithDetails.length === 0 ? (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', padding: '32px', textAlign: 'center' }}>
          <ListChecks size={40} style={{ color: 'var(--text-ter)', margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)', marginBottom: '4px' }}>No criteria configured</div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', marginBottom: '16px' }}>
            {criteriaGate.allowed ? 'Add attributes that will be used to evaluate and score items in this track.' : criteriaGate.reason}
          </div>
          {criteriaGate.allowed && (
            <button onClick={() => setShowAddModal(true)} disabled={criteriaLocked} style={{ ...wsGreenBtn(criteriaLocked), margin: '0 auto' }}>
              <Plus size={13} />
              Add First Criteria
            </button>
          )}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--hover-tint)' }}>
                <th style={wsTh}>Attribute</th>
                <th style={wsTh}>Values come from</th>
                <th style={wsTh}>Range status</th>
                <th style={wsTh}>Range</th>
                {!criteriaLocked && criteriaGate.allowed && <th style={{ ...wsTh, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {configsWithDetails.map(config => {
                const statusColor = config.rangeStatus === 'accepted' ? 'var(--green)' : config.rangeStatus === 'proposed' ? 'var(--mustard)' : 'var(--text-ter)';
                return (
                  <tr key={config.id}>
                    <td style={wsTd}>
                      <div style={{ fontWeight: 500 }}>{config.attribute?.label || config.attributeKey}</div>
                      {config.attribute?.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '2px' }}>{config.attribute.description}</div>}
                    </td>
                    <td style={wsTd}>
                      {/* Where this criterion's values come from (1062): the track's word, else the
                          cycle's or the workspace's, shown as such; locked with the criteria; the
                          server holds both the lock and the right (criteria_keep_locked_ranges). */}
                      {(() => {
                        const resolved = resolveValuesFrom({
                          track: { collectionMethod: config.collectionMethod, specificPersonId: config.specificPersonId },
                          cycle: track.cycleId ? getCycleOverride(track.cycleId, config.attributeKey) ?? null : null,
                          workspace: getTemplateByKey(config.attributeKey) ?? null,
                        });
                        const inherited = resolved.saidBy !== 'track' && resolved.from
                          ? ` (${resolved.saidBy === 'cycle' ? 'the cycle says' : 'the workspace says'})`
                          : '';
                        const keptBy = keepers.keeperName(config.attributeKey);
                        const note = keeperNote(config.attribute?.label || config.attributeKey, keptBy, resolved.from);
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <ValuesFromChoice
                              value={resolved.saidBy === 'track' ? resolved.from : null}
                              personId={resolved.saidBy === 'track' ? resolved.personId : null}
                              members={activeMembers.map((m) => ({ userId: m.userId, name: m.name }))}
                              disabled={criteriaLocked || !criteriaGate.allowed}
                              busy={valuesFromBusyId === config.id}
                              onChange={(next) => void handleValuesFrom(config.id, next)}
                            />
                            {resolved.saidBy !== 'track' && (
                              <span style={{ fontFamily: 'var(--sans)', fontSize: '0.7rem', color: 'var(--text-ter)' }}>
                                {resolved.from ? `${valuesFromLabel(resolved.from, nameOf(resolved.personId))}${inherited}` : 'Until it is said, S4 pre-fills from the record and the facilitator may type.'}
                              </span>
                            )}
                            {keptBy && resolved.from === 'any_group_member' && (
                              <span style={{ fontFamily: 'var(--sans)', fontSize: '0.7rem', color: 'var(--text-ter)' }} data-testid="values-from-kept-by">
                                kept by {keptBy}
                              </span>
                            )}
                            {note && (
                              <span style={{ fontFamily: 'var(--sans)', fontSize: '0.7rem', color: 'var(--mustard)' }} data-testid="values-from-keeper-note">
                                {note}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={wsTd}>
                      <span style={{
                        padding: '3px 8px', borderRadius: '2px', fontSize: '0.7rem', color: statusColor,
                        background: statusColor === 'var(--green)' ? 'rgba(58,125,86,0.12)' : statusColor === 'var(--mustard)' ? 'rgba(212,160,23,0.12)' : 'var(--hover-tint)',
                      }}>
                        {config.rangeStatus || 'pending'}
                      </span>
                    </td>
                    <td style={{ ...wsTd, color: 'var(--text-sec)' }}>{formatRangeDisplay(config.rangeValue) || '—'}</td>
                    {!criteriaLocked && criteriaGate.allowed && (
                      <td style={{ ...wsTd, textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => handleRemoveCriteria(config.id)}
                          disabled={removeBusyId === config.id}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--coral)', opacity: removeBusyId === config.id ? 0.5 : 1 }}
                          title="Remove criteria"
                        >
                          {removeBusyId === config.id ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {cycleObjectives.strategyOn && track.cycleId && criteriaGate.allowed && (
        <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', padding: '14px 16px' }}>
          <ObjectiveCriteriaChooser
            options={cycleObjectives.options}
            loading={cycleObjectives.loading}
            strategyName={cycleObjectives.strategyName}
            isOn={(key) => objectiveKeysOnTrack.includes(key)}
            onToggle={(o) => void handleObjectiveToggle(o)}
            disabled={criteriaLocked}
            disabledReason={criteriaLocked ? 'The criteria are locked. Unlock them to choose objectives.' : undefined}
            busyKey={objectiveBusyKey}
            alsoChosenCount={objectiveKeysOnTrack.filter(k => !cycleObjectives.options.some(o => o.key === k)).length}
          />
          {genericAlignmentRow && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[var(--text-sec)]" data-testid="generic-alignment-repeats">
              <span>Strategic Alignment on this track scores what the objectives score, without saying which objective.</span>
              {!criteriaLocked && (
                <button
                  type="button"
                  className="underline"
                  disabled={removeBusyId === genericAlignmentRow.id}
                  onClick={() => void handleRemoveCriteria(genericAlignmentRow.id)}
                >
                  {removeBusyId === genericAlignmentRow.id ? 'Removing…' : 'Remove Strategic Alignment'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Each criterion's share of the track's balance (1047); the wizard's Weights step saves the same way. */}
      <TrackCriteriaWeights
        trackId={track.id}
        rows={trackConfigs}
        labelOf={(key) => allAttributes.find(a => a.key === key)?.label || key}
        locked={criteriaLocked}
        gate={criteriaGate}
        onSaved={refreshConfigs}
      />

      {/* Add Criteria Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowAddModal(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', maxWidth: '420px', width: '100%', margin: '0 16px', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ fontFamily: 'var(--sans)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-pri)' }}>Add Criteria</div>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Attribute</label>
                <Select
                  value={selectedAttributeKey}
                  onChange={(e) => setSelectedAttributeKey(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--rule)', borderRadius: '2px', background: 'var(--bg)', color: 'var(--text-pri)', fontFamily: 'var(--sans)', fontSize: '0.85rem' }}
                >
                  <option value="">Select an attribute...</option>
                  {availableAttributes.map(attr => (
                    <option key={attr.key} value={attr.key}>{attr.label || attr.key}</option>
                  ))}
                </Select>
              </div>
              {addError && (
                <div style={{ padding: '10px 12px', background: 'rgba(192,83,58,0.06)', border: '1px solid rgba(192,83,58,0.25)', borderRadius: '2px', fontSize: '0.8rem', color: 'var(--coral)' }}>
                  {addError}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowAddModal(false)} style={{ padding: '7px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleAddCriteria} disabled={isAdding || !selectedAttributeKey} style={wsGreenBtn(isAdding || !selectedAttributeKey)}>
                {isAdding ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TrackValueRangesTab
// ============================================================================

export function TrackValueRangesTab({ track }: { track: Track }) {
  const userId = useUserStore(state => state.userId) || '';
  // Use the DB-resolved capability set directly. The old pattern of filtering through
  // toActorRole() silently dropped all job roles (criteria-authority, etc.) because
  // ActorRole only covers admin roles — making VALUE_RANGE_DEFINE invisible even when
  // the user held the Criteria Authority role assignment.
  const userCapabilities = useUserStore(state => state.resolvedCapabilityIds) as CapabilityId[];

  const allAttributes = useAttributesStore(state => state.attributes) || [];
  const allCycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs) || [];
  const fetchCycleConfigs = useAttributeGovernanceStore(state => state.fetchCycleConfigs);
  const updateCycleConfig = useAttributeGovernanceStore(state => state.updateCycleConfig);
  const saveTrackRange = useAttributeGovernanceStore(state => state.saveTrackRange);
  const canDefineRange = useAttributeGovernanceStore(state => state.canDefineRange);
  const canApproveRange = useAttributeGovernanceStore(state => state.canApproveRange);
  const acceptedValues = useValueProposalStore(state => state.acceptedValues) ?? [];
  const allPools = useItemPoolsStore(state => state.itemPools) ?? [];
  const trackPool = useMemo(() => allPools.find(p => p.trackId === track.id), [allPools, track.id]);
  const poolItemIds = useMemo(() => trackPool?.priorityIds ?? [], [trackPool]);

  const fetchTracksForCycle = useTrackStore(state => state.fetchTracksForCycle);
  const fetchAllTracksVR = useTrackStore(state => state.fetchAllTracks);
  const rangeSetLocked = !!track?.rangeSetLockedAt;
  const [lockBusy, setLockBusy] = useState(false);
  const { lockGate } = useTrackAuthority(track);

  const { effective: effectiveParticipants, effectiveUserIds: effectiveProviderIds, loading: effectiveLoading } = useEffectiveTrackParticipants(track.cycleId ?? undefined, track.id);
  const [valueProviderUserDisplay, setValueProviderUserDisplay] = useState<Record<string, { name?: string; email: string }>>({});

  useEffect(() => {
    const ids = effectiveProviderIds.filter(Boolean);
    if (ids.length === 0) return;
    import('../../../lib/supabaseClient').then(async ({ getSupabaseClient }) => {
      const supabase = getSupabaseClient();
      const { data: users } = await supabase.from('users').select('id, email, name').in('id', ids);
      if (users?.length) {
        const map: Record<string, { name?: string; email: string }> = {};
        users.forEach((u: { id: string; email: string; name?: string }) => { map[u.id] = { name: u.name, email: u.email || '' }; });
        setValueProviderUserDisplay(prev => ({ ...prev, ...map }));
      }
    });
  }, [effectiveProviderIds]);

  const valueProviderDisplayName = useCallback((uid: string) => {
    const u = valueProviderUserDisplay[uid];
    return u?.name || u?.email || 'Participant';
  }, [valueProviderUserDisplay]);

  const fetchTrackConfigsVR = useAttributeGovernanceStore(state => state.fetchTrackConfigs);

  // Helper: refresh configs after changes, works for both cycle-bound and standalone tracks
  const refreshRangeConfigs = useCallback(async () => {
    if (track.cycleId) {
      await fetchCycleConfigs(track.cycleId);
    } else {
      await fetchTrackConfigsVR(track.id);
    }
  }, [track.cycleId, track.id, fetchCycleConfigs, fetchTrackConfigsVR]);

  useEffect(() => {
    if (track.cycleId) {
      void fetchCycleConfigs(track.cycleId);
    } else {
      void fetchTrackConfigsVR(track.id);
    }
  }, [track.cycleId, track.id, fetchCycleConfigs, fetchTrackConfigsVR]);

  const fetchParticipantsForCycle = useCycleParticipantsStore(state => state.fetchParticipantsForCycle);
  const fetchTrackParticipantsForTrack = useTrackParticipantsStore(state => state.fetchParticipantsForTrack);
  useEffect(() => {
    if (track.cycleId) fetchParticipantsForCycle(track.cycleId);
    if (track.id) fetchTrackParticipantsForTrack(track.id);
  }, [track.cycleId, track.id, fetchParticipantsForCycle, fetchTrackParticipantsForTrack]);

  const trackConfigs = useMemo(() =>
    criteriaRowsForTrack(allCycleConfigs, track),
    [allCycleConfigs, track.cycleId, track.id]
  );
  const configsWithDetails = trackConfigs.map(config => ({
    ...config,
    attribute: allAttributes.find(a => a.key === config.attributeKey),
  }));

  // Per-config editing state
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [rangeForm, setRangeForm] = useState<ValueRange>({ type: 'number', min: 1, max: 5, step: 1 });
  const [rangeOptionsText, setRangeOptionsText] = useState('');
  const [rangeSaveBusy, setRangeSaveBusy] = useState(false);
  const [acceptBusyId, setAcceptBusyId] = useState<string | null>(null);
  const [rangeGovernanceError, setRangeGovernanceError] = useState<string | null>(null);
  const [rangeModalProviderIds, setRangeModalProviderIds] = useState<string[]>([]);

  const { showToast } = useToast();
  const handleLockToggle = useCallback(async () => {
    setLockBusy(true);
    try {
      const result = await setTrackRangesLockedViaRpc(track.id, !rangeSetLocked);
      if (result.success) {
        if (track.cycleId) {
          await fetchTracksForCycle(track.cycleId);
        } else {
          await fetchAllTracksVR();
        }
        showToast('success', rangeSetLocked ? 'Scales unlocked' : 'Scales locked');
      } else {
        console.error('[TrackValueRangesTab] Lock failed:', result.error);
        showToast(
          'error',
          rangeSetLocked ? 'Scales not unlocked' : 'Scales not locked',
          `${result.error ?? 'The server refused the change'}. Nothing was changed.`,
        );
      }
    } finally {
      setLockBusy(false);
    }
  }, [track.id, track.cycleId, rangeSetLocked, fetchTracksForCycle, fetchAllTracksVR, showToast]);

  const openRangeEditor = (config: typeof configsWithDetails[0]) => {
    setRangeGovernanceError(null);
    setEditingConfigId(config.id);
    const hasPersistedRange = config.rangeValue && (config.rangeValue.options?.length || config.rangeValue.min !== undefined || config.rangeValue.max !== undefined);
    const initial: ValueRange = hasPersistedRange ? { ...config.rangeValue! } : { type: 'number', min: 1, max: 5, step: 1 };
    setRangeForm(initial);
    setRangeOptionsText(Array.isArray(initial.options) && initial.options.length ? initial.options.join(', ') : initial.type === 'fibonacci' ? '1, 2, 3, 5, 8, 13' : '');
    setRangeModalProviderIds(config.specificPersonId ? config.specificPersonId.split(',').filter(Boolean) : []);
  };

  const handleSaveRange = async () => {
    if (!editingConfigId) return;
    const config = configsWithDetails.find(c => c.id === editingConfigId);
    if (!config || !track.id) return;
    if (!canDefineRange(config.attributeKey, userCapabilities, track.cycleId ?? undefined, track.id)) {
      setRangeGovernanceError('You do not have permission to define or edit value ranges for this criterion. Check Settings → Attribute permissions.');
      return;
    }
    setRangeGovernanceError(null);
    setRangeSaveBusy(true);
    const canAlsoApprove = canApproveRange(config.attributeKey, userCapabilities, track.cycleId ?? undefined, track.id);
    try {
      let rangeValueToSave: ValueRange = rangeForm;
      if (rangeForm.type === 'select' || rangeForm.type === 'scale' || rangeForm.type === 'custom') {
        rangeValueToSave = { ...rangeForm, options: rangeOptionsText.split(',').map(s => s.trim()).filter(Boolean) };
      } else if (rangeForm.type === 'fibonacci') {
        rangeValueToSave = { ...rangeForm, options: rangeOptionsText.split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n)) };
      }
      // The same save the cycle configure wizard's Value ranges step uses.
      const saved = await saveTrackRange(editingConfigId, rangeValueToSave, {
        by: userId,
        accept: canAlsoApprove,
        specificPersonId: rangeModalProviderIds.length > 0 ? rangeModalProviderIds.join(',') : undefined,
      });
      if (saved.error) {
        setRangeGovernanceError(`Range not saved: ${saved.error}`);
        return;
      }
      await refreshRangeConfigs();
      setEditingConfigId(null);
    } catch (err) {
      console.error('[TrackValueRangesTab] Error saving range:', err);
    } finally {
      setRangeSaveBusy(false);
    }
  };

  const handleAcceptRange = async (configId: string) => {
    const config = configsWithDetails.find(c => c.id === configId);
    if (!config || !track.id) return;
    if (!canApproveRange(config.attributeKey, userCapabilities, track.cycleId ?? undefined, track.id)) {
      setRangeGovernanceError('You do not have permission to approve value ranges for this criterion.');
      return;
    }
    setRangeGovernanceError(null);
    setAcceptBusyId(configId);
    try {
      await updateCycleConfig(configId, { rangeStatus: 'accepted', rangeAcceptedBy: userId, rangeAcceptedAt: new Date() });
      await refreshRangeConfigs();
    } catch (err) {
      console.error('[TrackValueRangesTab] Error accepting range:', err);
    } finally {
      setAcceptBusyId(null);
    }
  };

  const allAccepted = configsWithDetails.length > 0 && configsWithDetails.every(c => c.rangeStatus === 'accepted');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <StepLockControl
        title="Value ranges"
        description={
          configsWithDetails.length === 0
            ? 'No criteria configured yet. Add criteria first in the Criteria & scales tab.'
            : `${configsWithDetails.filter(c => c.rangeStatus === 'accepted').length} of ${configsWithDetails.length} range${configsWithDetails.length !== 1 ? 's' : ''} accepted.`
        }
        status={deriveStepStatus(rangeSetLocked, allAccepted)}
        conditionsMet={allAccepted}
        onLock={handleLockToggle}
        onUnlock={handleLockToggle}
        readOnlyNote={lockGate.allowed ? undefined : lockGate.reason}
        busy={lockBusy}
      />

      {rangeGovernanceError && !editingConfigId && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '2px', border: '1px solid rgba(212,160,23,0.25)', background: 'rgba(212,160,23,0.06)', fontSize: '0.8rem', color: 'var(--mustard)' }}>
          <span>{rangeGovernanceError}</span>
          <button type="button" onClick={() => setRangeGovernanceError(null)} style={{ marginLeft: '12px', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mustard)' }}>Dismiss</button>
        </div>
      )}

      {configsWithDetails.length === 0 ? (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', padding: '32px', textAlign: 'center' }}>
          <Sliders size={40} style={{ color: 'var(--text-ter)', margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)', marginBottom: '4px' }}>No criteria configured</div>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>Go to the <strong>Criteria &amp; scales</strong> tab to add attributes first.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {configsWithDetails.map(config => {
            const providerIds = config.specificPersonId ? config.specificPersonId.split(',').filter(Boolean) : [];
            const providerNames = providerIds.map(uid => valueProviderDisplayName(uid)).join(', ') || null;
            const isEditing = editingConfigId === config.id;
            const canDefine = !rangeSetLocked && !!track.id && canDefineRange(config.attributeKey, userCapabilities, track.cycleId ?? undefined, track.id);
            const canApprove = !rangeSetLocked && !!track.id && canApproveRange(config.attributeKey, userCapabilities, track.cycleId ?? undefined, track.id);

            const existingValuesForAttribute = acceptedValues.filter(av => av.attributeKey === config.attributeKey && poolItemIds.includes(av.itemId));
            const existingCount = existingValuesForAttribute.length;
            const statusColor = config.rangeStatus === 'accepted' ? 'var(--green)' : config.rangeStatus === 'proposed' ? 'var(--mustard)' : 'var(--text-ter)';

            return (
              <div key={config.id} style={{ border: `1px solid ${isEditing ? 'var(--slate)' : 'var(--rule)'}`, borderRadius: '2px', overflow: 'hidden' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'var(--bg-raised)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <div>
                      <p style={{ fontFamily: 'var(--sans)', fontWeight: 500, color: 'var(--text-pri)', fontSize: '0.85rem' }}>{config.attribute?.label || config.attributeKey}</p>
                      {config.attribute?.description && <p style={{ fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '2px' }}>{config.attribute.description}</p>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: '2px', fontSize: '0.7rem', fontWeight: 500, color: statusColor,
                      background: statusColor === 'var(--green)' ? 'rgba(58,125,86,0.12)' : statusColor === 'var(--mustard)' ? 'rgba(212,160,23,0.12)' : 'var(--hover-tint)',
                    }}>
                      {config.rangeStatus || 'pending'}
                    </span>
                    {config.rangeStatus === 'proposed' && canApprove && (
                      <button
                        type="button"
                        onClick={() => handleAcceptRange(config.id)}
                        disabled={acceptBusyId === config.id}
                        style={{ ...wsGreenBtn(acceptBusyId === config.id), padding: '5px 12px' }}
                      >
                        {acceptBusyId === config.id ? <Loader2 className="animate-spin" size={13} /> : 'Accept'}
                      </button>
                    )}
                    {isObjectiveCriterionKey(config.attributeKey) ? (
                      // 1066: an objective is scored on the track's shared range and takes no range of its own.
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-sec)' }}>Scored on the track&apos;s range; recorded as None to Primary</span>
                    ) : !isEditing && (
                      <button
                        type="button"
                        onClick={() => openRangeEditor(config)}
                        disabled={!canDefine}
                        title={!canDefine ? (rangeSetLocked ? 'Unlock ranges to edit' : 'You do not have permission to define ranges for this criterion') : undefined}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px',
                          fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                          color: 'var(--text-ter)', background: 'var(--hover-tint)', border: '1px solid var(--rule)', borderRadius: '2px',
                          cursor: canDefine ? 'pointer' : 'not-allowed', opacity: canDefine ? 1 : 0.4,
                        }}
                      >
                        <Sliders size={13} />
                        {config.rangeValue ? 'Edit range' : 'Set range'}
                      </button>
                    )}
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => setEditingConfigId(null)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        <X size={13} /> Cancel
                      </button>
                    )}
                  </div>
                </div>

                {/* Current summary (when not editing) */}
                {!isEditing && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '10px 18px', background: 'var(--hover-tint)', borderTop: '1px solid var(--rule)', fontSize: '0.8rem' }}>
                    <div>
                      <span style={wsLabel}>Range</span>
                      <p style={{ color: 'var(--text-pri)', marginTop: '2px' }}>{formatRangeDisplay(config.rangeValue) || '—'}</p>
                    </div>
                    <div>
                      <span style={wsLabel}>Value providers</span>
                      <p style={{ color: 'var(--text-pri)', marginTop: '2px' }}>{providerNames ?? 'Any participant'}</p>
                    </div>
                    <div>
                      <span style={wsLabel}>Evidence</span>
                      <p style={{ color: 'var(--text-pri)', marginTop: '2px' }}>{config.requiresEvidence ? 'Required' : 'Not required'}</p>
                    </div>
                  </div>
                )}

                {/* Inline editing form */}
                {isEditing && (() => {
                  const attr = allAttributes.find(a => a.key === config.attributeKey) as
                    | ({ scales?: Array<{ id: string; label: string; scaleType: string; points: Array<{ value: number; label: string }> }> })
                    | undefined;
                  const scales = attr?.scales;
                  const rangeInput: CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid var(--rule)', borderRadius: '2px', background: 'var(--bg)', color: 'var(--text-pri)', fontFamily: 'var(--sans)', fontSize: '0.85rem' };
                  return (
                    <div style={{ padding: '20px', borderTop: '1px solid var(--rule)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                      {rangeGovernanceError && (
                        <div style={{ padding: '10px 12px', borderRadius: '2px', background: 'rgba(212,160,23,0.1)', border: '1px solid rgba(212,160,23,0.3)', fontSize: '0.8rem', color: 'var(--mustard)' }}>{rangeGovernanceError}</div>
                      )}
                      {existingCount > 0 && (
                        <div style={{ borderRadius: '2px', border: '1px solid var(--rule)', background: 'var(--hover-tint)', padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-pri)' }}>
                          <strong>{existingCount} item{existingCount !== 1 ? 's' : ''}</strong> already have values for this attribute.
                        </div>
                      )}

                      {/* Predefined scales */}
                      {scales && scales.length > 0 && (
                        <div>
                          <label style={{ display: 'block', ...wsLabel, marginBottom: '8px' }}>Use a predefined scale</label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {scales.map(scale => (
                              <button
                                key={scale.id}
                                type="button"
                                onClick={() => {
                                  const isNumeric = scale.scaleType === 'numeric' || scale.scaleType === 'percentage';
                                  const nums = scale.points.map(p => p.value);
                                  if (isNumeric && nums.length >= 2) { setRangeForm({ type: 'number', min: Math.min(...nums), max: Math.max(...nums), step: nums.length > 1 ? nums[1] - nums[0] : 1 }); setRangeOptionsText(''); }
                                  else if (scale.scaleType === 'fibonacci') { setRangeForm({ type: 'fibonacci' }); setRangeOptionsText(scale.points.map(p => p.value).join(', ')); }
                                  else { setRangeForm({ type: 'select' }); setRangeOptionsText(scale.points.map(p => p.label).join(', ')); }
                                }}
                                style={{ textAlign: 'left', padding: '8px 12px', borderRadius: '2px', border: '1px solid var(--rule)', background: 'var(--bg-raised)', cursor: 'pointer' }}
                              >
                                <span style={{ display: 'block', fontFamily: 'var(--sans)', fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-pri)' }}>{scale.label}</span>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-sec)', marginTop: '2px' }}>{scale.points.map(p => p.label).join(' · ')}</span>
                              </button>
                            ))}
                          </div>
                          <div style={{ position: 'relative', margin: '16px 0', textAlign: 'center', borderTop: '1px solid var(--rule)' }}>
                            <span style={{ position: 'relative', top: '-9px', background: 'var(--bg-raised)', padding: '0 8px', ...wsLabel }}>or configure manually</span>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                        <div>
                          <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Type</label>
                          <Select
                            value={rangeForm.type}
                            onChange={(e) => setRangeForm(f => ({ ...f, type: e.target.value as ValueRange['type'] }))}
                            style={rangeInput}
                          >
                            <option value="number">Number (min–max)</option>
                            <option value="select">Select (options)</option>
                            <option value="scale">Scale (options)</option>
                            <option value="fibonacci">Fibonacci</option>
                            <option value="custom">Custom options</option>
                          </Select>
                        </div>
                        {rangeForm.type === 'number' && (
                          <>
                            <div>
                              <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Min</label>
                              <input type="number" value={rangeForm.min ?? ''} onChange={(e) => setRangeForm(f => ({ ...f, min: e.target.value === '' ? undefined : Number(e.target.value) }))} style={rangeInput} />
                            </div>
                            <div>
                              <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Max</label>
                              <input type="number" value={rangeForm.max ?? ''} onChange={(e) => setRangeForm(f => ({ ...f, max: e.target.value === '' ? undefined : Number(e.target.value) }))} style={rangeInput} />
                            </div>
                            <div>
                              <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Step (optional)</label>
                              <input type="number" value={rangeForm.step ?? ''} onChange={(e) => setRangeForm(f => ({ ...f, step: e.target.value === '' ? undefined : Number(e.target.value) }))} style={rangeInput} />
                            </div>
                          </>
                        )}
                        {(rangeForm.type === 'select' || rangeForm.type === 'scale' || rangeForm.type === 'custom') && (
                          <div style={{ gridColumn: 'span 2' }}>
                            <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Options (comma-separated)</label>
                            <input type="text" value={rangeOptionsText} onChange={(e) => setRangeOptionsText(e.target.value)} placeholder="e.g. Low, Medium, High" style={rangeInput} />
                          </div>
                        )}
                        {rangeForm.type === 'fibonacci' && (
                          <div style={{ gridColumn: 'span 2' }}>
                            <label style={{ display: 'block', ...wsLabel, marginBottom: '6px' }}>Options (comma-separated numbers)</label>
                            <input type="text" value={rangeOptionsText} onChange={(e) => setRangeOptionsText(e.target.value)} placeholder="1, 2, 3, 5, 8, 13" style={rangeInput} />
                          </div>
                        )}
                      </div>

                      {/* Value providers */}
                      <div style={{ paddingTop: '12px', borderTop: '1px solid var(--rule)' }}>
                        <label style={{ display: 'block', ...wsLabel, marginBottom: '4px' }}>Who provides values?</label>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-sec)', marginBottom: '8px' }}>Leave all unchecked for any participant. Check specific people to restrict.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '144px', overflowY: 'auto' }}>
                          {effectiveLoading ? (
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Loading participants…</p>
                          ) : effectiveProviderIds.length === 0 ? (
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)' }}>No participants in this cycle or track yet.</p>
                          ) : (
                            effectiveParticipants.map(p => (
                              <label key={p.userId} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={rangeModalProviderIds.includes(p.userId)}
                                  onChange={(e) => {
                                    if (e.target.checked) setRangeModalProviderIds(prev => [...prev, p.userId]);
                                    else setRangeModalProviderIds(prev => prev.filter(id => id !== p.userId));
                                  }}
                                />
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-pri)' }}>{valueProviderDisplayName(p.userId)}</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
                        <button
                          type="button"
                          onClick={() => setEditingConfigId(null)}
                          style={{ padding: '7px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveRange}
                          disabled={rangeSaveBusy || (rangeForm.type === 'number' && rangeForm.min == null && rangeForm.max == null)}
                          style={wsGreenBtn(rangeSaveBusy || (rangeForm.type === 'number' && rangeForm.min == null && rangeForm.max == null))}
                        >
                          {rangeSaveBusy && <Loader2 className="animate-spin" size={13} />}
                          {canApproveRange(config.attributeKey, userCapabilities, track.cycleId ?? undefined, track.id) ? 'Save & accept' : 'Save as proposed'}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Value range presets aligned to the configured scale definitions
const S5_RANGE_PRESETS: Record<string, (string | number)[]> = {
  fibonacci: [1, 2, 3, 5, 8, 13, 21],
  tshirt: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
};

function getOptionsFromRange(range: ValueRange | undefined): (string | number)[] | null {
  if (!range) return null;
  if (range.options && range.options.length > 0) return range.options;
  if (range.type === 'fibonacci') return S5_RANGE_PRESETS.fibonacci;
  return null;
}

interface TrackReadinessFlags {
  participationLocked: boolean;
  poolLocked: boolean;
  criteriaLocked: boolean;
  rangeSetLocked: boolean;
}

export function TrackValuesTab({
  track,
  readinessFlags = { participationLocked: false, poolLocked: false, criteriaLocked: false, rangeSetLocked: false },
}: {
  track: Track;
  readinessFlags?: TrackReadinessFlags;
}) {
  const s1to4Met =
    readinessFlags.participationLocked &&
    readinessFlags.poolLocked &&
    readinessFlags.criteriaLocked &&
    readinessFlags.rangeSetLocked;
  const proposals = useValueProposalStore(state => state.proposals) || [];
  const getAcceptedValuesForAttribute = useValueProposalStore(state => state.getAcceptedValuesForAttribute);
  const getSourceProposalForAcceptedValue = useValueProposalStore(state => state.getSourceProposalForAcceptedValue);
  const fetchFromSupabase = useValueProposalStore(state => state.fetchFromSupabase);
  const acceptValueDirectly = useValueProposalStore(state => state.acceptValueDirectly);
  const allPools = useItemPoolsStore(state => state.itemPools) || [];
  const allItems = useItemsStore(state => state.items) || [];
  const attributes = useAttributesStore(state => state.attributes) || [];
  const fetchTrackConfigs = useAttributeGovernanceStore(state => state.fetchTrackConfigs);
  // Subscribe to the DATA, not to getTrackConfigs (same rule as the
  // Criteria tab). The getter is a stable function, so a memo that depends on it never
  // recomputes when the fetch below resolves: opened first, this tab read "no criteria"
  // forever and drew an empty Value Grid until another tab had loaded them.
  const allCycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);
  const currentUserId = useUserStore(state => state.userId) || 'current-user';
  const fetchTracksForCycle = useTrackStore(state => state.fetchTracksForCycle);
  const fetchAllTracks = useTrackStore(state => state.fetchAllTracks);
  const { users } = useSystemUsers();
  const currentWorkspaceId = useTenantStore(state => state.currentWorkspaceId);
  const lockTrack = useValueCollectionStore(state => state.lockTrack);

  /** Effective scale for a criterion (attribute): default or first scale so saving doesn't overwrite other scales. */
  const getEffectiveScaleId = useCallback((attributeKey: string): string | null | undefined => {
    const presetScaleIds = track.metadata?.workflowPresetScaleIds;
    const workflowPresetScaleId =
      presetScaleIds && typeof presetScaleIds === 'object' && typeof (presetScaleIds as Record<string, unknown>)[attributeKey] === 'string'
        ? (presetScaleIds as Record<string, string>)[attributeKey]
        : null;
    const attr = attributes.find((a: { key: string }) => a.key === attributeKey) as { scales?: Array<{ id: string; label?: string; isDefault?: boolean }>; defaultScaleId?: string } | undefined;
    if (!attr?.scales?.length) return null;
    return workflowPresetScaleId ?? attr.defaultScaleId ?? attr.scales.find(s => s.isDefault)?.id ?? attr.scales[0]?.id ?? null;
  }, [attributes, track.metadata]);

  const resolveItemName = useCallback((itemId: string) => {
    const item = allItems.find(i => i.id === itemId);
    return item?.title ?? itemId.slice(0, 8) + '…';
  }, [allItems]);

  const resolveUserName = useCallback((userId: string | undefined) => {
    if (!userId) return '—';
    const user = users.find(u => u.id === userId);
    return user?.name ?? user?.email ?? 'Participant';
  }, [users]);

  const valuesConverged = !!track?.valuesConvergedAt;
  const { lockGate } = useTrackAuthority(track);
  const [valuesLockBusy, setValuesLockBusy] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  const refreshTrack = useCallback(async () => {
    if (track.cycleId) await fetchTracksForCycle(track.cycleId);
    else await fetchAllTracks();
  }, [track.cycleId, fetchTracksForCycle, fetchAllTracks]);

  const { showToast } = useToast();
  const handleLockTrackValues = useCallback(async () => {
    if (!currentWorkspaceId) return;
    setShowLockConfirm(false);
    setValuesLockBusy(true);
    try {
      const ok = await lockTrack({
        trackId: track.id,
        actorId: currentUserId,
        workspaceId: currentWorkspaceId,
        isAutomatic: false,
      });
      if (ok) {
        showToast('success', 'Values locked');
        await refreshTrack();
      } else {
        showToast('error', 'Values not locked', 'The server refused the lock and nothing was changed. Check that every required value is accepted, then try again.');
      }
    } finally {
      setValuesLockBusy(false);
    }
  }, [currentWorkspaceId, lockTrack, track.id, currentUserId, refreshTrack, showToast]);

  const handleValuesLockToggle = useCallback(async () => {
    setValuesLockBusy(true);
    try {
      const result = await setTrackValuesConvergedViaRpc(track.id, !valuesConverged);
      if (!result.success) {
        showToast(
          'error',
          valuesConverged ? 'Values not unlocked' : 'Values not locked',
          `${result.error ?? 'The server refused the change'}. Nothing was changed.`,
        );
        return;
      }
      showToast('success', valuesConverged ? 'Values unlocked' : 'Values locked');
      await refreshTrack();
    } finally {
      setValuesLockBusy(false);
    }
  }, [track.id, valuesConverged, refreshTrack, showToast]);

  const [savingCell, setSavingCell] = useState<{ itemId: string; attr: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [localValues, setLocalValues] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);
  // Which track this tab's own criteria read has finished for. An empty list means one of
  // two very different things: nobody has configured criteria, or they have not been read
  // yet. Telling someone to "configure criteria for this track" when five are configured,
  // accepted and locked is the second case wearing the first case's words — so the hint
  // below waits for this read, and stays silent if the read failed.
  const [criteriaLoadedForTrack, setCriteriaLoadedForTrack] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchTrackConfigs(track.id).then((loaded) => {
      if (!cancelled && loaded) setCriteriaLoadedForTrack(track.id);
    });
    return () => { cancelled = true; };
  }, [track.id, fetchTrackConfigs]);
  const criteriaLoaded = criteriaLoadedForTrack === track.id;

  const trackPool = useMemo(() => allPools.find((p: { trackId?: string }) => p.trackId === track.id), [allPools, track.id]);
  const poolItemIds = useMemo(() => trackPool?.priorityIds ?? [], [trackPool]);
  const poolItems = useMemo(() => allItems.filter(i => poolItemIds.includes(i.id)), [allItems, poolItemIds]);
  const criteriaConfigs = useMemo(
    () => (allCycleConfigs ?? []).filter(c => c.trackId === track.id),
    [allCycleConfigs, track.id],
  );

  // Filter proposals for this track (by trackId when set, else cycleId)
  const trackProposals = useMemo(() =>
    proposals.filter(p => (p as { trackId?: string }).trackId === track.id || (p.cycleId === track.cycleId && !(p as { trackId?: string }).trackId)),
    [proposals, track.id, track.cycleId]
  );

  /** All accepted values for (item, attribute) so we show every scale's value (no overwriting). */
  const getAcceptedRecords = (itemId: string, attributeKey: string) =>
    getAcceptedValuesForAttribute(itemId, attributeKey);

  const acceptedValueSummary = useMemo(() => {
    let totalAcceptedValues = 0;
    let itemsWithAcceptedValues = 0;

    for (const item of poolItems) {
      let itemHasAcceptedValue = false;

      for (const config of criteriaConfigs) {
        const effectiveScaleId = getEffectiveScaleId(config.attributeKey);
        const acceptedRecords = getAcceptedRecords(item.id, config.attributeKey);
        const acceptedRecord = effectiveScaleId != null
          ? acceptedRecords.find(av => (av.scaleId ?? null) === effectiveScaleId)
          : acceptedRecords.find(av => (av.scaleId ?? null) === null) ?? acceptedRecords[0];

        if (acceptedRecord) {
          totalAcceptedValues += 1;
          itemHasAcceptedValue = true;
        }
      }

      if (itemHasAcceptedValue) {
        itemsWithAcceptedValues += 1;
      }
    }

    return { totalAcceptedValues, itemsWithAcceptedValues };
  }, [criteriaConfigs, getAcceptedRecords, getEffectiveScaleId, poolItems]);

  const handleSaveValue = async (itemId: string, attributeKey: string, value: string | number) => {
    const valueStr = typeof value === 'number' ? String(value) : value.trim();
    if (valueStr === '') return;
    setSaveError(null);
    setSavingCell({ itemId, attr: attributeKey });
    const scaleId = getEffectiveScaleId(attributeKey);
    try {
      const result = await acceptValueDirectly({
        itemId,
        attributeKey,
        value: valueStr,
        acceptedBy: currentUserId,
        acceptanceMethod: 'facilitator_override',
        scaleId: scaleId ?? undefined,
      });
      if (result?.saved === false && result?.error) {
        setSaveError(result.error);
        console.warn('[TrackValuesTab] acceptValueDirectly:', result.error);
      } else {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
        await fetchFromSupabase();
      }
      setLocalValues(prev => {
        const next = { ...prev };
        if (!next[itemId]) next[itemId] = {};
        next[itemId][attributeKey] = valueStr;
        return next;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setSaveError(msg);
      console.error('[TrackValuesTab] Save value failed:', e);
    } finally {
      setSavingCell(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* S5: Value Source — selector before lock, permanent provenance after lock */}
      {currentWorkspaceId && (
        <div style={{ ...wsCard, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>S5 · Value Source</div>

          {/* When locked: permanent provenance indicator */}
          {valuesConverged ? (
            <ValueSourceLockedProvenance track={track} workspaceId={currentWorkspaceId} />
          ) : (
            <>
              {/* Selector */}
              <ValueSourceSelector
                track={track}
                workspaceId={currentWorkspaceId}
                actorId={currentUserId}
                onSelected={refreshTrack}
              />

              {/* Lock Track Values button */}
              <div style={{ paddingTop: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  type="button"
                  disabled={!s1to4Met || valuesLockBusy}
                  onClick={() => s1to4Met && setShowLockConfirm(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
                    fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                    borderRadius: '2px', border: 'none',
                    color: s1to4Met && !valuesLockBusy ? '#fff' : 'var(--text-dim)',
                    background: s1to4Met && !valuesLockBusy ? 'var(--mustard)' : 'var(--bg-raised)',
                    cursor: s1to4Met && !valuesLockBusy ? 'pointer' : 'not-allowed',
                    width: 'fit-content',
                  }}
                >
                  {valuesLockBusy ? (
                    <Loader2 className="animate-spin" size={13} />
                  ) : (
                    <Lock size={13} />
                  )}
                  Lock Track Values
                </button>

                {/* Named missing inputs (§5 — correction-E) */}
                {!s1to4Met && (
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem', paddingLeft: '4px' }}>
                    <li style={{ color: readinessFlags.participationLocked ? 'var(--green)' : 'var(--coral)' }}>
                      {readinessFlags.participationLocked ? '✓' : '✗'} Participation
                      {!readinessFlags.participationLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            const el = document.querySelector('[data-track-tab="participation"]') as HTMLButtonElement | null;
                            el?.click();
                          }}
                          style={{ marginLeft: '4px', textDecoration: 'underline', color: 'var(--text-ter)', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          → Configure participants
                        </button>
                      )}
                    </li>
                    <li style={{ color: readinessFlags.poolLocked ? 'var(--green)' : 'var(--coral)' }}>
                      {readinessFlags.poolLocked ? '✓' : '✗'} Item pool locked
                      {!readinessFlags.poolLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            const el = document.querySelector('[data-track-tab="pool"]') as HTMLButtonElement | null;
                            el?.click();
                          }}
                          style={{ marginLeft: '4px', textDecoration: 'underline', color: 'var(--text-ter)', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          → Manage item pool
                        </button>
                      )}
                    </li>
                    <li style={{ color: readinessFlags.criteriaLocked ? 'var(--green)' : 'var(--coral)' }}>
                      {readinessFlags.criteriaLocked ? '✓' : '✗'} Criteria locked
                      {!readinessFlags.criteriaLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            const el = document.querySelector('[data-track-tab="criteria"]') as HTMLButtonElement | null;
                            el?.click();
                          }}
                          style={{ marginLeft: '4px', textDecoration: 'underline', color: 'var(--text-ter)', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          → Select criteria
                        </button>
                      )}
                    </li>
                    <li style={{ color: readinessFlags.rangeSetLocked ? 'var(--green)' : 'var(--coral)' }}>
                      {readinessFlags.rangeSetLocked ? '✓' : '✗'} Scales accepted
                      {!readinessFlags.rangeSetLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            const el = document.querySelector('[data-track-tab="value-ranges"]') as HTMLButtonElement | null;
                            el?.click();
                          }}
                          style={{ marginLeft: '4px', textDecoration: 'underline', color: 'var(--text-ter)', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          → Accept scales
                        </button>
                      )}
                    </li>
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Confirmation dialog */}
      {showLockConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowLockConfirm(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', maxWidth: '420px', width: '100%', margin: '0 16px', padding: '24px' }}>
            <div style={{ fontFamily: 'var(--sans)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-pri)', marginBottom: '8px' }}>Lock Track Values?</div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-sec)', marginBottom: '20px' }}>
              Locking the Track freezes the value set. Sessions in this track will use these
              values and they cannot be changed without a governance override. Confirm?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowLockConfirm(false)}
                style={{ padding: '7px 14px', fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', background: 'none', border: '1px solid var(--rule)', borderRadius: '2px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLockTrackValues}
                style={wsGreenBtn(false)}
              >
                Lock Track Values
              </button>
            </div>
          </div>
        </div>
      )}

      {/* StepLockControl for the full values status */}
      <StepLockControl
        title="Values"
        description={`${acceptedValueSummary.totalAcceptedValues} value${acceptedValueSummary.totalAcceptedValues !== 1 ? 's' : ''} submitted across ${acceptedValueSummary.itemsWithAcceptedValues} item${acceptedValueSummary.itemsWithAcceptedValues !== 1 ? 's' : ''}`}
        status={deriveStepStatus(valuesConverged, acceptedValueSummary.totalAcceptedValues > 0)}
        conditionsMet={acceptedValueSummary.totalAcceptedValues > 0}
        onLock={handleValuesLockToggle}
        onUnlock={handleValuesLockToggle}
        busy={valuesLockBusy}
        readOnlyNote={lockGate.allowed ? undefined : lockGate.reason}
      />

      <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-pri)' }}>Value Grid</div>

      {/* Enter values: grid when pool and criteria exist. Uses the configured value range when defined. */}
      {poolItems.length > 0 && criteriaConfigs.length > 0 && (
        <div style={wsCard}>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)', marginBottom: '6px' }}>Enter values</div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)', marginBottom: '14px' }}>
            Add a value for each item and criterion (from the scale defined in Criteria & scales). Values are saved when you blur or choose an option.
          </p>
          {/* Fixed-height status area to prevent layout jump when save success/error appears or disappears */}
          <div style={{ marginBottom: '12px', minHeight: '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {saveError && (
              <div style={{ padding: '8px 12px', borderRadius: '2px', background: 'rgba(192,83,58,0.08)', border: '1px solid rgba(192,83,58,0.25)', color: 'var(--coral)', fontSize: '0.8rem' }}>
                Could not save: {saveError}
              </div>
            )}
            {saveSuccess && !saveError && (
              <div className="dark:!text-[var(--text-ter)]" style={{ padding: '8px 12px', borderRadius: '2px', background: 'rgba(58,125,86,0.08)', border: '1px solid rgba(58,125,86,0.25)', color: 'var(--green)', fontSize: '0.8rem' }}>
                Values saved.
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={wsTh}>Item</th>
                  {criteriaConfigs.map(c => (
                    <th key={c.id} style={wsTh}>{c.attributeKey}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {poolItems.map(item => (
                  <tr key={item.id}>
                    <td style={{ ...wsTd, fontWeight: 500 }}>{item.title || item.id.slice(0, 8)}</td>
                    {criteriaConfigs.map(c => {
                      const effectiveScaleId = getEffectiveScaleId(c.attributeKey);
                      const acceptedRecords = getAcceptedRecords(item.id, c.attributeKey);
                      const acceptedRecord = effectiveScaleId != null
                        ? acceptedRecords.find(av => (av.scaleId ?? null) === effectiveScaleId)
                        : acceptedRecords.find(av => (av.scaleId ?? null) === null) ?? acceptedRecords[0];
                      const accepted = acceptedRecord?.value;
                      const display = localValues[item.id]?.[c.attributeKey] ?? (accepted != null ? String(accepted) : '');
                      const isSaving = savingCell?.itemId === item.id && savingCell?.attr === c.attributeKey;
                      const options = getOptionsFromRange(c.rangeValue);
                      const range = c.rangeValue;
                      const hasMinMax = range && (range.type === 'number' || range.type === 'scale') && range.min != null && range.max != null;
                      const attr = attributes.find((a: { key: string }) => a.key === c.attributeKey) as { scales?: Array<{ id: string; label?: string }> } | undefined;
                      const otherScaleValues = acceptedRecords.filter(av => (av.scaleId ?? null) !== (effectiveScaleId ?? null));

                      return (
                        <td key={c.id} style={{ ...wsTd, verticalAlign: 'top' }}>
                          {/* The input keeps a fixed slot, so "Saving…" never resizes the row. The "Also:" and
                              provenance lines take space only when they have something to say: reserving
                              room for both in every row made each row ~118px tall, most of it empty
                              (owner, 2026-10-07). */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ display: 'flex', minHeight: '1.75rem', alignItems: 'center' }}>
                              {isSaving ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-sec)' }}>
                                  <Loader2 className="animate-spin" size={13} aria-hidden />
                                  Saving…
                                </span>
                              ) : options != null && options.length > 0 ? (
                                <Select
                                  style={{ width: '100%', maxWidth: '140px', padding: '4px 8px', border: '1px solid var(--rule)', borderRadius: '2px', color: 'var(--text-pri)', background: 'var(--bg)' }}
                                  value={display}
                                  onChange={e => {
                                    const v = e.target.value;
                                    setLocalValues(prev => ({ ...prev, [item.id]: { ...(prev[item.id] || {}), [c.attributeKey]: v } }));
                                    const num = Number(v);
                                    handleSaveValue(item.id, c.attributeKey, Number.isNaN(num) ? v : num);
                                  }}
                                >
                                  <option value="">—</option>
                                  {options.map(opt => (
                                    <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
                                  ))}
                                </Select>
                              ) : hasMinMax ? (
                                <input
                                  type="number"
                                  min={range!.min}
                                  max={range!.max}
                                  step={range!.step ?? 1}
                                  style={{ width: '100%', maxWidth: '100px', padding: '4px 8px', border: '1px solid var(--rule)', borderRadius: '2px', color: 'var(--text-pri)', background: 'var(--bg)' }}
                                  placeholder={`${range!.min}–${range!.max}`}
                                  value={display}
                                  onChange={e => setLocalValues(prev => ({ ...prev, [item.id]: { ...(prev[item.id] || {}), [c.attributeKey]: e.target.value } }))}
                                  onBlur={e => {
                                    const v = e.target.value.trim();
                                    if (v) {
                                      const n = Number(v);
                                      handleSaveValue(item.id, c.attributeKey, Number.isNaN(n) ? v : n);
                                    }
                                  }}
                                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  style={{ width: '100%', maxWidth: '120px', padding: '4px 8px', border: '1px solid var(--rule)', borderRadius: '2px', color: 'var(--text-pri)', background: 'var(--bg)' }}
                                  placeholder="—"
                                  value={display}
                                  onChange={e => setLocalValues(prev => ({ ...prev, [item.id]: { ...(prev[item.id] || {}), [c.attributeKey]: e.target.value } }))}
                                  onBlur={e => { const v = e.target.value.trim(); if (v) handleSaveValue(item.id, c.attributeKey, v); }}
                                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                />
                              )}
                            </div>
                            {otherScaleValues.length > 0 && (
                              <div style={{ fontSize: '0.7rem', lineHeight: '1.2', color: 'var(--text-dim)' }}>
                                Also:{' '}
                                {otherScaleValues.map(av => {
                                  const scaleLabel = attr?.scales?.find(s => s.id === av.scaleId)?.label ?? av.scaleId ?? '';
                                  return `${String(av.value)}${scaleLabel ? ` (${scaleLabel})` : ''}`;
                                }).join('; ')}
                              </div>
                            )}
                            {acceptedRecord && (
                              <div style={{ fontSize: '0.7rem', lineHeight: '1.3', color: 'var(--text-dim)' }}>
                                {(() => {
                                const sourceProposal = getSourceProposalForAcceptedValue(acceptedRecord);
                                return (
                                  <ValueProvenanceLine
                                    acceptedBy={acceptedRecord.acceptedBy}
                                    acceptedAt={acceptedRecord.acceptedAt}
                                    acceptanceMethod={acceptedRecord.acceptanceMethod}
                                    acceptanceNote={acceptedRecord.acceptanceNote}
                                    proposedBy={sourceProposal?.proposedBy}
                                    proposedAt={sourceProposal?.proposedAt}
                                    confidence={sourceProposal?.confidence}
                                    sourceLabel={sourceProposal?.evidence}
                                    proposalNote={sourceProposal?.rationale}
                                    compact
                                  />
                                );
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {poolItems.length === 0 && (
        <p style={{ fontSize: '0.8rem', color: 'var(--mustard)', background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)', borderRadius: '2px', padding: '10px 12px' }}>
          Add items to the track pool (Item Pool tab) before entering values.
        </p>
      )}
      {poolItems.length > 0 && criteriaConfigs.length === 0 && criteriaLoaded && (
        <p style={{ fontSize: '0.8rem', color: 'var(--mustard)', background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)', borderRadius: '2px', padding: '10px 12px' }}>
          Configure criteria for this track (Criteria tab) before entering values.
        </p>
      )}

      {trackProposals.length === 0 && poolItems.length === 0 ? (
        <div style={{ ...wsCard, textAlign: 'center', padding: '32px' }}>
          <BarChart3 size={40} style={{ color: 'var(--text-dim)', margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-pri)', marginBottom: '4px' }}>No values submitted yet</div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)' }}>
            Add items to the pool and configure criteria above, then use the grid to enter values. You can also create a session with purpose &quot;Converge values&quot; to collect values in a session.
          </p>
        </div>
      ) : trackProposals.length > 0 ? (
        <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={wsTh}>Item</th>
                <th style={wsTh}>Attribute</th>
                <th style={wsTh}>Value</th>
                <th style={wsTh}>Submitted By</th>
                <th style={wsTh}>Status</th>
              </tr>
            </thead>
            <tbody>
              {trackProposals.slice(0, 30).map(proposal => (
                <tr key={proposal.id}>
                  <td style={wsTd}>{resolveItemName(proposal.itemId)}</td>
                  <td style={{ ...wsTd, color: 'var(--text-sec)' }}>{proposal.attributeKey}</td>
                  <td style={{ ...wsTd, fontWeight: 500 }}>
                    {typeof proposal.proposedValue === 'object' ? JSON.stringify(proposal.proposedValue) : String(proposal.proposedValue ?? '')}
                  </td>
                  <td style={{ ...wsTd, color: 'var(--text-sec)' }}>{resolveUserName(proposal.proposedBy)}</td>
                  <td style={wsTd}>
                    <span style={{
                      borderRadius: '2px', padding: '2px 8px', fontSize: '0.7rem',
                      background: proposal.status === 'accepted' ? 'rgba(58,125,86,0.12)' : proposal.status === 'rejected' ? 'rgba(192,83,58,0.12)' : 'rgba(212,160,23,0.12)',
                      color: proposal.status === 'accepted' ? 'var(--green)' : proposal.status === 'rejected' ? 'var(--coral)' : 'var(--mustard)',
                    }}>
                      {proposal.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {trackProposals.length > 30 && (
            <div style={{ padding: '10px 14px', background: 'var(--hover-tint)', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
              Showing 30 of {trackProposals.length} values
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ─── Track tournaments subsection (inline component) ─────────────────────────

function TrackTournamentsSubsection({ trackId }: { trackId: string }) {
  const navigate              = useNavigate();
  const newTournamentPath     = `/prioritization/tournaments/new?trackId=${encodeURIComponent(trackId)}`;
  const loadTournaments       = useTournamentStore(s => s.loadTournaments);
  const getTournamentsForTrack = useTournamentStore(s => s.getTournamentsForTrack);
  const getRoundsForTournament = useTournamentStore(s => s.getRoundsForTournament);

  useEffect(() => {
    void loadTournaments();
  }, [loadTournaments]);

  const tournaments = getTournamentsForTrack(trackId);

  const stateColor: Record<string, { color: string; bg: string }> = {
    DRAFT: { color: 'var(--text-sec)', bg: 'var(--hover-tint)' },
    READY: { color: 'var(--text-ter)', bg: 'rgba(61,75,111,0.1)' },
    IN_PROGRESS: { color: 'var(--mustard)', bg: 'rgba(212,160,23,0.12)' },
    AWAITING_ADVANCE: { color: 'var(--coral)', bg: 'rgba(192,83,58,0.1)' },
    COMPLETE: { color: 'var(--green)', bg: 'rgba(58,125,86,0.12)' },
    CANCELLED: { color: 'var(--coral)', bg: 'rgba(192,83,58,0.06)' },
  };
  const stateLabel: Record<string, string> = {
    DRAFT: 'Draft', READY: 'Ready', IN_PROGRESS: 'In Progress',
    AWAITING_ADVANCE: 'Needs Review', COMPLETE: 'Complete', CANCELLED: 'Cancelled',
  };

  return (
    <div style={{ ...wsCard, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--rule)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Trophy size={14} style={{ color: 'var(--mustard)' }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>
            Tournaments
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
            multi-round alternative to a single session
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate(newTournamentPath)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 500, color: 'var(--mustard)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <Plus size={13} />
          New Tournament
        </button>
      </div>

      {tournaments.length === 0 ? (
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', flex: 1 }}>
            No tournaments yet. Use a tournament when a single session isn't enough — run items through multiple rounds to reach a final ranking.
          </p>
          <button
            type="button"
            onClick={() => navigate(newTournamentPath)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 500, color: 'var(--mustard)', background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)', padding: '6px 12px', borderRadius: '2px', cursor: 'pointer', flexShrink: 0 }}
          >
            <Trophy size={15} />
            Start tournament
          </button>
        </div>
      ) : (
        <div>
          {tournaments.map((t, i) => {
            const rounds = getRoundsForTournament(t.id);
            const active = rounds.find(r => r.state === 'OPEN' || r.state === 'ADVANCING' || r.state === 'SESSIONS_COMPLETE');
            const completed = rounds.filter(r => r.state === 'COMPLETE').length;
            const sc = stateColor[t.state] ?? { color: 'var(--text-sec)', bg: 'var(--hover-tint)' };
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => navigate(`/prioritization/tournaments/${t.id}`)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 16px', background: 'none', border: 'none', borderTop: i === 0 ? 'none' : '1px solid var(--rule)', cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  <Trophy size={14} style={{ color: 'var(--mustard)', flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                      {rounds.length} round{rounds.length !== 1 ? 's' : ''}
                      {rounds.length > 0 && ` · ${completed} complete`}
                      {active && ` · Round ${active.roundNumber} active`}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <span className={t.state === 'COMPLETE' ? 'dark:!text-[var(--text-ter)]' : undefined} style={{ fontSize: '0.7rem', fontWeight: 500, padding: '2px 8px', borderRadius: '2px', color: sc.color, background: sc.bg }}>
                    {stateLabel[t.state] ?? t.state}
                  </span>
                  <ChevronRight size={14} style={{ color: 'var(--text-dim)' }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Inline session configuration (reuses SessionDetail's own tab bodies) ─────
//
// Configuring a session used to mean leaving the track entirely for the
// standalone SessionDetail page. That broke the "conditions-met, not a
// wizard" model this whole page follows for every other tab (Participation,
// Item pool, Criteria & scales): expand in place, configure in place. Reuses
// SessionDetail's own SetupTab/ParticipantsTab/SessionItemsTab/CriteriaTab
// (exported for this purpose) so behavior stays identical — only the
// container changed. Live execution (Run) still opens SessionDetail's own
// run surface; that's a genuinely different, real-time UI, not configuration.

type InlineSessionSubTab = 'setup' | 'participants' | 'items' | 'criteria';

const INLINE_SESSION_SUB_TABS: { key: InlineSessionSubTab; label: string }[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'participants', label: 'Participants' },
  { key: 'items', label: 'Items' },
  { key: 'criteria', label: 'Criteria' },
];

function InlineSessionConfig({ session }: { session: PrioritizationSession }) {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState<InlineSessionSubTab>('setup');
  const { effectiveUserIds } = useEffectiveSessionParticipants(session);

  return (
    <div style={{ borderTop: '1px solid var(--rule)', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 16px' }}>
        {INLINE_SESSION_SUB_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            style={{
              padding: '10px 12px',
              fontFamily: 'var(--mono)',
              fontSize: '0.58rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: subTab === t.key ? 'var(--text-pri)' : 'var(--text-sec)',
              background: 'none',
              border: 'none',
              borderBottom: subTab === t.key ? '2px solid var(--mustard)' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate(`/prioritization/sessions/${session.id}?tab=run`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            marginBottom: '6px',
            fontFamily: 'var(--mono)',
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#fff',
            background: 'var(--green)',
            border: 'none',
            borderRadius: '2px',
            cursor: 'pointer',
          }}
        >
          <Play size={11} />
          Run session
        </button>
      </div>
      <div style={{ borderTop: '1px solid var(--rule)', background: 'var(--bg-raised)', padding: '16px' }}>
        {subTab === 'setup' && <SetupTab session={session} />}
        {subTab === 'participants' && <ParticipantsTab session={session} />}
        {subTab === 'items' && <SessionItemsTab session={session} />}
        {subTab === 'criteria' && <CriteriaTab session={session} effectiveParticipantCount={effectiveUserIds.length} />}
      </div>
    </div>
  );
}

// ─── Track sessions tab ───────────────────────────────────────────────────────

export function TrackSessionsTab({ track, cycle, sessions, conditionsSummary }: { track: Track; cycle: { metadata?: Record<string, unknown> | null } | null | undefined; sessions: PrioritizationSession[]; conditionsSummary: TrackConditionsSummary | null }) {
  const tournamentsEnabled = useModuleGate('tournaments');
  const catchballLoopCtx = useCatchballLoopContext();
  const can = useUserStore(state => state.can);
  const permissionsLoaded = useUserStore(state => state.permissionsLoaded);

  // Ensure participant stores are populated when navigating directly to Sessions tab
  const fetchParticipantsForCycleInSession = useCycleParticipantsStore(state => state.fetchParticipantsForCycle);
  const fetchTrackParticipantsInSession = useTrackParticipantsStore(state => state.fetchParticipantsForTrack);
  useEffect(() => {
    if (track.cycleId) fetchParticipantsForCycleInSession(track.cycleId);
    fetchTrackParticipantsInSession(track.id);
  }, [track.cycleId, track.id, fetchParticipantsForCycleInSession, fetchTrackParticipantsInSession]);
  const navigate = useNavigate();
  const getCurrentWorkspace = useTenantStore(state => state.getCurrentWorkspace);
  // Subscribe to the DATA, not to getTrackConfigs, and read the track's criteria here too:
  // the getter is a stable function, so a memo on it never recomputed, and this tab never
  // asked for the criteria itself. Opened first, "New sessions in this track will inherit"
  // left out the Criteria chip. (Same fault as the Values tab, #895.)
  const allCycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);
  const fetchTrackConfigsForSessions = useAttributeGovernanceStore(state => state.fetchTrackConfigs);
  useEffect(() => {
    void fetchTrackConfigsForSessions(track.id);
  }, [track.id, fetchTrackConfigsForSessions]);
  const allPools = useItemPoolsStore(state => state.itemPools);

  // Inherit from track: criteria (accepted for this track) and pool item IDs
  const inheritedFromTrack = useMemo(() => {
    const trackRows = (allCycleConfigs ?? []).filter((c) => c.trackId === track.id);
    const criteriaIds = trackRows
      .filter((c) => c.rangeStatus === 'accepted')
      .map((c) => c.attributeKey);
    // The track's weights (1047), when it gives one to every criterion: the same rule
    // a session made in the Event Designer starts from (criteriaFromTrackAttributeKeys).
    const weighted = trackRows.length > 0 && trackRows.every((c) => c.weight != null);
    const criteriaWeights = weighted && criteriaIds.length > 0
      ? Object.fromEntries(
          criteriaFromTrackAttributeKeys(criteriaIds, undefined, (key) => trackRows.find((c) => c.attributeKey === key)?.weight)
            .map((c) => [c.id, c.weight]),
        )
      : undefined;
    const pools = allPools ?? [];
    const trackPool = pools.find((p: { trackId?: string }) => p.trackId === track.id);
    const itemIds = trackPool?.priorityIds ?? [];
    return { initialCriteriaIds: criteriaIds, initialCriteriaWeights: criteriaWeights, initialItemIds: itemIds };
  }, [track.id, allCycleConfigs, allPools]);

  const [createError, setCreateError] = useState<string | null>(null);
  const [showRunLive, setShowRunLive] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [showCandidateReview, setShowCandidateReview] = useState(true);
  const [selectedRelationship, setSelectedRelationship] = useState<
    | { kind: 'edge'; id: string }
    | { kind: 'group'; id: string }
    | null
  >(null);

  // Relationship data for inline candidate review
  const allItems = useItemsStore((s) => s.items) || [];
  const relEdges = useItemRelationshipsStore((s) => s.edges) || [];
  const relGroups = useItemRelationshipsStore((s) => s.groups) || [];
  const relMemberships = useItemRelationshipsStore((s) => s.groupMemberships) || [];
  const updateEdge = useItemRelationshipsStore((s) => s.updateEdge);
  const updateGroup = useItemRelationshipsStore((s) => s.updateGroup);
  const dissolveEdge = useItemRelationshipsStore((s) => s.dissolveEdge);
  const dissolveGroup = useItemRelationshipsStore((s) => s.dissolveGroup);

  const trackPoolItemIds = useMemo(() => {
    const pool = (allPools ?? []).find((p: { trackId?: string }) => p.trackId === track.id);
    return new Set<string>(pool?.priorityIds ?? []);
  }, [allPools, track.id]);

  const itemsById = useMemo(() => new Map(allItems.map((i) => [i.id, i.title ?? i.id])), [allItems]);
  const itemsFullById = useMemo(() => new Map(allItems.map((i) => [i.id, i])), [allItems]);

  const candidateEdges = useMemo(
    () => relEdges.filter(
      (e) => e.status === 'candidate' &&
        (trackPoolItemIds.has(e.sourceItemId) || trackPoolItemIds.has(e.targetItemId))
    ),
    [relEdges, trackPoolItemIds]
  );
  const candidateGroups = useMemo(
    () => relGroups.filter(
      (g) => g.status === 'candidate' &&
        relMemberships.some((m) => m.groupId === g.id && trackPoolItemIds.has(m.itemId))
    ),
    [relGroups, relMemberships, trackPoolItemIds]
  );
  const totalCandidates = candidateEdges.length + candidateGroups.length;

  const { effectiveUserIds, loading: participantsLoading } = useEffectiveTrackParticipants(track.cycleId ?? undefined, track.id);

  // Default-forward: resolve effective config (GLOBAL → CYCLE → TRACK → SESSION) for direct session creation
  const resolvedCreateDefaults = useMemo(() => {
    const lastSession = sessions[0];
    const sessionLayer = lastSession
      ? sessionLayerFromPriorSession({
          modeConfig: lastSession.modeConfig,
          facilitatorId: lastSession.facilitatorId,
        })
      : null;
    const workspaceLayer = workspaceLayerFromWorkspace(getCurrentWorkspace());
    const cycleLayer = cycleLayerFromCycle(cycle);
    const trackLayer = trackLayerFromTrack(track);
    const effective = resolveEffectiveConfig(
      workspaceLayer ?? DEFAULT_GLOBAL_SESSION_CONFIG,
      cycleLayer ?? undefined,
      trackLayer ?? undefined,
      sessionLayer
    );
    // During participants loading we trust the config-resolved facilitator even before
    // the effective participant set confirms them — session creation will validate at runtime.
    const inSet = (id: string) => participantsLoading || effectiveUserIds.includes(id);
    const facilitatorId =
      (lastSession?.facilitatorId && inSet(lastSession.facilitatorId) ? lastSession.facilitatorId : null) ??
      (effective.defaultFacilitatorId && inSet(effective.defaultFacilitatorId) ? effective.defaultFacilitatorId : null) ??
      (effectiveUserIds.length > 0 ? effectiveUserIds[0] : null);

    return {
      effective,
      facilitatorId,
      toolType:
        (lastSession?.selectedToolId && typeof lastSession.selectedToolId === 'string'
          ? normalizeSessionToolId(lastSession.selectedToolId)
          : undefined) ?? 'pairwise_ranking',
    };
  }, [sessions, getCurrentWorkspace, cycle, track, effectiveUserIds, participantsLoading]);

  // Live session: facilitator must be selected and from effective participant set.
  // Suppress the blocker during async participant resolution to avoid false positives.
  const noEffectiveParticipants = !participantsLoading && effectiveUserIds.length === 0;
  const liveFacilitatorRequired = !participantsLoading && resolvedCreateDefaults.effective.timingMode === 'live' && !resolvedCreateDefaults.facilitatorId;
  const liveFacilitatorBlocker = noEffectiveParticipants
    ? 'Add participants to this track in Participation first.'
    : liveFacilitatorRequired
      ? (conditionsSummary?.liveFacilitatorBlockerMessage ?? 'Set a facilitator in Session Defaults on the Overview tab, or switch to Async mode.')
      : null;
  // Relationship review is a soft warning, not a hard blocker — ranking in the presence
  // of unreviewed relationships is the user's judgment call. We surface it as a visible
  // caution banner but never disable the button over it.
  const relationshipWarning = conditionsSummary?.relationshipBlockerMessage ?? null;
  const canFacilitateSessions = permissionsLoaded && can('session.facilitate');
  const createAuthBlocker = !permissionsLoaded
    ? 'Checking your permissions...'
    : !canFacilitateSessions
      ? 'You do not have permission to create or facilitate sessions.'
      : null;
  // Hard blockers: auth + live facilitator only. Relationship review is a warning.
  const createBlockedReason = createAuthBlocker ?? liveFacilitatorBlocker ?? null;
  const createBlocked = Boolean(createBlockedReason);

  // Clear stale errors when the button is no longer blocked (e.g. after conditions resolve)
  useEffect(() => {
    if (!createBlocked) setCreateError(null);
  }, [createBlocked]);

  useEffect(() => {
    const onBlocked = (event: Event) => {
      const detail = (event as CustomEvent).detail as { action?: string; message?: string } | undefined;
      if (detail?.action === 'createSession') {
        setCreateError(detail.message ?? 'Could not create session.');
      }
    };

    window.addEventListener('authorization-denied', onBlocked);
    return () => window.removeEventListener('authorization-denied', onBlocked);
  }, []);

  const handleCreateSession = () => {
    if (createBlocked) {
      setCreateError(createBlockedReason ?? 'Cannot create session right now.');
      return;
    }
    setCreateError(null);
    // Event Designer is the default setup surface (F6). Creating a blank
    // classic session and dumping into Session Detail is what produced
    // "session not found" on the participant brief, then a link to the old
    // setup console. Bind cycle/track here; inheritance happens on createEvent.
    navigate(
      eventDesignerHref({
        trackId: track.id,
        cycleId: track.cycleId,
        loopId: catchballLoopCtx?.loopId,
      }),
    );
  };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.55rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>
            Sessions
          </div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''} for this track
          </div>
          {/* The result published for the track is on the Results tab (mig 934). */}
          <Link to={`/prioritization/tracks/${track.id}/result`} style={{ fontSize: '0.8rem', color: 'var(--text-ter)', textDecoration: 'underline' }}>
            See the published result
          </Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          <button
            onClick={handleCreateSession}
            disabled={createBlocked}
            title={createBlockedReason ?? undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
              fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: '#fff', background: 'var(--green)', border: 'none', borderRadius: '2px',
              cursor: createBlocked ? 'not-allowed' : 'pointer',
              opacity: createBlocked ? 0.5 : 1,
            }}
          >
            <Plus size={13} />
            New Session
          </button>
          {canFacilitateSessions && (
            <button
              type="button"
              onClick={() => setShowRunLive((v) => !v)}
              data-testid="run-it-live-button"
              aria-expanded={showRunLive}
              title="Open a room on this track's items now; phones join from the Board"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
                fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--bg)', background: 'var(--text-pri)', border: 'none', borderRadius: '2px',
                cursor: 'pointer',
              }}
            >
              <Play size={13} />
              Run it live
            </button>
          )}
          {tournamentsEnabled && (
          <button
            type="button"
            onClick={() => navigate(`/prioritization/tournaments/new?trackId=${track.id}`)}
            title="Run a multi-round tournament instead of a single session"
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
              fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--mustard)', background: 'var(--bg-raised)', border: '1px solid var(--rule)',
              borderRadius: '2px', cursor: 'pointer',
            }}
          >
            <Trophy size={13} />
            Tournament
          </button>
          )}
        </div>
      </div>

      {showRunLive && (
        <RunItLiveCard
          track={{ id: track.id, name: track.name, cycleId: track.cycleId ?? null }}
          participantIds={effectiveUserIds}
          itemIds={inheritedFromTrack.initialItemIds}
          criteriaIds={inheritedFromTrack.initialCriteriaIds}
          criteriaWeights={inheritedFromTrack.initialCriteriaWeights}
          onClose={() => setShowRunLive(false)}
        />
      )}

      {/* Inheritance preview — what a new session will receive from this track */}
      {(() => {
        const { effective } = resolvedCreateDefaults;
        const allowSessionOverride: boolean = track.metadata?.allowSessionOverride !== false;
        const hasCriteria = inheritedFromTrack.initialCriteriaIds.length > 0;
        const hasItems = inheritedFromTrack.initialItemIds.length > 0;
        const hasAnyInheritance = effective.timingMode || effective.entryMode || hasCriteria || hasItems || !allowSessionOverride;
        if (!hasAnyInheritance) return null;
        const chip = (label: string, value: string, fromCycle: boolean) => (
          <span
            key={label}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', borderRadius: '2px',
              background: 'var(--hover-tint)', padding: '4px 10px', fontSize: '0.7rem',
              fontFamily: 'var(--sans)', color: 'var(--text-sec)',
            }}
          >
            <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>{label}</span>
            <span style={{ fontWeight: 600 }}>{value}</span>
            {fromCycle && <span style={{ color: 'var(--text-ter)', fontSize: '0.6rem', fontStyle: 'italic' }}>from cycle</span>}
          </span>
        );
        return (
          <div style={{ borderRadius: '2px', border: '1px solid var(--rule)', background: 'var(--bg-raised)', padding: '12px 14px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.55rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '10px' }}>
              New sessions in this track will inherit
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {effective.timingMode && chip('Timing', effective.timingMode === 'live' ? 'Live' : 'Async', !!(cycle && track.defaultTimingMode !== effective.timingMode))}
              {effective.entryMode && chip('Entry', effective.entryMode === 'facilitator' ? 'Facilitator' : 'Participants', !!(cycle && track.defaultEntryMode !== effective.entryMode))}
              {hasCriteria && chip('Criteria', `${inheritedFromTrack.initialCriteriaIds.length} locked from track`, false)}
              {hasItems && chip('Items', `${inheritedFromTrack.initialItemIds.length} from track pool`, false)}
              {!allowSessionOverride && (
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', borderRadius: '2px',
                    background: 'rgba(212,160,23,0.12)', padding: '4px 10px', fontSize: '0.7rem',
                    fontFamily: 'var(--sans)', color: 'var(--mustard)', fontWeight: 600,
                  }}
                >
                  <Lock size={11} />
                  Sessions cannot override track settings
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Hard block reason — shown inline so user knows what to fix */}
      {createBlocked && createBlockedReason && (
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 14px',
            background: 'rgba(192,83,58,0.06)', border: '1px solid rgba(192,83,58,0.25)',
            borderLeft: '3px solid var(--coral)', borderRadius: '2px',
          }}
        >
          <AlertCircle size={14} style={{ color: 'var(--coral)', marginTop: '2px', flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>{createBlockedReason}</span>
        </div>
      )}

      {/* Soft relationship warning with inline candidate review */}
      {!createBlocked && relationshipWarning && (
        <div style={{ borderRadius: '2px', border: '1px solid rgba(212,160,23,0.25)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 14px', background: 'rgba(212,160,23,0.06)' }}>
            <AlertCircle size={14} style={{ color: 'var(--mustard)', marginTop: '2px', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
                {totalCandidates} unreviewed relationship{totalCandidates !== 1 ? 's' : ''} need review.
                You can still create a session — or review them here first.
              </p>
            </div>
            {totalCandidates > 0 && (
              <button
                type="button"
                onClick={() => setShowCandidateReview((v) => !v)}
                style={{
                  flexShrink: 0, fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--mustard)', background: 'none', border: 'none',
                  cursor: 'pointer', marginLeft: '8px',
                }}
              >
                {showCandidateReview ? 'Hide' : `Review ${totalCandidates}`}
              </button>
            )}
          </div>

          {showCandidateReview && totalCandidates > 0 && (
            <div className="border-t border-[var(--mustard)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
              {candidateEdges.map((edge) => {
                const srcTitle = itemsById.get(edge.sourceItemId) ?? edge.sourceItemId;
                const tgtTitle = itemsById.get(edge.targetItemId) ?? edge.targetItemId;
                return (
                  <div key={edge.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <button
                      type="button"
                      onClick={() => setSelectedRelationship({ kind: 'edge', id: edge.id })}
                      className="flex-1 min-w-0 text-left hover:text-primary-700 transition-colors group"
                    >
                      <span className="font-medium text-[var(--text-pri)] group-hover:text-primary-700 truncate">{srcTitle}</span>
                      <span className="mx-1.5 text-xs text-[var(--text-ter)] italic">{edge.type}{edge.subtype ? ` · ${edge.subtype}` : ''}</span>
                      <span className="font-medium text-[var(--text-pri)] group-hover:text-primary-700 truncate">{tgtTitle}</span>
                    </button>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => updateEdge(edge.id, { status: 'active' })}
                        className="px-2.5 py-1 text-xs font-semibold bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => dissolveEdge(edge.id)}
                        className="px-2.5 py-1 text-xs font-semibold bg-[var(--surface)] text-[var(--text-ter)] border border-[var(--rule)] hover:bg-[var(--hover-tint)] transition-colors"
                      >
                        Dissolve
                      </button>
                    </div>
                  </div>
                );
              })}
              {candidateGroups.map((group) => {
                const members = relMemberships
                  .filter((m) => m.groupId === group.id)
                  .map((m) => itemsById.get(m.itemId) ?? m.itemId);
                return (
                  <div key={group.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <button
                      type="button"
                      onClick={() => setSelectedRelationship({ kind: 'group', id: group.id })}
                      className="flex-1 min-w-0 text-left hover:text-primary-700 transition-colors group"
                    >
                      <span className="text-xs text-[var(--text-ter)] italic capitalize mr-1.5">{group.type}{group.subtype ? ` · ${group.subtype}` : ''}</span>
                      <span className="font-medium text-[var(--text-pri)] group-hover:text-primary-700">{group.title || members.join(', ')}</span>
                    </button>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => updateGroup(group.id, { status: 'active' })}
                        className="px-2.5 py-1 text-xs font-semibold bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => dissolveGroup(group.id)}
                        className="px-2.5 py-1 text-xs font-semibold bg-[var(--surface)] text-[var(--text-ter)] border border-[var(--rule)] hover:bg-[var(--hover-tint)] transition-colors"
                      >
                        Dissolve
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Relationship detail modal */}
      {selectedRelationship && (() => {
        if (selectedRelationship.kind === 'edge') {
          const edge = relEdges.find((e) => e.id === selectedRelationship.id);
          if (!edge) return null;
          const src = itemsFullById.get(edge.sourceItemId);
          const tgt = itemsFullById.get(edge.targetItemId);
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedRelationship(null)} />
              <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-lg overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] capitalize">
                      {edge.type}{edge.subtype ? ` · ${edge.subtype}` : ''}
                    </span>
                    <h3 className="text-base font-semibold text-[var(--text-pri)] mt-0.5">Relationship Detail</h3>
                  </div>
                  <button onClick={() => setSelectedRelationship(null)} className="p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-5 space-y-3">
                  {[src, tgt].map((item, i) => item && (
                    <div key={i} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
                      <p className="font-medium text-[var(--text-pri)] text-sm">{item.title ?? item.id}</p>
                      {item.description && (
                        <p className="text-xs text-[var(--text-ter)] mt-1 line-clamp-3">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]">
                  <button
                    type="button"
                    onClick={() => { updateEdge(edge.id, { status: 'active' }); setSelectedRelationship(null); }}
                    className="flex-1 px-4 py-2 text-sm font-semibold bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
                  >
                    Confirm relationship
                  </button>
                  <button
                    type="button"
                    onClick={() => { dissolveEdge(edge.id); setSelectedRelationship(null); }}
                    className="flex-1 px-4 py-2 text-sm font-semibold bg-[var(--bg-raised)] text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                  >
                    Dissolve
                  </button>
                </div>
              </div>
            </div>
          );
        }

        // Group
        const group = relGroups.find((g) => g.id === selectedRelationship.id);
        if (!group) return null;
        const memberItems = relMemberships
          .filter((m) => m.groupId === group.id)
          .map((m) => itemsFullById.get(m.itemId) ?? { id: m.itemId, title: m.itemId });
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedRelationship(null)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] capitalize">
                    {group.type}{group.subtype ? ` · ${group.subtype}` : ''}
                  </span>
                  <h3 className="text-base font-semibold text-[var(--text-pri)] mt-0.5">
                    {group.title || `${memberItems.length} items`}
                  </h3>
                </div>
                <button onClick={() => setSelectedRelationship(null)} className="p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-5 space-y-2 max-h-80 overflow-y-auto">
                {memberItems.map((item) => item && (
                  <div key={item.id} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
                    <p className="font-medium text-[var(--text-pri)] text-sm">{('title' in item ? item.title : null) ?? item.id}</p>
                    {'description' in item && item.description && (
                      <p className="text-xs text-[var(--text-ter)] mt-1 line-clamp-2">{item.description as string}</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-3 px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]">
                <button
                  type="button"
                  onClick={() => { updateGroup(group.id, { status: 'active' }); setSelectedRelationship(null); }}
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
                >
                  Confirm group
                </button>
                <button
                  type="button"
                  onClick={() => { dissolveGroup(group.id); setSelectedRelationship(null); }}
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-[var(--bg-raised)] text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                >
                  Dissolve
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {createError && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-3 text-sm text-[var(--coral)]">
          {createError}
        </div>
      )}
      
      {sessions.length === 0 ? (
        <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', padding: '32px', textAlign: 'center' }}>
          <Play size={40} style={{ color: 'var(--text-ter)', margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)', marginBottom: '4px' }}>No sessions yet</div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: 'var(--text-sec)', marginBottom: '16px' }}>
            Create a session to run prioritization within this track.
          </div>
          <button
            onClick={handleCreateSession}
            disabled={createBlocked}
            title={createBlockedReason ?? undefined}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', margin: '0 auto',
              fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: '#fff', background: 'var(--green)', border: 'none', borderRadius: '2px',
              cursor: createBlocked ? 'not-allowed' : 'pointer',
              opacity: createBlocked ? 0.5 : 1,
            }}
          >
            <Plus size={13} />
            Create First Session
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {sessions.map(session => {
            const isExpanded = expandedSessionId === session.id;
            const stateColor = session.state === 'PUBLISHED' || session.state === 'RUN_COMPLETE' ? 'var(--green)' : 'var(--text-ter)';
            return (
              <div
                key={session.id}
                style={{
                  background: 'var(--bg-raised)', border: `1px solid ${isExpanded ? 'var(--slate)' : 'var(--rule)'}`,
                  borderLeft: `3px solid ${isExpanded ? 'var(--slate)' : stateColor}`,
                  borderRadius: '2px', overflow: 'hidden', transition: 'border-color 0.15s',
                }}
              >
                <div
                  onClick={() => setExpandedSessionId(isExpanded ? null : session.id)}
                  style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <div>
                    <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)' }}>{session.name}</div>
                    <div style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '2px' }}>
                      {session.selectedToolId
                        ? (SESSION_TOOL_DESCRIPTOR_BY_ANY_ID[session.selectedToolId]?.name ?? session.selectedToolId)
                        : 'No tool selected'
                      } • {session.state || 'DRAFT'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        fontFamily: 'var(--mono)', fontSize: '0.58rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                        padding: '3px 8px', borderRadius: '2px', color: stateColor,
                        background: stateColor === 'var(--green)' ? 'rgba(58,125,86,0.12)' : 'var(--hover-tint)',
                      }}
                    >
                      {session.state || 'DRAFT'}
                    </span>
                    <ChevronRight
                      size={15}
                      style={{ color: 'var(--text-dim)', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'none' }}
                    />
                  </div>
                </div>
                {isExpanded && <InlineSessionConfig session={session} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Configuring a session (setup/participants/items/criteria) now happens inline
          above, expanded from its card — the same conditions-met, in-place pattern as
          every other track tab. Only Run (live execution) still opens SessionDetail. */}

    </div>
  );
}

function TrackResultsTab({ track }: { track: Track }) {
  const tournamentsEnabled = useModuleGate('tournaments');
  const [addingSessionId, setAddingSessionId] = useState<string | null>(null);
  const [addMessage, setAddMessage] = useState<{ type: 'success' | 'error'; text: string; poolId?: string } | null>(null);

  const cycleSessionsResult = useCycleSessions(track.cycleId ?? '');
  const trackSessionsResult = useTrackSessions(track.id);
  const rawSessions = track.cycleId ? cycleSessionsResult : trackSessionsResult;
  const sessions = (Array.isArray(rawSessions) ? rawSessions : []).filter((s) => s.trackId === track.id);
  const allItems = useItemsStore(state => state.items);
  const fetchSessions = useSessionStore(state => state.fetchFromSupabase);
  const setResultingItemPool = useSessionStore(state => state.setResultingItemPool);
  const userId = useUserStore(state => state.userId) ?? 'system';
  const createItemPoolForTrack = useItemPoolsStore(state => state.createItemPoolForTrack);
  const updateRanking = useItemPoolsStore(state => state.updateRanking);
  const updateItemPool = useItemPoolsStore(state => state.updateItemPool);
  const allPools = useItemPoolsStore(state => state.itemPools) ?? [];

  // Refetch sessions when viewing Results so completed runs are visible (e.g. after returning from Session Detail)
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Find completed ranking sessions
  const completedSessions = sessions.filter(s =>
    s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED'
  );
  
  // Published sessions come first
  const sortedSessions = [...completedSessions].sort((a, b) => {
    if (a.state === 'PUBLISHED' && b.state !== 'PUBLISHED') return -1;
    if (b.state === 'PUBLISHED' && a.state !== 'PUBLISHED') return 1;
    const aDate = new Date(a.updatedAt || a.createdAt).getTime();
    const bDate = new Date(b.updatedAt || b.createdAt).getTime();
    return bDate - aDate;
  });
  
  // Get item name by id
  const getItemName = (itemId: string) => {
    const item = allItems.find(i => i.id === itemId);
    return item?.title ?? itemId.substring(0, 8);
  };

  const handleAddAsPriorityList = async (session: {
    id: string;
    name: string;
    rankingOutput?: {
      rankedItemIds?: string[];
      orderedItems?: { id: string }[];
      classifications?: Record<string, Record<string, string>>;
    };
  }) => {
    const rankedIds: string[] = session.rankingOutput?.rankedItemIds ||
      (session.rankingOutput?.orderedItems?.map((i: { id: string }) => i.id)) || [];
    if (rankedIds.length === 0) {
      setAddMessage({ type: 'error', text: 'No ranking data to add.' });
      return;
    }
    setAddingSessionId(session.id);
    setAddMessage(null);
    try {
      const poolId = await createItemPoolForTrack(
        { cycleId: track.cycleId ?? '', trackId: track.id },
        `Results: ${session.name}`,
        { ownerId: userId }
      );
      updateRanking(poolId, rankedIds);
      const classificationTags = buildClassificationSummaryTags(session.rankingOutput?.classifications);
      if (classificationTags.length > 0) {
        updateItemPool(poolId, { tags: classificationTags });
      }
      // Mark the session so the button is hidden on re-render and duplicate pools can't be created
      setResultingItemPool(session.id, poolId);
      setAddMessage({
        type: 'success',
        poolId,
        text: `Created item pool "${session.name}" (${rankedIds.length} items) from this session result. Use it in Planning or publish it later.`,
      });
    } catch (e) {
      setAddMessage({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to create item pool from session result.',
      });
    } finally {
      setAddingSessionId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-pri)' }}>Results</div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)' }}>
          {completedSessions.length} completed session{completedSessions.length !== 1 ? 's' : ''}
          {completedSessions.filter(s => s.state === 'PUBLISHED').length > 0 &&
            ` (${completedSessions.filter(s => s.state === 'PUBLISHED').length} published)`
          }
        </p>
      </div>

      {/* The result published for the track — by the person who holds the ball (mig 934). */}
      <TrackResultPanel trackId={track.id} refreshKey={`${track.updatedAt}:${completedSessions.length}`} showPageLink />

      {addMessage && (
        <div style={{
          borderRadius: '2px', padding: '10px 12px', fontSize: '0.8rem',
          background: addMessage.type === 'success' ? 'rgba(58,125,86,0.08)' : 'rgba(192,83,58,0.08)',
          border: `1px solid ${addMessage.type === 'success' ? 'rgba(58,125,86,0.25)' : 'rgba(192,83,58,0.25)'}`,
          color: addMessage.type === 'success' ? 'var(--green)' : 'var(--coral)',
        }}>
          {addMessage.text}
          {addMessage.type === 'success' && (
            <span style={{ display: 'block', marginTop: '4px' }}>
              <Link to={addMessage.poolId ? `/prioritization/lists/${addMessage.poolId}` : '/prioritization/lists'} className="dark:!text-[var(--text-ter)]" style={{ fontWeight: 500, color: 'var(--green)' }}>Open the item pool</Link>
              {' · '}
              <Link to="/planning/plans" className="dark:!text-[var(--text-ter)]" style={{ fontWeight: 500, color: 'var(--green)' }}>Open Planning</Link>
            </span>
          )}
        </div>
      )}

      {sortedSessions.length === 0 ? (
        <div style={{ ...wsCard, textAlign: 'center', padding: '32px' }}>
          <Flag size={40} style={{ color: 'var(--text-dim)', margin: '0 auto 12px' }} />
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-pri)', marginBottom: '4px' }}>No results yet</div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)' }}>
            Run a ranking session to generate results for this track.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {sortedSessions.map(session => {
            // Get ranked item IDs - support both new (rankedItemIds) and legacy (orderedItems) formats
            const rankedIds: string[] = session.rankingOutput?.rankedItemIds ?? [];
            const scores = session.rankingOutput?.scores || {};
            const classifications = session.rankingOutput?.classifications || {};
            // Guard against duplicate pools: check the session's resultingItemPoolId (set after creation)
            // and also check by pool name as a fallback for pools created before this fix was applied.
            const poolAlreadyExists = Boolean(session.resultingItemPoolId) ||
              allPools.some((p) => p.name === `Results: ${session.name}` && p.trackId === track.id);
            const canAddAsList = rankedIds.length > 0 && !poolAlreadyExists;
            const isPublished = session.state === 'PUBLISHED';

            return (
              <div key={session.id} style={{ ...wsCard, border: isPublished ? '1px solid rgba(58,125,86,0.4)' : wsCard.border }}>
                <CompetingChoiceNotice
                  className="mb-3"
                  result={{ id: session.id, name: session.name, rankedItemIds: rankedIds, cutLinePosition: session.cutLinePosition ?? null }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>{session.name}</div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                      {isPublished ? 'Published' : 'Completed'}{' '}
                      {session.updatedAt ? new Date(session.updatedAt).toLocaleDateString() : 'recently'}
                    </p>
                  </div>
                  <span style={{
                    borderRadius: '2px', padding: '3px 10px', fontSize: '0.75rem',
                    background: isPublished ? 'rgba(58,125,86,0.12)' : 'rgba(61,75,111,0.1)',
                    color: isPublished ? 'var(--green)' : 'var(--slate)',
                  }}>
                    {isPublished ? 'Published' : 'Complete'}
                  </span>
                </div>

                {rankedIds.length > 0 ? (
                  <div style={{ borderTop: '1px solid var(--rule)', paddingTop: '12px' }}>
                    <div style={{ ...wsLabel, marginBottom: '8px' }}>
                      Ranked Result ({rankedIds.length} items)
                    </div>
                    <ol style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {rankedIds.slice(0, 5).map((itemId, idx) => {
                        const itemScore = scores[itemId];
                        const totalScore =
                          itemScore && typeof itemScore === 'object'
                            ? (itemScore.total ?? Object.entries(itemScore).reduce(
                                (sum, [key, score]) => (key === 'total' ? sum : sum + score),
                                0
                              ))
                            : undefined;
                        const classificationEntries = getVisibleClassificationEntries(classifications[itemId]).slice(0, 2);
                        return (
                          <li key={itemId} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.8rem' }}>
                            <span style={{
                              flexShrink: 0, width: '22px', height: '22px', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600,
                              background: isPublished ? 'rgba(58,125,86,0.12)' : 'rgba(61,75,111,0.1)',
                              color: isPublished ? 'var(--green)' : 'var(--slate)',
                            }}>
                              {idx + 1}
                            </span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: 'var(--text-pri)' }}>{getItemName(itemId)}</span>
                                <ItemRelationshipFlags itemId={itemId} />
                                {totalScore !== undefined && (
                                  <span style={{ color: 'var(--text-dim)' }}>
                                    ({totalScore.toFixed(1)})
                                  </span>
                                )}
                              </div>
                              {classificationEntries.length > 0 && (
                                <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {classificationEntries.map((entry) => (
                                    <span
                                      key={`${itemId}-${entry.key}`}
                                      className={CLASSIFICATION_CHIP_CLASS}
                                      title={`${entry.label}: ${entry.value}`}
                                    >
                                      <span className="font-medium">{entry.label}:</span>&nbsp;{entry.value}
                                    </span>
                                  ))}
                                  {getVisibleClassificationEntries(classifications[itemId]).length > 2 && (
                                    <span
                                      className={CHIP_OVERFLOW_CLASS}
                                      title={getVisibleClassificationEntries(classifications[itemId])
                                        .map((entry) => `${entry.label}: ${entry.value}`)
                                        .join(', ')}
                                    >
                                      +{getVisibleClassificationEntries(classifications[itemId]).length - 2} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                    {rankedIds.length > 5 && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '8px' }}>
                        +{rankedIds.length - 5} more items
                      </p>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-sec)', borderTop: '1px solid var(--rule)', paddingTop: '12px' }}>No ranking data available</p>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--rule)' }}>
                  <Link
                    to={`/prioritization/sessions/${session.id}`}
                    style={{ fontSize: '0.8rem', color: 'var(--text-ter)' }}
                  >
                    View Session →
                  </Link>
                  {poolAlreadyExists && rankedIds.length > 0 ? (
                    <span className="dark:!text-[var(--text-ter)]" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: 'var(--green)', background: 'rgba(58,125,86,0.08)', border: '1px solid rgba(58,125,86,0.25)', padding: '6px 12px', borderRadius: '2px' }}>
                      <CheckCircle2 size={13} />
                      Item pool created
                    </span>
                  ) : canAddAsList ? (
                    <button
                      type="button"
                      onClick={() => handleAddAsPriorityList(session)}
                      disabled={addingSessionId !== null}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 500,
                        color: 'var(--text-sec)', background: 'var(--hover-tint)', border: 'none', borderRadius: '2px', padding: '6px 12px',
                        cursor: addingSessionId !== null ? 'not-allowed' : 'pointer', opacity: addingSessionId !== null ? 0.5 : 1,
                      }}
                    >
                      {addingSessionId === session.id ? (
                        <>
                          <Loader2 className="animate-spin" size={13} />
                          Creating…
                        </>
                      ) : (
                        <>Create item pool from result →</>
                      )}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tournaments — multi-round alternative to a single ranking session */}
      {tournamentsEnabled && <TrackTournamentsSubsection trackId={track.id} />}
    </div>
  );
}

export default TrackDetail;


// ─── Defaults tab (shared with TrackWorkspace V2 — V-02 parity) ──────────────

export function TrackDefaultsTab({
  track,
  cycle,
  currentUserId,
  workspaceId,
  onScopeChanged,
}: {
  track: Track;
  cycle: DegapCycle | null | undefined;
  currentUserId: string | null | undefined;
  workspaceId: string | null | undefined;
  onScopeChanged?: (newCells: GridCellFilter[]) => void;
}) {
  const trackOwnerId = getTrackOwnerUserIdFromMetadata(track.metadata);
  const canReassignTrackOwner = Boolean(currentUserId) && (
    currentUserId === trackOwnerId ||
    (cycle != null && cycle.ownerId != null && currentUserId === cycle.ownerId)
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <WorkflowPresetSummary cycle={cycle} track={track} />
      <div style={{ ...wsCard, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>Track owner</div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px' }}>
            Optional. The person accountable for configuring participants and running sessions in this Track. Can be set or changed at any time.
          </p>
        </div>
        <TrackOwnerSubControl
          trackId={track.id}
          readOnly={!canReassignTrackOwner}
          workspaceId={workspaceId}
        />
      </div>
      <TrackItemScopeSection track={track} onAfterSave={onScopeChanged ?? (() => {})} />
      <TrackReadinessSection track={track} cycle={cycle} />
      <TrackSessionDefaultsSection track={track} cycle={cycle} />
    </div>
  );
}
